'use client';

import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent, type PointerEvent, type ReactNode, type WheelEvent as ReactWheelEvent } from 'react';

import { optimizeImageForTextureUpload } from '@/lib/imageUtils';
import {
  mrApiErrorMessage,
  mrApiMapCreate,
  mrApiMapDelete,
  mrApiMapDeleteAsset,
  mrApiMapDuplicate,
  mrApiMapGet,
  mrApiMapPublish,
  mrApiMapsMine,
  mrApiMapsPublished,
  mrApiMapUpdate,
  mrApiMapUploadAsset,
  mrApiObjectSkins,
  mrApiProfile,
  type MrMapAsset,
  type MrMapFull,
  type MrMapSummary,
  type MrObjectSkin,
} from '../runtime/net/mysteryRoomApi';
import { getMysteryRoomMapPlayableModes, MYSTERY_ROOM_ALL_GAME_MODES } from '../mysteryRoomMapModes';
import { registerMysteryRoomMapDefinition } from '../runtime/world/mapRegistry';
import {
  MYSTERY_ROOM_DEFAULT_TEXTURES,
  MYSTERY_ROOM_DEFAULT_TEXTURE_STYLES,
  getMysteryRoomDefaultTexture,
  getMysteryRoomTextureTileSize,
  getMysteryRoomTextureWorldTileSize,
  normalizeMysteryRoomTextureScale,
  resolveMysteryRoomTextureAssetUrl,
  type MysteryRoomDefaultTextureDefinition,
  type MysteryRoomDefaultTextureStyle,
} from '../runtime/world/defaultTextures';
import {
  MYSTERY_ROOM_ACCESS_CARD_LEVELS,
  getMysteryRoomEffectiveCustomObjectAsset,
  getMysteryRoomAccessCardVisual,
  getMysteryRoomAccessLevelForCardLevel,
  normalizeMysteryRoomAccessCardLevel,
  getMysteryRoomDefaultItemVisual,
} from '../runtime/world/itemAssets';
import {
  rectContainsPoint,
  rectCenter,
  type MysteryRoomAccessLevel,
  type MysteryRoomAccessCardLevel,
  type MysteryRoomAnchorKind,
  type MysteryRoomAssetFit,
  type MysteryRoomCollisionShape,
  type MysteryRoomCustomObjectDefinition,
  type MysteryRoomCustomObjectKind,
  type MysteryRoomDoorAccessMode,
  type MysteryRoomDoorDefinition,
  type MysteryRoomHazardDefinition,
  type MysteryRoomIncidentAnchorDefinition,
  type MysteryRoomIncidentAnchorKind,
  type MysteryRoomInteractableDefinition,
  type MysteryRoomInteractableKind,
  type MysteryRoomLightDefinition,
  type MysteryRoomGameMode,
  type MysteryRoomMapDefinition,
  type MysteryRoomNpcActorPreset,
  type MysteryRoomNpcBehaviorProfile,
  type MysteryRoomNpcFaction,
  type MysteryRoomNavNodeDefinition,
  type MysteryRoomNpcRole,
  type MysteryRoomRect,
  type MysteryRoomResourceKind,
  type MysteryRoomSoundSourceDefinition,
  type MysteryRoomSurfaceTexture,
  type MysteryRoomTextureEffects,
  type MysteryRoomTextureLayer,
  type MysteryRoomTriggerAction,
  type MysteryRoomTriggerDefinition,
  type MysteryRoomVec2,
  type MysteryRoomWorldLabelVisibility,
} from '../runtime/world/mapTypes';
import {
  adjustEditorSelectionLayer,
  bindPersistedMapId,
  clampEditorLayer,
  createBlankMysteryRoomEditorMap,
  createEditorEntity,
  deleteEditorSelection,
  duplicateEditorSelections,
  dragTransformEditorSelection,
  formatMapAssetBytes,
  getEditorSelectionBounds,
  getEditorSelectionLayer,
  getEditorSelectionsBounds,
  getCustomObjectShapePointCount,
  getCustomObjectShapePoints,
  getCustomObjectShapeWorldPoints,
  getEditableMapContentBounds,
  generateEditorResourceObjects,
  getMysteryRoomEditorTemplates,
  hexColorToNumber,
  classifyMysteryRoomMapAsset,
  connectEditorWallPoints,
  createCustomObjectShapeCollision,
  customObjectEdgeHandle,
  customObjectEdgeHandleIndex,
  findEditorWallNode,
  getConnectedEditorWallIndices,
  isCustomObjectEdgeHandle,
  isRectSideHandle,
  isWallEndpointHandle,
  isMysteryRoomMapAudioAsset,
  isMysteryRoomMapImageAsset,
  moveEditorSelection,
  moveEditorSelections,
  MR_EDITOR_DEFAULT_CANVAS_HEIGHT,
  MR_EDITOR_DEFAULT_CANVAS_WIDTH,
  MR_EDITOR_LAYER_MAX,
  MR_EDITOR_LAYER_MIN,
  MR_EDITOR_WALL_NODE_SNAP_RADIUS,
  MR_MAP_ASSET_ACCEPT,
  MR_MAP_AUDIO_FORMAT_LABEL,
  MR_MAP_ASSET_LIMIT_BYTES,
  MR_MAP_IMAGE_FORMAT_LABEL,
  moveEditorWallNode,
  normalizeEditableMap,
  normalizeEditorSpawnSlots,
  numberToHexColor,
  rectSideFromHandle,
  rectSideHandle,
  resolveEditorWallBuildPoint,
  selectedEntityLabel,
  setEditorSelectionLayer,
  setCustomObjectShapePointCount,
  formatMysteryRoomPublishIssue,
  validateEditableMap,
  wallEndpointFromHandle,
  wallEndpointHandle,
  MYSTERY_ROOM_EDITOR_HAZARD_KINDS,
  MYSTERY_ROOM_EDITOR_INCIDENT_KINDS,
  MYSTERY_ROOM_EDITOR_NPC_BEHAVIOR_PROFILES,
  MYSTERY_ROOM_EDITOR_NPC_FACTIONS,
  MYSTERY_ROOM_EDITOR_NPC_ROLES,
  MYSTERY_ROOM_EDITOR_WORK_ANCHOR_KINDS,
  type MysteryRoomEditorRectSide,
  type MysteryRoomEditorTransformHandle,
  type MysteryRoomEditorWallEndpoint,
  type MysteryRoomEditorTemplate,
  type MysteryRoomEditorSelection,
  type MysteryRoomEditorTool,
} from './editorModel';
import {
  MYSTERY_ROOM_RESOURCE_DEFINITIONS,
  MYSTERY_ROOM_RESOURCE_KINDS,
  MYSTERY_ROOM_RESOURCE_SOURCE_DEFINITIONS,
  mysteryRoomResourceItemId,
} from '../runtime/world/resourceDefinitions';
import { getStoredUserRaw } from '@/lib/authStorage';

type Props = {
  onPlayMap?: (mapId: string, map: MysteryRoomMapDefinition, title: string) => void;
  initialMapId?: string | null;
};

type PublishNotice = {
  mapTitle: string;
  issues: string[];
};

type StoredUser = {
  is_superuser?: boolean;
  is_staff?: boolean;
  role?: { name?: string | null } | null;
};

type DragState =
  | {
      kind: 'move';
      selection: MysteryRoomEditorSelection;
      selections: MysteryRoomEditorSelection[];
      last: MysteryRoomVec2;
      startMap: MysteryRoomMapDefinition;
      moved: boolean;
      pointerId: number;
    }
  | {
      kind: 'asset-pan';
      objectId: string;
      last: MysteryRoomVec2;
      startMap: MysteryRoomMapDefinition;
      moved: boolean;
      pointerId: number;
    }
  | {
      kind: 'pan';
      startClient: MysteryRoomVec2;
      startViewBox: ViewBox;
      pointerId: number;
    }
  | {
      kind: 'transform';
      selection: MysteryRoomEditorSelection;
      handle: MysteryRoomEditorTransformHandle;
      startPoint: MysteryRoomVec2;
      startMap: MysteryRoomMapDefinition;
      changed: boolean;
      pointerId: number;
    }
  | {
      kind: 'select-box';
      start: MysteryRoomVec2;
      current: MysteryRoomVec2;
      addToExisting: boolean;
      moved: boolean;
      pointerId: number;
    }
  | {
      kind: 'wall-node';
      node: MysteryRoomVec2;
      last: MysteryRoomVec2;
      startMap: MysteryRoomMapDefinition;
      moved: boolean;
      pointerId: number;
    };

type PaletteDragState = {
  tool: MysteryRoomEditorTool;
  pointerId: number;
  clientX: number;
  clientY: number;
};

type EditorContextMenuState =
  | {
      kind: 'object';
      clientX: number;
      clientY: number;
      point: MysteryRoomVec2 | null;
      selection: MysteryRoomEditorSelection;
    }
  | {
      kind: 'canvas';
      clientX: number;
      clientY: number;
      point: MysteryRoomVec2 | null;
    };

type SaveState = 'idle' | 'dirty' | 'saving' | 'saved' | 'error';

type AssetViewMode = 'grid' | 'list';

type PendingMapAssetUpload = {
  id: string;
  name: string;
  type: string;
  size: number;
  kind: 'image' | 'audio';
  previewUrl?: string;
};

type ViewBox = {
  x: number;
  y: number;
  width: number;
  height: number;
};

type WallBuildDraft = {
  anchor: MysteryRoomVec2;
  startMap: MysteryRoomMapDefinition;
  createdSegments: number;
};

type SvgViewportSize = {
  width: number;
  height: number;
};

type MysteryRoomEditorHistorySnapshot = {
  map: MysteryRoomMapDefinition;
  selection: MysteryRoomEditorSelection | null;
};

type ToolDefinition = {
  id: MysteryRoomEditorTool;
  icon: string;
  label: string;
  title: string;
  shortcut: string;
  group: 'navigation' | 'actors' | 'geometry' | 'objects' | 'systems';
  keywords: string;
};

type TextureBaseColorControls = {
  color: number;
  colorEnabled?: boolean;
  colorOpacity?: number;
  outlineColor?: number;
  outlineEnabled?: boolean;
  outlineOpacity?: number;
  outlineWidth?: number;
  onColorChange: (color: number) => void;
  onColorEnabledChange: (enabled: boolean) => void;
  onColorOpacityChange: (opacity: number) => void;
  onOutlineColorChange?: (color: number) => void;
  onOutlineEnabledChange?: (enabled: boolean) => void;
  onOutlineOpacityChange?: (opacity: number) => void;
  onOutlineWidthChange?: (width: number) => void;
};

const TOOL_DEFINITIONS: ToolDefinition[] = [
  { id: 'select', icon: '↖', label: 'Выбор', title: 'Выбор и перетаскивание объектов', shortcut: 'S', group: 'navigation', keywords: 'select выбор move drag' },
  { id: 'marquee', icon: '▣', label: 'Выделение', title: 'Рамочное выделение объектов', shortcut: 'M', group: 'navigation', keywords: 'marquee rectangle выделение рамка group' },
  { id: 'pan', icon: '✋', label: 'Полотно', title: 'Перемещение камеры полотна', shortcut: 'P', group: 'navigation', keywords: 'pan камера полотно колесико' },
  { id: 'nav', icon: '●', label: 'Nav node', title: 'Узел навигации NPC', shortcut: 'N', group: 'navigation', keywords: 'nav node путь npc' },
  { id: 'spawn', icon: '①', label: 'Игроки', title: 'Нумерованные точки спавна игроков для мест лобби', shortcut: 'X', group: 'actors', keywords: 'spawn старт player игрок место лобби слот' },
  { id: 'bot', icon: 'BOT', label: 'Боты', title: 'Универсальный актер: NPC, турель, зомби или кастомный бот', shortcut: 'Y', group: 'actors', keywords: 'bot npc turret zombie бот нпс турель зомби актер' },
  { id: 'boss', icon: '★', label: 'Боссы', title: 'Крупный уникальный боевой актер, включая невидимые варианты', shortcut: 'Z', group: 'actors', keywords: 'boss босс уникальный невидимый бой актер' },
  { id: 'room', icon: '□', label: 'Комната', title: 'Зона пола / помещение', shortcut: 'R', group: 'geometry', keywords: 'room floor комната зона пол' },
  { id: 'wall', icon: '◢', label: 'Стены', title: 'Чертеж стен: точки, отрезки и связанное перемещение', shortcut: 'W', group: 'geometry', keywords: 'wall стена line blocker чертеж строительство' },
  { id: 'door', icon: '▭', label: 'Дверь', title: 'Дверь с зоной открытия и уровнем доступа', shortcut: 'D', group: 'geometry', keywords: 'door дверь access' },
  { id: 'collision', icon: '◇', label: 'Коллизия', title: 'Невидимый или технический collision-shape', shortcut: 'C', group: 'geometry', keywords: 'collision коллизия obstacle blocker' },
  { id: 'table', icon: '▤', label: 'Стол', title: 'Стол/поверхность с базовой коллизией', shortcut: 'T', group: 'objects', keywords: 'table стол prop object' },
  { id: 'object', icon: '◆', label: 'Объект', title: 'Примитив, картинка, пропуск, оружие или декор', shortcut: 'O', group: 'objects', keywords: 'object primitive image badge weapon объект картинка' },
  { id: 'flashlight', icon: 'FL', label: 'Фонарик', title: 'Подбираемый фонарик для направленного света', shortcut: 'F', group: 'objects', keywords: 'flashlight фонарик item pickup свет' },
  { id: 'access_card', icon: 'ID', label: 'Пропуск', title: 'Подбираемая карточка доступа для дверей', shortcut: 'V', group: 'objects', keywords: 'badge access card пропуск карточка дверь' },
  { id: 'pistol', icon: 'PS', label: 'Пистолет', title: 'Подбираемый пистолет для инвентаря и стрельбы', shortcut: 'B', group: 'objects', keywords: 'pistol weapon gun пистолет оружие' },
  { id: 'quest_item', icon: 'QI', label: 'Предмет задания', title: 'Подбираемый предмет для сценариев и действий', shortcut: 'Q', group: 'objects', keywords: 'quest item pickup предмет задание ключ' },
  { id: 'resource_bush', icon: 'BU', label: 'Куст', title: 'Источник дерева: подбираемый ресурс', shortcut: '—', group: 'objects', keywords: 'resource bush wood куст дерево ресурс pickup' },
  { id: 'resource_scrap', icon: 'SC', label: 'Металлолом', title: 'Источник металла: подбираемый ресурс', shortcut: '—', group: 'objects', keywords: 'resource scrap metal металлолом металл ресурс pickup' },
  { id: 'resource_rock', icon: 'RK', label: 'Камни', title: 'Источник камня: подбираемый ресурс', shortcut: '—', group: 'objects', keywords: 'resource rock stone камни камень ресурс pickup' },
  { id: 'light', icon: '☼', label: 'Свет', title: 'Источник света с радиусом и цветом', shortcut: 'L', group: 'systems', keywords: 'light свет lamp' },
  { id: 'switch', icon: '⏻', label: 'Выключатель', title: 'Интерактивный выключатель света', shortcut: 'I', group: 'systems', keywords: 'switch выключатель light' },
  { id: 'trigger', icon: '◎', label: 'Триггер', title: 'Область onEnter/onStep с actions', shortcut: 'G', group: 'systems', keywords: 'trigger триггер action alarm smoke door' },
  { id: 'sound', icon: '♪', label: 'Звук', title: 'Источник звука: loop или one-shot', shortcut: 'A', group: 'systems', keywords: 'sound звук audio fan alarm' },
  { id: 'hazard', icon: '≈', label: 'Эффект', title: 'Зона дыма или искр из runtime', shortcut: 'H', group: 'systems', keywords: 'hazard effect smoke sparks дым искры эффект' },
  { id: 'incident', icon: '⚠', label: 'Инцидент', title: 'Якорь серверных событий и аварий', shortcut: 'J', group: 'systems', keywords: 'incident anchor server reactor fire alarm инцидент сервер авария' },
  { id: 'work', icon: '⌘', label: 'Точка задач', title: 'Универсальная точка поведения: работа, отдых, туалет, патруль, эвакуация или поломка', shortcut: 'K', group: 'navigation', keywords: 'work anchor task npc patrol rest toilet эвакуация рабочая точка задач npc' },
];

const TOOL_GROUP_LABELS: Record<ToolDefinition['group'], string> = {
  navigation: 'Навигация',
  actors: 'Актеры',
  geometry: 'Геометрия',
  objects: 'Объекты',
  systems: 'Системы',
};

const ACCESS_LEVELS: MysteryRoomAccessLevel[] = ['public', 'lab', 'server', 'reactor'];
const LIGHT_FLICKER_MODES = ['steady', 'flicker', 'event_flicker', 'random_bulb'] as const;
const LIGHT_FLICKER_MODE_LABELS: Record<(typeof LIGHT_FLICKER_MODES)[number], string> = {
  steady: 'Постоянное свечение',
  flicker: 'Мерцание',
  event_flicker: 'Мерцание по событию',
  random_bulb: 'Случайная лампочка',
};
const DOOR_ACCESS_MODES: MysteryRoomDoorAccessMode[] = ['public', 'npc_only', 'card_required', 'all_cards_required'];
const OBJECT_KINDS: MysteryRoomCustomObjectKind[] = ['primitive', 'image', 'table', 'wall', 'door', 'equipment', 'badge', 'weapon', 'resource', 'decor'];
const PICKUP_KINDS = ['tool', 'weapon', 'access_card', 'resource'] as const;
const RESOURCE_SOURCE_KIND_BY_RESOURCE: Record<MysteryRoomResourceKind, keyof typeof MYSTERY_ROOM_RESOURCE_SOURCE_DEFINITIONS> = {
  wood: 'bush',
  metal: 'scrap',
  stone: 'rock',
};
const INTERACTABLE_KINDS: MysteryRoomInteractableKind[] = ['access_grant', 'hide_zone', 'inspect', 'pickup', 'light_switch'];
const HAZARD_KINDS: MysteryRoomHazardDefinition['kind'][] = MYSTERY_ROOM_EDITOR_HAZARD_KINDS;
const INCIDENT_ANCHOR_KINDS: MysteryRoomIncidentAnchorKind[] = MYSTERY_ROOM_EDITOR_INCIDENT_KINDS;
const WORK_ANCHOR_KINDS: MysteryRoomAnchorKind[] = MYSTERY_ROOM_EDITOR_WORK_ANCHOR_KINDS;
const NPC_ROLES: MysteryRoomNpcRole[] = MYSTERY_ROOM_EDITOR_NPC_ROLES;
const NPC_FACTIONS: MysteryRoomNpcFaction[] = MYSTERY_ROOM_EDITOR_NPC_FACTIONS;
const NPC_BEHAVIOR_PROFILES: MysteryRoomNpcBehaviorProfile[] = MYSTERY_ROOM_EDITOR_NPC_BEHAVIOR_PROFILES;
const NPC_ACTOR_PRESETS: MysteryRoomNpcActorPreset[] = ['npc', 'turret', 'zombie', 'custom', 'boss'];
const TASK_AREA_SHAPES = ['circle', 'square'];
const NPC_DUTY_ACTIVATIONS = ['auto', 'trigger', 'door_open', 'world_alarm', 'manual'];
const SURFACE_TEXTURE_FITS: MysteryRoomAssetFit[] = ['repeat', 'cover', 'contain', 'stretch'];
const TEXTURE_EFFECT_FIELDS: Array<{ key: keyof MysteryRoomTextureEffects; label: string }> = [
  { key: 'brightness', label: 'Яркость' },
  { key: 'contrast', label: 'Контрастность' },
  { key: 'reflection', label: 'Отражение' },
  { key: 'shading', label: 'Затенение' },
  { key: 'vignette', label: 'Виньетка' },
  { key: 'grain', label: 'Зернистость' },
];

function defaultInteractableColor(kind: MysteryRoomInteractableKind): number {
  if (kind === 'light_switch') return 0xfacc15;
  if (kind === 'pickup') return 0x38bdf8;
  if (kind === 'access_grant') return 0x34d399;
  return 0xc4b5fd;
}

function hazardKindLabel(kind: MysteryRoomHazardDefinition['kind']): string {
  return kind === 'smoke' ? 'Дым' : 'Искры';
}

function incidentAnchorKindLabel(kind: MysteryRoomIncidentAnchorKind): string {
  switch (kind) {
    case 'alarm':
      return 'Тревога';
    case 'fire':
      return 'Пожар';
    case 'door_failure':
      return 'Сбой двери';
    case 'reactor_fault':
      return 'Сбой реактора';
    case 'server_fault':
      return 'Сбой серверов';
    case 'injured_person':
      return 'Пострадавший';
    default:
      return kind;
  }
}

function workAnchorKindLabel(kind: MysteryRoomAnchorKind): string {
  switch (kind) {
    case 'checkpoint':
      return 'Контроль';
    case 'workstation':
      return 'Рабочее место';
    case 'experiment':
      return 'Эксперимент';
    case 'medical_station':
      return 'Медпункт';
    case 'maintenance':
      return 'Обслуживание';
    case 'standby':
      return 'Ожидание';
    case 'restroom':
      return 'Туалет';
    case 'break_room':
      return 'Место отдыха';
    case 'armory':
      return 'Оружейная';
    case 'patrol_point':
      return 'Точка патрулирования';
    case 'medic_work':
      return 'Рабочее место медика';
    case 'engineer_work':
      return 'Рабочее место инженера';
    case 'guard_work':
      return 'Рабочее место охранника';
    case 'operator_work':
      return 'Рабочее место оператора';
    case 'evacuation':
      return 'Место эвакуации';
    case 'breakdown':
      return 'Место возможной поломки';
    default:
      return kind;
  }
}

function workAnchorKindDescription(kind: MysteryRoomAnchorKind): string {
  switch (kind) {
    case 'restroom':
      return 'Бот редко приходит сюда и задерживается ненадолго.';
    case 'break_room':
      return 'Бот восстанавливается и меняет короткие позиции вокруг точки.';
    case 'armory':
      return 'Охрана или оператор проверяет оружие и готовится к тревоге.';
    case 'patrol_point':
    case 'checkpoint':
      return 'Охранник использует точку для маршрута патруля или наблюдения.';
    case 'medic_work':
    case 'medical_station':
      return 'Медик дежурит, проверяет оборудование и реагирует на раненых.';
    case 'engineer_work':
    case 'maintenance':
      return 'Инженер обслуживает оборудование и идет сюда при ремонте.';
    case 'guard_work':
      return 'Охранник наблюдает, докладывает и реагирует на угрозы.';
    case 'operator_work':
      return 'Оператор контролирует терминалы и процессы объекта.';
    case 'evacuation':
      return 'Боты бегут сюда при тревоге, если нет срочных задач.';
    case 'breakdown':
      return 'Точка, где может появиться поломка для ремонта.';
    case 'experiment':
      return 'Научный сотрудник проводит эксперимент и отходит рядом.';
    case 'standby':
      return 'Свободный бот ожидает следующую задачу.';
    case 'workstation':
    default:
      return 'Бот выполняет рабочую рутину и перемещается рядом.';
  }
}

function incidentAnchorColor(kind: MysteryRoomIncidentAnchorKind): number {
  switch (kind) {
    case 'alarm':
      return 0xf97316;
    case 'fire':
      return 0xef4444;
    case 'door_failure':
      return 0xf59e0b;
    case 'reactor_fault':
      return 0xfb7185;
    case 'server_fault':
      return 0x38bdf8;
    case 'injured_person':
      return 0x34d399;
    default:
      return 0xe2e8f0;
  }
}

function workAnchorColor(kind: MysteryRoomAnchorKind): number {
  switch (kind) {
    case 'medical_station':
    case 'medic_work':
      return 0x34d399;
    case 'maintenance':
    case 'engineer_work':
    case 'breakdown':
      return 0xf59e0b;
    case 'experiment':
      return 0xc084fc;
    case 'checkpoint':
    case 'patrol_point':
    case 'guard_work':
    case 'armory':
      return 0x38bdf8;
    case 'break_room':
      return 0xa3e635;
    case 'restroom':
      return 0x60a5fa;
    case 'evacuation':
      return 0xfb7185;
    case 'operator_work':
      return 0xfacc15;
    case 'standby':
      return 0x94a3b8;
    case 'workstation':
    default:
      return 0xeab308;
  }
}

const MIN_VIEWBOX_WIDTH = 360;
const MIN_VIEWBOX_HEIGHT = 240;
const MAX_VIEWBOX_WIDTH = 48_000;
const MAX_VIEWBOX_HEIGHT = 32_000;
const GRID_CELL_SIZE = 96;
const WHEEL_ZOOM_SENSITIVITY = 0.0018;
const AUTOSAVE_DELAY_MS = 1800;
const EDITOR_HISTORY_LIMIT = 30;
const MIDDLE_MOUSE_BUTTON = 1;
const LEFT_MOUSE_BUTTON = 0;
const RIGHT_MOUSE_BUTTON = 2;
const WALL_TROWEL_CURSOR = `url("data:image/svg+xml,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"><defs><linearGradient id="blade" x1="6" y1="25" x2="27" y2="6"><stop stop-color="#cbd5e1"/><stop offset="0.48" stop-color="#f8fafc"/><stop offset="1" stop-color="#94a3b8"/></linearGradient><linearGradient id="handle" x1="3" y1="29" x2="15" y2="17"><stop stop-color="#78350f"/><stop offset="1" stop-color="#f59e0b"/></linearGradient></defs><path d="M6 25 25 5l3 17z" fill="url(#blade)" stroke="#020617" stroke-width="1.8" stroke-linejoin="round"/><path d="M5 25 2 29" stroke="url(#handle)" stroke-width="5" stroke-linecap="round"/><path d="M20 10 25 5l3 4-4 4z" fill="#fbbf24" stroke="#020617" stroke-width="1"/><path d="M10 22 23 9" stroke="#ffffff" stroke-width="1.2" stroke-linecap="round" opacity="0.75"/></svg>')}") 5 27, crosshair`;
const TOOL_SHORTCUTS: Record<string, MysteryRoomEditorTool> = {
  s: 'select',
  m: 'marquee',
  p: 'pan',
  r: 'room',
  w: 'wall',
  d: 'door',
  t: 'table',
  o: 'object',
  f: 'flashlight',
  v: 'access_card',
  b: 'pistol',
  q: 'quest_item',
  c: 'collision',
  l: 'light',
  i: 'switch',
  g: 'trigger',
  a: 'sound',
  h: 'hazard',
  j: 'incident',
  k: 'work',
  x: 'spawn',
  y: 'bot',
  z: 'boss',
  n: 'nav',
};

function isCreationTool(tool: MysteryRoomEditorTool): boolean {
  return tool !== 'select' && tool !== 'marquee' && tool !== 'pan';
}

function toolDefinitionById(tool: MysteryRoomEditorTool): ToolDefinition | undefined {
  return TOOL_DEFINITIONS.find((entry) => entry.id === tool);
}

const CONTEXT_CREATE_GROUPS: Array<{ label: string; tools: MysteryRoomEditorTool[] }> = [
  { label: 'Актеры', tools: ['spawn', 'bot', 'boss'] },
  { label: 'Геометрия', tools: ['room', 'wall', 'door', 'collision'] },
  { label: 'Объекты', tools: ['table', 'object', 'flashlight', 'access_card', 'pistol', 'quest_item', 'resource_bush', 'resource_scrap', 'resource_rock'] },
  { label: 'Система', tools: ['light', 'switch', 'trigger', 'sound', 'hazard', 'incident', 'work', 'spawn', 'nav'] },
];
const WORLD_LABEL_VISIBILITY_OPTIONS: MysteryRoomWorldLabelVisibility[] = ['hidden', 'near_interactive', 'always'];

function svgImageBoxForFit(
  fit: MysteryRoomAssetFit | undefined,
  width: number,
  height: number,
  naturalSize?: { width: number; height: number },
): MysteryRoomRect {
  if (
    fit === 'stretch' ||
    fit === 'repeat' ||
    !naturalSize ||
    naturalSize.width <= 0 ||
    naturalSize.height <= 0 ||
    width <= 0 ||
    height <= 0
  ) {
    return { x: 0, y: 0, width, height };
  }

  const imageRatio = naturalSize.width / naturalSize.height;
  const rectRatio = width / height;
  const fillByWidth = fit === 'cover' ? imageRatio < rectRatio : imageRatio > rectRatio;
  const nextWidth = fillByWidth ? width : height * imageRatio;
  const nextHeight = fillByWidth ? width / imageRatio : height;
  return {
    x: (width - nextWidth) / 2,
    y: (height - nextHeight) / 2,
    width: nextWidth,
    height: nextHeight,
  };
}

function createSurfaceTextureFromAsset(asset: MrMapAsset, fit: MysteryRoomAssetFit = 'cover'): MysteryRoomSurfaceTexture {
  return {
    assetId: asset.id,
    assetUrl: asset.url,
    assetName: asset.name,
    assetMimeType: asset.type,
    fit,
    opacity: 0.92,
  };
}

function createTextureLayerFromAsset(asset: MrMapAsset, index = 0, fit: MysteryRoomAssetFit = 'repeat'): MysteryRoomTextureLayer {
  return {
    ...createSurfaceTextureFromAsset(asset, fit),
    id: `texture_${asset.id.replace(/[^a-zA-Z0-9_-]/g, '_')}_${Date.now().toString(36)}_${index}`,
    label: asset.name,
    visible: true,
    offsetX: 0,
    offsetY: 0,
    scaleX: 1,
    scaleY: 1,
    rotation: 0,
    repeatX: 1,
    repeatY: 1,
    tileSize: getMysteryRoomTextureTileSize(undefined),
    effects: defaultTextureEffects(),
  };
}

function createTextureLayerFromDefaultTexture(
  texture: MysteryRoomDefaultTextureDefinition,
  index = 0,
  fit: MysteryRoomAssetFit = 'repeat',
): MysteryRoomTextureLayer {
  return {
    builtinTextureId: texture.id,
    assetName: texture.label,
    assetMimeType: texture.assetMimeType,
    fit,
    opacity: 0.92,
    id: `builtin_${texture.id}_${Date.now().toString(36)}_${index}`,
    label: texture.label,
    visible: true,
    offsetX: 0,
    offsetY: 0,
    scaleX: 1,
    scaleY: 1,
    rotation: 0,
    repeatX: 1,
    repeatY: 1,
    tileSize: texture.tileSize,
    effects: defaultTextureEffects(),
  };
}

function defaultTextureEffects(): MysteryRoomTextureEffects {
  return {
    brightness: 0,
    contrast: 0,
    reflection: 0,
    shading: 0,
    vignette: 0,
    grain: 0,
  };
}

function normalizeSurfaceTextureOpacity(value: number | null | undefined): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return 0.92;
  }
  return Math.max(0, Math.min(1, Math.round(value * 100) / 100));
}

function normalizeBaseColorOpacity(value: number | null | undefined, fallback = 0.9): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return fallback;
  }
  return Math.max(0, Math.min(1, Math.round(value * 100) / 100));
}

function isBaseColorEnabled(entry: { colorEnabled?: boolean }): boolean {
  return entry.colorEnabled ?? true;
}

function baseColorOpacity(entry: { colorEnabled?: boolean; colorOpacity?: number }, fallback = 0.9): number {
  return isBaseColorEnabled(entry) ? normalizeBaseColorOpacity(entry.colorOpacity, fallback) : 0;
}

function normalizeOutlineWidth(value: number | null | undefined, fallback = 3): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return fallback;
  }
  return Math.max(0, Math.min(100, Math.round(value)));
}

function outlineOpacity(entry: { outlineEnabled?: boolean; outlineOpacity?: number }, fallback = 0.5): number {
  return entry.outlineEnabled === false ? 0 : normalizeBaseColorOpacity(entry.outlineOpacity, fallback);
}

function outlineWidth(entry: { outlineEnabled?: boolean; outlineWidth?: number }, fallback = 3): number {
  return entry.outlineEnabled === false ? 0 : normalizeOutlineWidth(entry.outlineWidth, fallback);
}

function normalizeTextureEffectIntensity(value: number | null | undefined): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return 0;
  }
  return Math.max(-100, Math.min(100, Math.round(value)));
}

function normalizeTextureScale(value: number | null | undefined): number {
  return normalizeMysteryRoomTextureScale(value);
}

function normalizeTextureLayer(layer: MysteryRoomTextureLayer, index: number): MysteryRoomTextureLayer {
  const builtin = getMysteryRoomDefaultTexture(layer.builtinTextureId);
  return {
    ...layer,
    id: layer.id || `texture_${index + 1}`,
    assetName: layer.assetName ?? builtin?.label,
    assetMimeType: layer.assetMimeType ?? builtin?.assetMimeType,
    label: layer.label ?? layer.assetName ?? builtin?.label ?? `Текстура ${index + 1}`,
    fit: layer.fit ?? 'repeat',
    opacity: normalizeSurfaceTextureOpacity(layer.opacity),
    offsetX: finiteEditorNumber(layer.offsetX, 0),
    offsetY: finiteEditorNumber(layer.offsetY, 0),
    scaleX: normalizeTextureScale(layer.scaleX),
    scaleY: normalizeTextureScale(layer.scaleY),
    rotation: finiteEditorNumber(layer.rotation, 0),
    repeatX: normalizeTextureScale(layer.repeatX),
    repeatY: normalizeTextureScale(layer.repeatY),
    tileSize: getMysteryRoomTextureTileSize(layer),
    visible: layer.visible ?? true,
    effects: {
      ...defaultTextureEffects(),
      ...(layer.effects ?? {}),
    },
  };
}

function textureLayerToSurfaceTexture(layer: MysteryRoomTextureLayer | undefined): MysteryRoomSurfaceTexture | undefined {
  if (!layer?.assetId && !layer?.assetUrl && !layer?.builtinTextureId) return undefined;
  const { id: _id, label: _label, visible: _visible, ...texture } = layer;
  return texture;
}

function normalizeEditorAssetTransform(object: MysteryRoomCustomObjectDefinition) {
  const transform = object.assetTransform;
  return {
    offsetX: finiteEditorNumber(transform?.offsetX, 0),
    offsetY: finiteEditorNumber(transform?.offsetY, 0),
    scaleX: positiveEditorNumber(transform?.scaleX, 1),
    scaleY: positiveEditorNumber(transform?.scaleY, 1),
    rotation: finiteEditorNumber(transform?.rotation, 0),
    clipToBounds: transform?.clipToBounds ?? true,
  };
}

function objectSkinItemId(object: MysteryRoomCustomObjectDefinition): string {
  return object.itemId ?? object.pickupKind ?? '';
}

function objectSkinMatchesObject(skin: MrObjectSkin, object: MysteryRoomCustomObjectDefinition): boolean {
  return skin.kind === object.kind && (skin.itemId === objectSkinItemId(object) || skin.itemId === '');
}

function defaultObjectSkinForObject(skins: MrObjectSkin[], object: MysteryRoomCustomObjectDefinition): MrObjectSkin | null {
  const itemId = objectSkinItemId(object);
  return (
    skins.find((skin) => skin.isDefault && skin.kind === object.kind && skin.itemId === itemId) ??
    skins.find((skin) => skin.isDefault && skin.kind === object.kind && skin.itemId === '') ??
    null
  );
}

function applyObjectSkinToObject(object: MysteryRoomCustomObjectDefinition, skin: MrObjectSkin): MysteryRoomCustomObjectDefinition {
  const payload = normalizeObjectSkinPayloadForObject(object, skin.payload ?? {});
  return {
    ...object,
    ...payload,
    assetId: undefined,
    visualSkinId: skin.id,
    visualSkinName: skin.title,
    kind: object.kind === 'image' ? 'primitive' : object.kind,
  };
}

function normalizeObjectSkinPayloadForObject(
  object: MysteryRoomCustomObjectDefinition,
  payload: MrObjectSkin['payload'],
): MrObjectSkin['payload'] {
  const next = { ...payload };
  const isPickupLike = Boolean(object.pickupKind || object.itemId || object.kind === 'weapon' || object.kind === 'badge');
  if (!next.assetUrl) {
    next.assetUrl = undefined;
    next.assetName = undefined;
    next.assetMimeType = undefined;
    next.assetFit = undefined;
    next.assetTransform = undefined;
    next.assetAlphaCollisionEnabled = false;
  } else if (isPickupLike && next.assetFit === 'cover') {
    next.assetFit = 'contain';
  }
  if (isPickupLike && next.textures?.length) {
    next.textures = next.textures.map((layer, index) =>
      normalizeTextureLayer(
        layer.fit === 'cover' && (layer.assetUrl || layer.assetId)
          ? { ...layer, fit: 'contain' }
          : layer,
        index,
      ),
    );
  }
  return next;
}

function defaultItemAppearancePatch(itemId: string | null | undefined): Partial<MysteryRoomCustomObjectDefinition> {
  const visual = getMysteryRoomDefaultItemVisual(itemId);
  if (!visual) {
    return {
      assetId: undefined,
      assetUrl: undefined,
      assetName: undefined,
      assetMimeType: undefined,
      assetFit: undefined,
      assetTransform: undefined,
      assetAlphaCollisionEnabled: false,
      textures: undefined,
      visualSkinId: undefined,
      visualSkinName: undefined,
      colorEnabled: true,
      outlineEnabled: true,
    };
  }
  return {
    assetId: undefined,
    assetUrl: visual.worldUrl,
    assetName: `${visual.inventoryName} world`,
    assetMimeType: visual.mimeType,
    assetFit: 'contain',
    assetTransform: { offsetX: 0, offsetY: 0, scaleX: 1, scaleY: 1, rotation: 0, clipToBounds: true },
    assetAlphaCollisionEnabled: true,
    textures: undefined,
    visualSkinId: undefined,
    visualSkinName: undefined,
    colorEnabled: false,
    colorOpacity: 0,
    outlineEnabled: false,
    outlineOpacity: 0,
    outlineWidth: 0,
    inventoryIconAssetUrl: visual.inventoryUrl,
    inventoryIconAssetName: visual.inventoryName,
    inventoryIconAssetMimeType: visual.mimeType,
  };
}

function accessCardAppearancePatch(accessCardLevel: MysteryRoomAccessCardLevel): Partial<MysteryRoomCustomObjectDefinition> {
  const visual = getMysteryRoomAccessCardVisual(accessCardLevel);
  return {
    accessCardLevel,
    grantsAccess: getMysteryRoomAccessLevelForCardLevel(accessCardLevel),
    assetId: undefined,
    assetUrl: visual.worldUrl,
    assetName: `${visual.inventoryName} world`,
    assetMimeType: visual.mimeType,
    assetFit: 'contain',
    assetTransform: { offsetX: 0, offsetY: 0, scaleX: 1, scaleY: 1, rotation: 0, clipToBounds: true },
    assetAlphaCollisionEnabled: true,
    textures: undefined,
    visualSkinId: undefined,
    visualSkinName: undefined,
    colorEnabled: false,
    colorOpacity: 0,
    outlineEnabled: false,
    outlineOpacity: 0,
    outlineWidth: 0,
    inventoryIconAssetUrl: visual.inventoryUrl,
    inventoryIconAssetName: visual.inventoryName,
    inventoryIconAssetMimeType: visual.mimeType,
  };
}

function itemPatchForPickupKind(
  object: MysteryRoomCustomObjectDefinition,
  pickupKind: NonNullable<MysteryRoomCustomObjectDefinition['pickupKind']>,
): Partial<MysteryRoomCustomObjectDefinition> {
  if (pickupKind === 'weapon') {
    return {
      kind: 'weapon',
      pickupKind,
      itemId: 'pistol',
      interactive: true,
      tags: ['pickup', 'weapon', 'pistol'],
      ...defaultItemAppearancePatch('pistol'),
    };
  }
  if (pickupKind === 'access_card') {
    return {
      kind: 'badge',
      pickupKind,
      itemId: object.id,
      interactive: true,
      tags: ['pickup', 'access_card'],
      inventoryIcon: object.inventoryIcon ?? 'ID',
      ...accessCardAppearancePatch(normalizeMysteryRoomAccessCardLevel(object.accessCardLevel)),
    };
  }
  if (pickupKind === 'resource') {
    const resourceKind = object.resourceKind ?? 'wood';
    const resource = MYSTERY_ROOM_RESOURCE_DEFINITIONS[resourceKind];
    return {
      kind: 'resource',
      pickupKind,
      itemId: mysteryRoomResourceItemId(resourceKind),
      resourceKind,
      resourceAmount: object.resourceAmount ?? 1,
      resourceSourceKind: object.resourceSourceKind ?? 'bush',
      interactive: true,
      inventoryIcon: resource.icon,
      tags: ['pickup', 'resource', resourceKind],
      assetId: undefined,
      assetUrl: undefined,
      assetName: undefined,
      assetMimeType: undefined,
      assetFit: undefined,
      assetTransform: undefined,
      assetAlphaCollisionEnabled: false,
    };
  }
  return {
    kind: 'equipment',
    pickupKind,
    itemId: 'flashlight',
    interactive: true,
    tags: ['pickup', 'flashlight'],
    ...defaultItemAppearancePatch('flashlight'),
  };
}

function finiteEditorNumber(value: number | null | undefined, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function positiveEditorNumber(value: number | null | undefined, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : fallback;
}

function moveCustomObjectAssetTransform(
  map: MysteryRoomMapDefinition,
  objectId: string,
  delta: MysteryRoomVec2,
): MysteryRoomMapDefinition {
  if (delta.x === 0 && delta.y === 0) {
    return map;
  }
  return normalizeEditableMap({
    ...map,
    customObjects: map.customObjects.map((object) => {
      if (object.id !== objectId || !object.assetUrl) return object;
      const transform = normalizeEditorAssetTransform(object);
      return {
        ...object,
        assetTransform: {
          ...transform,
          offsetX: Math.round((transform.offsetX + delta.x) * 100) / 100,
          offsetY: Math.round((transform.offsetY + delta.y) * 100) / 100,
        },
      };
    }),
  });
}

function cloneEditorMap(map: MysteryRoomMapDefinition): MysteryRoomMapDefinition {
  if (typeof structuredClone === 'function') {
    return structuredClone(map);
  }
  return JSON.parse(JSON.stringify(map)) as MysteryRoomMapDefinition;
}

function cloneEditorSelection(selection: MysteryRoomEditorSelection | null): MysteryRoomEditorSelection | null {
  return selection ? { ...selection } : null;
}

function createEditorHistorySnapshot(
  map: MysteryRoomMapDefinition,
  selection: MysteryRoomEditorSelection | null,
): MysteryRoomEditorHistorySnapshot {
  return {
    map: cloneEditorMap(map),
    selection: cloneEditorSelection(selection),
  };
}

function trimEditorHistory(entries: MysteryRoomEditorHistorySnapshot[]): MysteryRoomEditorHistorySnapshot[] {
  return entries.length > EDITOR_HISTORY_LIMIT ? entries.slice(-EDITOR_HISTORY_LIMIT) : entries;
}

function editorMapsEqual(left: MysteryRoomMapDefinition, right: MysteryRoomMapDefinition): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function compactSvgLabel(label: string, maxLength = 24): string {
  const value = label.trim();
  if (value.length <= maxLength) return value;
  return `${value.slice(0, Math.max(1, maxLength - 3))}...`;
}

function worldLabelVisibilityLabel(value: MysteryRoomWorldLabelVisibility): string {
  switch (value) {
    case 'always':
      return 'Видно в игре';
    case 'near_interactive':
      return 'Рядом с интерактивным';
    case 'hidden':
    default:
      return 'Не видно в игре';
  }
}

function editorSelectionKey(selection: MysteryRoomEditorSelection): string {
  return `${selection.type}:${selection.id}`;
}

function editorSelectionsEqual(left: MysteryRoomEditorSelection | null, right: MysteryRoomEditorSelection | null): boolean {
  return Boolean(left && right && left.type === right.type && left.id === right.id);
}

function uniqueEditorSelections(selections: MysteryRoomEditorSelection[]): MysteryRoomEditorSelection[] {
  const seen = new Set<string>();
  const result: MysteryRoomEditorSelection[] = [];
  selections.forEach((selection) => {
    const key = editorSelectionKey(selection);
    if (seen.has(key)) return;
    seen.add(key);
    result.push(selection);
  });
  return result;
}

function isSelected(
  selection: MysteryRoomEditorSelection | null,
  type: MysteryRoomEditorSelection['type'],
  id: string,
  additionalSelections: MysteryRoomEditorSelection[] = [],
): boolean {
  return Boolean(
    (selection?.type === type && selection.id === id) ||
      additionalSelections.some((entry) => entry.type === type && entry.id === id),
  );
}

function rectsIntersect(left: MysteryRoomRect, right: MysteryRoomRect): boolean {
  return (
    left.x + left.width >= right.x &&
    left.x <= right.x + right.width &&
    left.y + left.height >= right.y &&
    left.y <= right.y + right.height
  );
}

function selectionRectFromPoints(start: MysteryRoomVec2, current: MysteryRoomVec2): MysteryRoomRect {
  const x = Math.min(start.x, current.x);
  const y = Math.min(start.y, current.y);
  return {
    x,
    y,
    width: Math.abs(current.x - start.x),
    height: Math.abs(current.y - start.y),
  };
}

function EditorEntityLabel({ x, y, label, fill = '#e2e8f0' }: { x: number; y: number; label: string; fill?: string }) {
  return (
    <text
      x={x}
      y={y}
      textAnchor="middle"
      dominantBaseline="hanging"
      fill={fill}
      fontSize="22"
      fontFamily="Inter, ui-sans-serif, system-ui"
      fontWeight="800"
      paintOrder="stroke"
      stroke="#020617"
      strokeWidth="5"
      pointerEvents="none"
    >
      {compactSvgLabel(label)}
    </text>
  );
}

type TransformGizmoHandleConfig = {
  handle: MysteryRoomEditorTransformHandle;
  label: string;
  title: string;
  cursor: string;
};

const STATIC_TRANSFORM_GIZMO_HANDLES: Record<'rotate' | 'length' | 'thickness' | 'radius', TransformGizmoHandleConfig> = {
  rotate: { handle: 'rotate', label: '↻', title: 'Тянуть для точного поворота вокруг центра', cursor: 'grab' },
  length: { handle: 'length', label: 'L', title: 'Тянуть для изменения длины', cursor: 'ew-resize' },
  thickness: { handle: 'thickness', label: 'T', title: 'Тянуть для изменения толщины', cursor: 'ns-resize' },
  radius: { handle: 'radius', label: 'R', title: 'Тянуть для изменения радиуса', cursor: 'ew-resize' },
};

function transformGizmoHandleConfig(handle: MysteryRoomEditorTransformHandle): TransformGizmoHandleConfig {
  if (isRectSideHandle(handle)) {
    const side = rectSideFromHandle(handle);
    const labels: Record<MysteryRoomEditorRectSide, string> = {
      top: '↑',
      right: '→',
      bottom: '↓',
      left: '←',
    };
    const titles: Record<MysteryRoomEditorRectSide, string> = {
      top: 'Тянуть верхнюю сторону',
      right: 'Тянуть правую сторону',
      bottom: 'Тянуть нижнюю сторону',
      left: 'Тянуть левую сторону',
    };
    return {
      handle,
      label: labels[side],
      title: titles[side],
      cursor: side === 'left' || side === 'right' ? 'ew-resize' : 'ns-resize',
    };
  }
  if (isWallEndpointHandle(handle)) {
    const endpoint = wallEndpointFromHandle(handle);
    return {
      handle,
      label: endpoint === 'start' ? '◀' : '▶',
      title: endpoint === 'start' ? 'Тянуть начало стены' : 'Тянуть конец стены',
      cursor: 'grab',
    };
  }
  if (isCustomObjectEdgeHandle(handle)) {
    return {
      handle,
      label: '↔',
      title: `Тянуть грань ${customObjectEdgeHandleIndex(handle) + 1}`,
      cursor: 'grab',
    };
  }
  return STATIC_TRANSFORM_GIZMO_HANDLES[handle as keyof typeof STATIC_TRANSFORM_GIZMO_HANDLES];
}

function SelectionTransformGizmo({
  map,
  selection,
  viewBox,
  viewportSize,
  activeHandle,
  onHandlePointerDown,
  onLayerChange,
}: {
  map: MysteryRoomMapDefinition;
  selection: MysteryRoomEditorSelection | null;
  viewBox: ViewBox;
  viewportSize: SvgViewportSize;
  activeHandle?: MysteryRoomEditorTransformHandle | null;
  onHandlePointerDown: (event: PointerEvent<SVGGElement>, handle: MysteryRoomEditorTransformHandle) => void;
  onLayerChange: (event: PointerEvent<SVGGElement>, delta: number) => void;
}) {
  const bounds = getSelectionGizmoBounds(map, selection);
  const center = getSelectionGizmoCenter(map, selection);
  const handles = getSelectionGizmoHandles(map, selection);
  if (!bounds || !center || handles.length === 0) return null;

  const unitX = viewBox.width / Math.max(1, viewportSize.width);
  const unitY = viewBox.height / Math.max(1, viewportSize.height);
  const unit = Math.max(unitX, unitY);
  const sizeX = unitX * 34;
  const sizeY = unitY * 34;
  const radius = Math.max(sizeX, sizeY) / 2;
  const stroke = Math.max(1.5, unit * 2.2);
  const outlinePadX = unitX * 8;
  const outlinePadY = unitY * 8;
  const handleOffsetX = unitX * 62;
  const handleOffsetY = unitY * 62;
  const layerButtonY = center.y - handleOffsetY;
  const layerButtonX = center.x + handleOffsetX;
  const positions = new Map<MysteryRoomEditorTransformHandle, MysteryRoomVec2>([
    ['rotate', { x: center.x, y: center.y - handleOffsetY }],
    ['length', { x: center.x + handleOffsetX, y: center.y }],
    ['thickness', { x: center.x, y: center.y + handleOffsetY }],
    ['radius', { x: center.x + handleOffsetX, y: center.y }],
  ]);
  for (const position of getRectSideHandlePositions(bounds, Math.max(unitX, unitY) * 44)) {
    positions.set(rectSideHandle(position.side), position.point);
  }
  if (selection?.type === 'wall') {
    for (const position of getWallEndpointHandlePositions(map, selection.id, Math.max(unitX, unitY) * 44)) {
      positions.set(wallEndpointHandle(position.endpoint), position.point);
    }
  }
  if (selection?.type === 'customObject') {
    for (const position of getCustomObjectEdgeHandlePositions(map, selection.id, unit * 46)) {
      positions.set(customObjectEdgeHandle(position.edgeIndex), position.point);
    }
  }

  return (
    <g
      pointerEvents="all"
      onPointerDown={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
    >
      <rect
        x={bounds.x - outlinePadX}
        y={bounds.y - outlinePadY}
        width={bounds.width + outlinePadX * 2}
        height={bounds.height + outlinePadY * 2}
        rx={10 * unit}
        fill="none"
        stroke="#fde68a"
        strokeOpacity="0.86"
        strokeWidth={stroke}
        strokeDasharray={`${8 * unit} ${5 * unit}`}
        pointerEvents="none"
      />
      <circle cx={center.x} cy={center.y} r={Math.max(4 * unit, 3)} fill="#fde68a" stroke="#020617" strokeWidth={stroke * 0.9} pointerEvents="none" />
      {handles.map((handle) => {
        const position = positions.get(handle);
        if (!position) return null;
        return (
          <TransformGizmoSvgHandle
            key={handle}
            config={transformGizmoHandleConfig(handle)}
            x={position.x}
            y={position.y}
            sizeX={sizeX}
            sizeY={sizeY}
            radius={radius}
            strokeWidth={stroke}
            active={activeHandle === handle}
            onPointerDown={onHandlePointerDown}
          />
        );
      })}
      {isLayerQuickControlSelection(selection) ? (
        <>
          <LayerGizmoSvgButton
            x={layerButtonX}
            y={layerButtonY - sizeY * 0.64}
            sizeX={sizeX}
            sizeY={sizeY * 0.78}
            strokeWidth={stroke}
            label="↑"
            title="Поднять слой"
            onPointerDown={(event) => onLayerChange(event, 1)}
          />
          <LayerGizmoSvgButton
            x={layerButtonX}
            y={layerButtonY + sizeY * 0.64}
            sizeX={sizeX}
            sizeY={sizeY * 0.78}
            strokeWidth={stroke}
            label="↓"
            title="Опустить слой"
            onPointerDown={(event) => onLayerChange(event, -1)}
          />
        </>
      ) : null}
    </g>
  );
}

function LayerGizmoSvgButton({
  x,
  y,
  sizeX,
  sizeY,
  strokeWidth,
  label,
  title,
  onPointerDown,
}: {
  x: number;
  y: number;
  sizeX: number;
  sizeY: number;
  strokeWidth: number;
  label: string;
  title: string;
  onPointerDown: (event: PointerEvent<SVGGElement>) => void;
}) {
  return (
    <g
      style={{ cursor: 'pointer' }}
      onPointerDown={(event) => {
        if (event.button !== LEFT_MOUSE_BUTTON) return;
        event.preventDefault();
        event.stopPropagation();
        onPointerDown(event);
      }}
    >
      <title>{title}</title>
      <rect
        x={x - sizeX / 2}
        y={y - sizeY / 2}
        width={sizeX}
        height={sizeY}
        rx={Math.min(sizeX, sizeY) * 0.24}
        fill="#bae6fd"
        fillOpacity="0.98"
        stroke="#020617"
        strokeWidth={strokeWidth}
      />
      <text
        x={x}
        y={y + sizeY * 0.03}
        textAnchor="middle"
        dominantBaseline="middle"
        fill="#0f172a"
        fontSize={Math.min(sizeX, sizeY) * 0.62}
        fontFamily="Inter, ui-sans-serif, system-ui"
        fontWeight="900"
        pointerEvents="none"
      >
        {label}
      </text>
    </g>
  );
}

function TransformGizmoSvgHandle({
  x,
  y,
  sizeX,
  sizeY,
  radius,
  strokeWidth,
  config,
  active,
  onPointerDown,
}: {
  x: number;
  y: number;
  sizeX: number;
  sizeY: number;
  radius: number;
  strokeWidth: number;
  config: TransformGizmoHandleConfig;
  active: boolean;
  onPointerDown: (event: PointerEvent<SVGGElement>, handle: MysteryRoomEditorTransformHandle) => void;
}) {
  return (
    <g
      style={{ cursor: config.cursor }}
      onPointerDown={(event) => {
        if (event.button !== LEFT_MOUSE_BUTTON) return;
        event.preventDefault();
        event.stopPropagation();
        onPointerDown(event, config.handle);
      }}
    >
      <title>{config.title}</title>
      <rect
        x={x - sizeX / 2}
        y={y - sizeY / 2}
        width={sizeX}
        height={sizeY}
        rx={Math.min(sizeX, sizeY) * 0.26}
        fill={active ? '#fef3c7' : '#fde68a'}
        fillOpacity="0.98"
        stroke={active ? '#ffffff' : '#020617'}
        strokeWidth={strokeWidth}
      />
      <text
        x={x}
        y={y + sizeY * 0.04}
        textAnchor="middle"
        dominantBaseline="middle"
        fill="#0f172a"
        fontSize={radius * 0.92}
        fontFamily="Inter, ui-sans-serif, system-ui"
        fontWeight="900"
        pointerEvents="none"
      >
        {config.label}
      </text>
    </g>
  );
}

function getSelectionGizmoHandles(map: MysteryRoomMapDefinition, selection: MysteryRoomEditorSelection | null): MysteryRoomEditorTransformHandle[] {
  if (!selection) return [];
  const rectSideHandles = RECT_SIDE_TRANSFORM_HANDLES;
  switch (selection.type) {
    case 'customObject': {
      const object = map.customObjects.find((entry) => entry.id === selection.id);
      const edgeCount = object ? Math.max(1, getCustomObjectShapePointCount(object)) : 0;
      return ['rotate', ...Array.from({ length: edgeCount }, (_, index) => customObjectEdgeHandle(index))];
    }
    case 'door':
      return ['rotate', ...rectSideHandles];
    case 'wall':
      return ['rotate', wallEndpointHandle('start'), wallEndpointHandle('end')];
    case 'room':
    case 'trigger':
      return [...rectSideHandles];
    case 'light':
    case 'hazard':
    case 'interactable':
    case 'soundSource':
      return ['radius'];
    default:
      return [];
  }
}

function isTextureAssignableSelection(selection: MysteryRoomEditorSelection): boolean {
  return ['room', 'wall', 'door', 'customObject', 'light', 'interactable', 'trigger', 'hazard'].includes(selection.type);
}

const RECT_SIDE_TRANSFORM_HANDLES = [
  rectSideHandle('top'),
  rectSideHandle('right'),
  rectSideHandle('bottom'),
  rectSideHandle('left'),
] as const;

function getRectSideHandlePositions(
  bounds: MysteryRoomRect,
  offset: number,
): Array<{ side: MysteryRoomEditorRectSide; point: MysteryRoomVec2 }> {
  const center = rectCenter(bounds);
  return [
    { side: 'top', point: { x: center.x, y: bounds.y - offset } },
    { side: 'right', point: { x: bounds.x + bounds.width + offset, y: center.y } },
    { side: 'bottom', point: { x: center.x, y: bounds.y + bounds.height + offset } },
    { side: 'left', point: { x: bounds.x - offset, y: center.y } },
  ];
}

function getWallEndpointHandlePositions(
  map: MysteryRoomMapDefinition,
  wallId: string,
  offset: number,
): Array<{ endpoint: MysteryRoomEditorWallEndpoint; point: MysteryRoomVec2 }> {
  const wall = map.walls[Number(wallId)];
  if (!wall) return [];
  const dx = wall.end.x - wall.start.x;
  const dy = wall.end.y - wall.start.y;
  const length = Math.max(1, Math.hypot(dx, dy));
  const axis = { x: dx / length, y: dy / length };
  return [
    { endpoint: 'start', point: { x: wall.start.x - axis.x * offset, y: wall.start.y - axis.y * offset } },
    { endpoint: 'end', point: { x: wall.end.x + axis.x * offset, y: wall.end.y + axis.y * offset } },
  ];
}

function getCustomObjectEdgeHandlePositions(
  map: MysteryRoomMapDefinition,
  objectId: string,
  offset: number,
): Array<{ edgeIndex: number; point: MysteryRoomVec2 }> {
  const object = map.customObjects.find((entry) => entry.id === objectId);
  if (!object) return [];
  const points = getCustomObjectShapeWorldPoints(object);
  if (points.length === 0) return [];
  if (points.length === 1) {
    return [{ edgeIndex: 0, point: { x: points[0].x + offset, y: points[0].y } }];
  }
  const objectCenter = rectCenter(object.rect);
  return points.map((point, index) => {
    const next = points[(index + 1) % points.length] ?? point;
    const center = { x: (point.x + next.x) / 2, y: (point.y + next.y) / 2 };
    const baseNormal = outwardWorldNormal(points, index);
    const awayFromObject = { x: center.x - objectCenter.x, y: center.y - objectCenter.y };
    const normal =
      awayFromObject.x * baseNormal.x + awayFromObject.y * baseNormal.y < 0
        ? { x: -baseNormal.x, y: -baseNormal.y }
        : baseNormal;
    return {
      edgeIndex: index,
      point: {
        x: center.x + normal.x * offset,
        y: center.y + normal.y * offset,
      },
    };
  });
}

function outwardWorldNormal(points: MysteryRoomVec2[], index: number): MysteryRoomVec2 {
  const point = points[index] ?? points[0];
  const next = points[(index + 1) % points.length] ?? point;
  const edge = { x: next.x - point.x, y: next.y - point.y };
  const area = points.reduce((sum, current, currentIndex) => {
    const nextPoint = points[(currentIndex + 1) % points.length] ?? current;
    return sum + current.x * nextPoint.y - nextPoint.x * current.y;
  }, 0) / 2;
  const normal = area < 0 ? { x: edge.y, y: -edge.x } : { x: -edge.y, y: edge.x };
  const length = Math.hypot(normal.x, normal.y);
  return length < 0.0001 ? { x: 1, y: 0 } : { x: normal.x / length, y: normal.y / length };
}

function isLayerQuickControlSelection(
  selection: MysteryRoomEditorSelection | null,
): selection is MysteryRoomEditorSelection & { type: 'wall' | 'door' | 'customObject' | 'light' | 'interactable' | 'trigger' | 'soundSource' } {
  return (
    selection?.type === 'wall' ||
    selection?.type === 'door' ||
    selection?.type === 'customObject' ||
    selection?.type === 'light' ||
    selection?.type === 'interactable' ||
    selection?.type === 'trigger' ||
    selection?.type === 'soundSource'
  );
}

function getSelectionGizmoBounds(map: MysteryRoomMapDefinition, selection: MysteryRoomEditorSelection | null): MysteryRoomRect | null {
  if (!selection) return null;
  switch (selection.type) {
    case 'room': {
      return map.rooms.find((room) => room.id === selection.id)?.floor[0] ?? null;
    }
    case 'wall': {
      const wall = map.walls[Number(selection.id)];
      if (!wall) return null;
      const minX = Math.min(wall.start.x, wall.end.x);
      const minY = Math.min(wall.start.y, wall.end.y);
      const maxX = Math.max(wall.start.x, wall.end.x);
      const maxY = Math.max(wall.start.y, wall.end.y);
      return { x: minX - 24, y: minY - 24, width: Math.max(48, maxX - minX + 48), height: Math.max(48, maxY - minY + 48) };
    }
    case 'door':
      return map.doors.find((door) => door.id === selection.id)?.panel ?? null;
    case 'customObject':
      return map.customObjects.find((object) => object.id === selection.id)?.rect ?? null;
    case 'light': {
      const light = map.lights.find((entry) => entry.id === selection.id);
      return light ? pointIconBounds(light.position, 72) : null;
    }
    case 'hazard': {
      const hazard = map.hazards.find((entry) => entry.id === selection.id);
      return hazard ? pointIconBounds(hazard.position, 72) : null;
    }
    case 'interactable': {
      const entry = map.interactables.find((item) => item.id === selection.id);
      return entry ? pointIconBounds(entry.anchor, 68) : null;
    }
    case 'trigger':
      return map.triggers.find((trigger) => trigger.id === selection.id)?.rect ?? null;
    case 'incidentAnchor': {
      const anchor = map.incidentAnchors.find((entry) => entry.id === selection.id);
      return anchor ? pointIconBounds(anchor.position, 68) : null;
    }
    case 'workAnchor': {
      const anchor = map.workAnchors.find((entry) => entry.id === selection.id);
      return anchor ? pointIconBounds(anchor.position, 68) : null;
    }
    case 'soundSource': {
      const source = map.soundSources.find((entry) => entry.id === selection.id);
      return source ? pointIconBounds(source.position, 72) : null;
    }
    default:
      return null;
  }
}

function getSelectionGizmoCenter(map: MysteryRoomMapDefinition, selection: MysteryRoomEditorSelection | null): MysteryRoomVec2 | null {
  if (!selection) return null;
  switch (selection.type) {
    case 'room': {
      const rect = map.rooms.find((room) => room.id === selection.id)?.floor[0];
      return rect ? rectCenter(rect) : null;
    }
    case 'wall': {
      const wall = map.walls[Number(selection.id)];
      return wall ? { x: (wall.start.x + wall.end.x) / 2, y: (wall.start.y + wall.end.y) / 2 } : null;
    }
    case 'door': {
      const panel = map.doors.find((door) => door.id === selection.id)?.panel;
      return panel ? rectCenter(panel) : null;
    }
    case 'customObject': {
      const object = map.customObjects.find((entry) => entry.id === selection.id);
      return object ? rectCenter(object.rect) : null;
    }
    case 'light':
      return map.lights.find((entry) => entry.id === selection.id)?.position ?? null;
    case 'hazard':
      return map.hazards.find((entry) => entry.id === selection.id)?.position ?? null;
    case 'interactable':
      return map.interactables.find((entry) => entry.id === selection.id)?.anchor ?? null;
    case 'trigger': {
      const trigger = map.triggers.find((entry) => entry.id === selection.id);
      return trigger ? rectCenter(trigger.rect) : null;
    }
    case 'incidentAnchor':
      return map.incidentAnchors.find((entry) => entry.id === selection.id)?.position ?? null;
    case 'workAnchor':
      return map.workAnchors.find((entry) => entry.id === selection.id)?.position ?? null;
    case 'soundSource':
      return map.soundSources.find((entry) => entry.id === selection.id)?.position ?? null;
    default:
      return null;
  }
}

function pointIconBounds(point: MysteryRoomVec2, size: number): MysteryRoomRect {
  return {
    x: point.x - size / 2,
    y: point.y - size / 2,
    width: size,
    height: size,
  };
}

function textureLayersFromLegacy(
  layers: MysteryRoomTextureLayer[] | undefined,
  legacy: MysteryRoomSurfaceTexture | undefined,
  prefix: string,
): MysteryRoomTextureLayer[] {
  if (layers?.length) {
    return layers.map(normalizeTextureLayer);
  }
  if (!legacy?.assetUrl && !legacy?.assetId && !legacy?.builtinTextureId) {
    return [];
  }
  return [
    normalizeTextureLayer(
      {
        ...legacy,
        id: `${prefix}_texture_1`,
        label: legacy.assetName ?? 'Текстура',
        visible: true,
      },
      0,
    ),
  ];
}

function textureLayerSvgFilter(layer: MysteryRoomTextureLayer): string {
  const effects = layer.effects ?? {};
  const brightness = 100 + normalizeTextureEffectIntensity(effects.brightness);
  const contrast = 100 + normalizeTextureEffectIntensity(effects.contrast);
  return `brightness(${Math.max(0, brightness)}%) contrast(${Math.max(0, contrast)}%)`;
}

function textureLayerOverlayColor(layer: MysteryRoomTextureLayer): { color: string; opacity: number } | null {
  const effects = layer.effects ?? {};
  const shading = normalizeTextureEffectIntensity(effects.shading);
  const reflection = normalizeTextureEffectIntensity(effects.reflection);
  if (shading > 0) {
    return { color: '#020617', opacity: Math.min(0.55, shading / 180) };
  }
  if (reflection > 0) {
    return { color: '#ffffff', opacity: Math.min(0.34, reflection / 260) };
  }
  if (reflection < 0) {
    return { color: '#020617', opacity: Math.min(0.3, Math.abs(reflection) / 260) };
  }
  return null;
}

function useSvgImageNaturalSizes(assetUrls: string[]): Record<string, { width: number; height: number }> {
  const [naturalSizes, setNaturalSizes] = useState<Record<string, { width: number; height: number }>>({});
  useEffect(() => {
    const missingUrls = assetUrls.filter((assetUrl, index, urls) => !naturalSizes[assetUrl] && urls.indexOf(assetUrl) === index);
    if (missingUrls.length === 0) return;
    let cancelled = false;
    missingUrls.forEach((assetUrl) => {
      const image = new Image();
      image.onload = () => {
        if (cancelled) return;
        const width = image.naturalWidth || image.width;
        const height = image.naturalHeight || image.height;
        if (!width || !height) return;
        setNaturalSizes((current) =>
          current[assetUrl]
            ? current
            : {
                ...current,
                [assetUrl]: { width, height },
              },
        );
      };
      image.src = assetUrl;
    });
    return () => {
      cancelled = true;
    };
  }, [assetUrls, naturalSizes]);
  return naturalSizes;
}

function TextureLayersSvg({
  idPrefix,
  layers,
  rect,
  clip,
}: {
  idPrefix: string;
  layers: MysteryRoomTextureLayer[];
  rect: MysteryRoomRect;
  clip?: ReactNode;
}) {
  const visibleLayers = useMemo(
    () =>
      layers
        .map(normalizeTextureLayer)
        .map((layer) => ({ layer, assetUrl: resolveMysteryRoomTextureAssetUrl(layer) }))
        .filter(
          (entry): entry is { layer: MysteryRoomTextureLayer; assetUrl: string } => entry.layer.visible !== false && Boolean(entry.assetUrl),
        ),
    [layers],
  );
  const visibleLayerUrls = useMemo(() => visibleLayers.map(({ assetUrl }) => assetUrl), [visibleLayers]);
  const naturalSizes = useSvgImageNaturalSizes(visibleLayerUrls);
  if (visibleLayers.length === 0) return null;
  const clipId = clip ? `${sanitizeSvgId(idPrefix)}_clip` : '';
  return (
    <svg
      x={rect.x}
      y={rect.y}
      width={rect.width}
      height={rect.height}
      viewBox={`0 0 ${rect.width} ${rect.height}`}
      overflow="hidden"
      pointerEvents="none"
    >
      <defs>
        {clip ? <clipPath id={clipId} clipPathUnits="userSpaceOnUse">{clip}</clipPath> : null}
        {visibleLayers.map(({ layer, assetUrl }, index) => {
          if (layer.fit !== 'repeat') return null;
          const patternId = `${sanitizeSvgId(idPrefix)}_${sanitizeSvgId(layer.id)}_${index}`;
          const fallbackTileSize = getMysteryRoomTextureTileSize(layer);
          const naturalSize = naturalSizes[assetUrl] ?? { width: fallbackTileSize, height: fallbackTileSize };
          const tileSize = getMysteryRoomTextureWorldTileSize(layer, naturalSize);
          return (
            <pattern
              key={patternId}
              id={patternId}
              patternUnits="userSpaceOnUse"
              x={-finiteEditorNumber(layer.offsetX, 0)}
              y={-finiteEditorNumber(layer.offsetY, 0)}
              width={tileSize.width}
              height={tileSize.height}
              patternTransform={`rotate(${finiteEditorNumber(layer.rotation, 0)} ${rect.width / 2} ${rect.height / 2})`}
            >
              <image
                href={assetUrl}
                x={0}
                y={0}
                width={tileSize.width}
                height={tileSize.height}
                preserveAspectRatio="none"
                opacity={normalizeSurfaceTextureOpacity(layer.opacity)}
                filter={textureLayerSvgFilter(layer)}
              />
            </pattern>
          );
        })}
      </defs>
      <g clipPath={clip ? `url(#${clipId})` : undefined}>
        {visibleLayers.map(({ layer, assetUrl }, index) => {
          const overlay = textureLayerOverlayColor(layer);
          const grain = Math.max(0, normalizeTextureEffectIntensity(layer.effects?.grain));
          const vignette = normalizeTextureEffectIntensity(layer.effects?.vignette);
          const baseKey = `${layer.id}:${index}`;
          if (layer.fit === 'repeat') {
            const patternId = `${sanitizeSvgId(idPrefix)}_${sanitizeSvgId(layer.id)}_${index}`;
            return (
              <g key={baseKey}>
                <rect x={0} y={0} width={rect.width} height={rect.height} fill={`url(#${patternId})`} />
                {overlay ? <rect x={0} y={0} width={rect.width} height={rect.height} fill={overlay.color} fillOpacity={overlay.opacity} /> : null}
                {vignette !== 0 ? <rect x={0} y={0} width={rect.width} height={rect.height} fill="#020617" fillOpacity={Math.min(0.42, Math.abs(vignette) / 240)} /> : null}
                {grain > 0 ? <TextureGrainOverlay rect={rect} intensity={grain} /> : null}
              </g>
            );
          }
          const naturalSize = naturalSizes[assetUrl];
          const imageBox = svgImageBoxForFit(layer.fit, rect.width, rect.height, naturalSize);
          return (
            <g key={baseKey}>
              <g
                transform={`translate(${rect.width / 2 + finiteEditorNumber(layer.offsetX, 0)} ${rect.height / 2 + finiteEditorNumber(layer.offsetY, 0)}) rotate(${finiteEditorNumber(layer.rotation, 0)})`}
              >
                <image
                  href={assetUrl}
                  x={imageBox.x - rect.width / 2}
                  y={imageBox.y - rect.height / 2}
                  width={imageBox.width}
                  height={imageBox.height}
                  preserveAspectRatio="none"
                  opacity={normalizeSurfaceTextureOpacity(layer.opacity)}
                  filter={textureLayerSvgFilter(layer)}
                />
              </g>
              {overlay ? <rect x={0} y={0} width={rect.width} height={rect.height} fill={overlay.color} fillOpacity={overlay.opacity} /> : null}
              {vignette !== 0 ? <rect x={0} y={0} width={rect.width} height={rect.height} fill="#020617" fillOpacity={Math.min(0.42, Math.abs(vignette) / 240)} /> : null}
              {grain > 0 ? <TextureGrainOverlay rect={rect} intensity={grain} /> : null}
            </g>
          );
        })}
      </g>
    </svg>
  );
}

function TextureGrainOverlay({ rect, intensity }: { rect: MysteryRoomRect; intensity: number }) {
  const gap = Math.max(8, 34 - intensity / 4);
  const opacity = Math.min(0.22, intensity / 520);
  const dots: ReactNode[] = [];
  for (let y = 3; y < rect.height; y += gap) {
    for (let x = 5; x < rect.width; x += gap) {
      const jitter = ((Math.round(x * 13 + y * 7) % 9) - 4) * 0.5;
      dots.push(<circle key={`${x}:${y}`} cx={x + jitter} cy={y - jitter} r={1.2} fill="#f8fafc" fillOpacity={opacity} />);
    }
  }
  return <g>{dots}</g>;
}

function wallEditorNodeKey(point: MysteryRoomVec2): string {
  return `${Math.round(point.x)}:${Math.round(point.y)}`;
}

function wallJointPolygonPoints(node: MysteryRoomVec2, walls: MysteryRoomMapDefinition['walls'], width: number): MysteryRoomVec2[] {
  const half = width / 2;
  const points: MysteryRoomVec2[] = [];
  walls.forEach((wall) => {
    const usesStart = wallEditorNodeKey(wall.start) === wallEditorNodeKey(node);
    const usesEnd = wallEditorNodeKey(wall.end) === wallEditorNodeKey(node);
    if (!usesStart && !usesEnd) return;
    const other = usesStart ? wall.end : wall.start;
    const dx = other.x - node.x;
    const dy = other.y - node.y;
    const length = Math.hypot(dx, dy);
    if (length <= 0.001) return;
    const nx = (-dy / length) * half;
    const ny = (dx / length) * half;
    points.push({ x: node.x + nx, y: node.y + ny }, { x: node.x - nx, y: node.y - ny });
  });
  return points.sort((left, right) => Math.atan2(left.y - node.y, left.x - node.x) - Math.atan2(right.y - node.y, right.x - node.x));
}

function WallJointGlyph({
  node,
  walls,
  selected,
}: {
  node: MysteryRoomVec2;
  walls: MysteryRoomMapDefinition['walls'];
  selected: boolean;
}) {
  if (walls.length < 2) return null;
  const baseWall = walls[0];
  const thickness = selected ? 18 : 12;
  const outlineWidthValue = outlineWidth(baseWall, selected ? 3 : 1);
  const outlinePoints = wallJointPolygonPoints(node, walls, thickness + outlineWidthValue * 2);
  const bodyPoints = wallJointPolygonPoints(node, walls, thickness);
  if (bodyPoints.length < 3) return null;
  return (
    <g pointerEvents="none">
      {outlineWidthValue > 0 && outlinePoints.length >= 3 ? (
        <polygon
          points={outlinePoints.map((point) => `${point.x},${point.y}`).join(' ')}
          fill={numberToHexColor(baseWall.outlineColor, '#e2e8f0')}
          fillOpacity={outlineOpacity(baseWall, 0.52)}
        />
      ) : null}
      <polygon
        points={bodyPoints.map((point) => `${point.x},${point.y}`).join(' ')}
        fill={numberToHexColor(baseWall.color ?? 0x0f172a)}
        fillOpacity={baseColorOpacity(baseWall, 0.72)}
      />
    </g>
  );
}

function RoomFloorSurface({
  room,
  floor,
  selected,
  index,
}: {
  room: MysteryRoomMapDefinition['rooms'][number];
  floor: MysteryRoomRect;
  selected: boolean;
  index: number;
}) {
  const textureLayers = textureLayersFromLegacy(room.textures, room.floorTexture, room.id);
  const hasTexture = textureLayers.length > 0;
  const roomOutlineColor = numberToHexColor(room.outlineColor, '#e2e8f0');
  const roomOutlineOpacity = outlineOpacity(room, 0.35);
  const roomOutlineWidth = outlineWidth(room, selected ? 8 : 3);
  return (
    <g key={`${room.id}:${index}`}>
      <rect
        x={floor.x}
        y={floor.y}
        width={floor.width}
        height={floor.height}
        rx={18}
        fill={numberToHexColor(room.color)}
        fillOpacity={baseColorOpacity(room, hasTexture ? 0.34 : 0.62)}
        stroke={roomOutlineColor}
        strokeOpacity={roomOutlineOpacity}
        strokeWidth={roomOutlineWidth}
      />
      <TextureLayersSvg idPrefix={`room_${room.id}_${index}`} layers={textureLayers} rect={floor} />
      {hasTexture ? (
        <rect
          x={floor.x}
          y={floor.y}
          width={floor.width}
          height={floor.height}
          rx={18}
          fill="none"
          stroke={roomOutlineColor}
          strokeOpacity={roomOutlineOpacity}
          strokeWidth={roomOutlineWidth}
          pointerEvents="none"
        />
      ) : null}
    </g>
  );
}

function WallGlyph({
  wall,
  selected,
  className = 'cursor-move',
  onPointerDown,
}: {
  wall: MysteryRoomMapDefinition['walls'][number];
  selected: boolean;
  className?: string;
  onPointerDown: (event: PointerEvent<SVGElement>) => void;
}) {
  const dx = wall.end.x - wall.start.x;
  const dy = wall.end.y - wall.start.y;
  const length = Math.max(1, Math.hypot(dx, dy));
  const cx = (wall.start.x + wall.end.x) / 2;
  const cy = (wall.start.y + wall.end.y) / 2;
  const angle = (Math.atan2(dy, dx) * 180) / Math.PI;
  const thickness = selected ? 18 : 12;
  const textureLayers = textureLayersFromLegacy(wall.textures, wall.texture, 'wall');
  const hasTexture = textureLayers.length > 0;
  const wallOutlineColor = numberToHexColor(wall.outlineColor, '#e2e8f0');
  const wallOutlineOpacity = outlineOpacity(wall, 0.52);
  const wallOutlineWidth = outlineWidth(wall, selected ? 3 : 1);

  if (!hasTexture) {
    return (
      <g onPointerDown={onPointerDown} className={className}>
        <line
          x1={wall.start.x}
          y1={wall.start.y}
          x2={wall.end.x}
          y2={wall.end.y}
          stroke={wallOutlineColor}
          strokeOpacity={wallOutlineOpacity}
          strokeWidth={thickness + wallOutlineWidth * 2}
          strokeLinecap="square"
          pointerEvents="none"
        />
        <line
          x1={wall.start.x}
          y1={wall.start.y}
          x2={wall.end.x}
          y2={wall.end.y}
          stroke={numberToHexColor(wall.color ?? 0x0f172a)}
          strokeOpacity={baseColorOpacity(wall, 0.72)}
          strokeWidth={thickness}
          strokeLinecap="square"
        />
      </g>
    );
  }

  return (
    <g transform={`translate(${cx} ${cy}) rotate(${angle})`} onPointerDown={onPointerDown} className={className}>
      <rect
        x={-length / 2}
        y={-thickness / 2}
        width={length}
        height={thickness}
        rx={0}
        fill={numberToHexColor(wall.color ?? 0x0f172a)}
        fillOpacity={baseColorOpacity(wall, 0.72)}
        stroke={wallOutlineColor}
        strokeOpacity={wallOutlineOpacity}
        strokeWidth={wallOutlineWidth}
      />
      <TextureLayersSvg
        idPrefix={`wall_${wall.start.x}_${wall.start.y}_${wall.end.x}_${wall.end.y}`}
        layers={textureLayers}
        rect={{ x: -length / 2, y: -thickness / 2, width: length, height: thickness }}
      />
      <rect
        x={-length / 2}
        y={-thickness / 2}
        width={length}
        height={thickness}
        rx={0}
        fill="none"
        stroke={wallOutlineColor}
        strokeOpacity={wallOutlineOpacity}
        strokeWidth={wallOutlineWidth}
        pointerEvents="none"
      />
    </g>
  );
}

function CustomObjectGlyph({
  object,
  selected,
  onPointerDown,
}: {
  object: MysteryRoomCustomObjectDefinition;
  selected: boolean;
  onPointerDown: (event: PointerEvent<SVGGElement>) => void;
}) {
  const cx = object.rect.x + object.rect.width / 2;
  const cy = object.rect.y + object.rect.height / 2;
  const fill = numberToHexColor(object.color);
  const bodyStroke = numberToHexColor(object.outlineColor ?? object.accentColor, '#e2e8f0');
  const bodyStrokeOpacity = outlineOpacity(object, object.interactive ? 0.88 : 0.5);
  const bodyStrokeWidth = outlineWidth(object, selected ? 5 : object.collision.kind !== 'none' ? 4 : 3);
  const bodyOpacity = object.tags.includes('collision') ? Math.min(baseColorOpacity(object, 0.9), 0.18) : baseColorOpacity(object, 0.9);
  const visualAsset = getMysteryRoomEffectiveCustomObjectAsset(object);
  const visualAssetUrl = visualAsset?.assetUrl ?? '';
  const visualAssetUrls = useMemo(() => (visualAssetUrl ? [visualAssetUrl] : []), [visualAssetUrl]);
  const visualAssetNaturalSizes = useSvgImageNaturalSizes(visualAssetUrls);
  const visualAssetBox = visualAsset
    ? svgImageBoxForFit(visualAsset.assetFit, object.rect.width, object.rect.height, visualAssetNaturalSizes[visualAsset.assetUrl])
    : null;
  const assetTransform = normalizeEditorAssetTransform({
    ...object,
    assetTransform: visualAsset?.assetTransform ?? object.assetTransform,
  });
  const assetClipPathId = assetTransform.clipToBounds ? `mr-editor-object-asset-clip-${sanitizeSvgId(object.id)}` : '';
  const assetClipPath = assetClipPathId ? customObjectImageClipPathElement(object, assetClipPathId) : null;
  const fallbackDotRadius = Math.max(4, Math.min(8, Math.min(object.rect.width, object.rect.height) * 0.12));

  return (
    <g>
      <g transform={`rotate(${object.rotation} ${cx} ${cy})`}>
        <g onPointerDown={onPointerDown} className="cursor-move">
          <rect
            x={object.rect.x}
            y={object.rect.y}
            width={object.rect.width}
            height={object.rect.height}
            fill="transparent"
            pointerEvents="all"
          />
          {!visualAsset ? (
            <circle
              cx={cx}
              cy={cy}
              r={fallbackDotRadius}
              fill={fill}
              fillOpacity={Math.max(0.32, bodyOpacity)}
              stroke={bodyStroke}
              strokeOpacity={bodyStrokeOpacity}
              strokeWidth={Math.max(1.5, Math.min(3, bodyStrokeWidth))}
              pointerEvents="none"
            />
          ) : null}
          {visualAsset ? (
            <>
              {assetTransform.clipToBounds ? (
                <svg
                  x={object.rect.x}
                  y={object.rect.y}
                  width={object.rect.width}
                  height={object.rect.height}
                  viewBox={`0 0 ${object.rect.width} ${object.rect.height}`}
                  overflow="hidden"
                  pointerEvents="none"
                >
                  {assetClipPath ? <defs>{assetClipPath}</defs> : null}
                  <g
                    clipPath={assetClipPath ? `url(#${assetClipPathId})` : undefined}
                    transform={`translate(${object.rect.width / 2 + assetTransform.offsetX} ${object.rect.height / 2 + assetTransform.offsetY}) rotate(${assetTransform.rotation}) scale(${assetTransform.scaleX} ${assetTransform.scaleY})`}
                  >
                    <image
                      href={visualAsset.assetUrl}
                      x={(visualAssetBox?.x ?? 0) - object.rect.width / 2}
                      y={(visualAssetBox?.y ?? 0) - object.rect.height / 2}
                      width={visualAssetBox?.width ?? object.rect.width}
                      height={visualAssetBox?.height ?? object.rect.height}
                      preserveAspectRatio="none"
                      opacity="0.98"
                    />
                  </g>
                </svg>
              ) : (
                <g
                  pointerEvents="none"
                  transform={`translate(${cx + assetTransform.offsetX} ${cy + assetTransform.offsetY}) rotate(${assetTransform.rotation}) scale(${assetTransform.scaleX} ${assetTransform.scaleY})`}
                >
                  <image
                    href={visualAsset.assetUrl}
                    x={(visualAssetBox?.x ?? 0) - object.rect.width / 2}
                    y={(visualAssetBox?.y ?? 0) - object.rect.height / 2}
                    width={visualAssetBox?.width ?? object.rect.width}
                    height={visualAssetBox?.height ?? object.rect.height}
                    preserveAspectRatio="none"
                    opacity="0.98"
                  />
                </g>
              )}
            </>
          ) : null}
          {!visualAsset ? (
            <TextureLayersSvg
              idPrefix={`custom_${object.id}`}
              layers={object.textures ?? []}
              rect={object.rect}
              clip={customObjectLocalClipShape(object)}
            />
          ) : null}
        </g>
      </g>
      <EditorEntityLabel x={cx} y={object.rect.y + object.rect.height + 18} label={object.label} />
    </g>
  );
}

function customObjectShapeSvgPoints(object: MysteryRoomCustomObjectDefinition): MysteryRoomVec2[] {
  const center = rectCenter(object.rect);
  return getCustomObjectShapePoints(object).map((point) => ({
    x: center.x + point.x * object.rect.width,
    y: center.y + point.y * object.rect.height,
  }));
}

function customObjectImageClipPathElement(object: MysteryRoomCustomObjectDefinition, id: string): ReactNode {
  const clipShape = customObjectLocalClipShape(object);
  if (!clipShape) return null;
  return (
    <clipPath id={id} clipPathUnits="userSpaceOnUse">
      {clipShape}
    </clipPath>
  );
}

function customObjectLocalClipShape(object: MysteryRoomCustomObjectDefinition): ReactNode {
  const rect = object.rect;
  if (getMysteryRoomEffectiveCustomObjectAsset(object)?.isDefaultItemAsset) {
    return <rect x={0} y={0} width={rect.width} height={rect.height} />;
  }
  const points = getCustomObjectShapePoints(object).map((point) => ({
    x: rect.width / 2 + point.x * rect.width,
    y: rect.height / 2 + point.y * rect.height,
  }));
  if (points.length === 0) {
    return null;
  }
  if (points.length === 1) {
    return <circle cx={points[0].x} cy={points[0].y} r={Math.max(8, Math.min(rect.width, rect.height) * 0.16)} />;
  }
  if (points.length === 2) {
    return (
      <line
        x1={points[0].x}
        y1={points[0].y}
        x2={points[1].x}
        y2={points[1].y}
        stroke="#fff"
        strokeWidth={Math.max(8, Math.min(rect.width, rect.height) * 0.18)}
        strokeLinecap="round"
      />
    );
  }
  return <polygon points={points.map((point) => `${point.x},${point.y}`).join(' ')} />;
}

function sanitizeSvgId(value: string): string {
  return value.replace(/[^a-zA-Z0-9_-]/g, '_');
}

function CustomObjectShapeBody({
  object,
  points,
  fill,
  stroke,
  strokeWidth,
  strokeOpacity,
  opacity,
}: {
  object: MysteryRoomCustomObjectDefinition;
  points: MysteryRoomVec2[];
  fill: string;
  stroke: string;
  strokeWidth: number;
  strokeOpacity: number;
  opacity: number;
}) {
  const cx = object.rect.x + object.rect.width / 2;
  const cy = object.rect.y + object.rect.height / 2;
  if (points.length === 1) {
    return (
      <circle
        cx={cx}
        cy={cy}
        r={Math.max(10, Math.min(object.rect.width, object.rect.height) * 0.16)}
        fill={fill}
        fillOpacity={opacity}
        stroke={stroke}
        strokeOpacity={strokeOpacity}
        strokeWidth={strokeWidth}
      />
    );
  }
  if (points.length === 2) {
    return (
      <line
        x1={points[0].x}
        y1={points[0].y}
        x2={points[1].x}
        y2={points[1].y}
        stroke={stroke}
        strokeOpacity={strokeOpacity}
        strokeWidth={Math.max(strokeWidth, Math.min(object.rect.width, object.rect.height) * 0.18)}
        strokeLinecap="round"
      />
    );
  }
  const path = points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' ');
  return (
    <>
      <path d={`${path} Z`} fill={fill} fillOpacity={opacity} stroke={stroke} strokeOpacity={strokeOpacity} strokeWidth={strokeWidth} strokeLinejoin="round" />
      {points.map((point, index) => (
        <circle key={`${object.id}:point:${index}`} cx={point.x} cy={point.y} r={Math.max(4, strokeWidth * 0.82)} fill="#020617" fillOpacity="0.55" pointerEvents="none" />
      ))}
    </>
  );
}

function DoorGlyph({
  door,
  selected,
  onPointerDown,
}: {
  door: MysteryRoomDoorDefinition;
  selected: boolean;
  onPointerDown: (event: PointerEvent<SVGGElement>) => void;
}) {
  const cx = door.panel.x + door.panel.width / 2;
  const cy = door.panel.y + door.panel.height / 2;
  const horizontal = door.orientation === 'horizontal';
  const color = numberToHexColor(door.color ?? 0x0f172a);
  const panelStroke = numberToHexColor(door.outlineColor, '#f8fafc');
  const panelStrokeOpacity = outlineOpacity(door, 0.62);
  const panelStrokeWidth = outlineWidth(door, 4);
  const leafGap = 4;
  const leafA = horizontal
    ? { x: door.panel.x, y: door.panel.y, width: Math.max(4, door.panel.width / 2 - leafGap / 2), height: door.panel.height }
    : { x: door.panel.x, y: door.panel.y, width: door.panel.width, height: Math.max(4, door.panel.height / 2 - leafGap / 2) };
  const leafB = horizontal
    ? { x: cx + leafGap / 2, y: door.panel.y, width: Math.max(4, door.panel.width / 2 - leafGap / 2), height: door.panel.height }
    : { x: door.panel.x, y: cy + leafGap / 2, width: door.panel.width, height: Math.max(4, door.panel.height / 2 - leafGap / 2) };
  const accessMode = door.accessMode ?? (door.requiredAccess === 'public' ? 'public' : 'card_required');

  return (
    <g>
      {selected ? (
        <rect x={door.trigger.x} y={door.trigger.y} width={door.trigger.width} height={door.trigger.height} fill="#38bdf8" fillOpacity="0.08" stroke="#38bdf8" strokeOpacity="0.25" strokeDasharray="14 10" pointerEvents="none" />
      ) : null}
      <g onPointerDown={onPointerDown} className="cursor-move">
        {selected ? (
          <rect x={door.panel.x} y={door.panel.y} width={door.panel.width} height={door.panel.height} rx={8} fill="none" stroke="#fde68a" strokeOpacity="0.9" strokeWidth={Math.max(4, panelStrokeWidth + 3)} pointerEvents="none" />
        ) : null}
        <rect x={door.panel.x - 4} y={door.panel.y - 4} width={door.panel.width + 8} height={door.panel.height + 8} rx={8} fill="#020617" fillOpacity="0.5" stroke={panelStroke} strokeOpacity={panelStrokeOpacity} strokeWidth={Math.max(2, panelStrokeWidth)} />
        <TextureLayersSvg idPrefix={`door_${door.id}`} layers={door.textures ?? []} rect={door.panel} />
        <rect {...leafA} rx={6} fill={color} fillOpacity={baseColorOpacity(door, 1)} stroke={panelStroke} strokeOpacity={panelStrokeOpacity} strokeWidth={panelStrokeWidth} />
        <rect {...leafB} rx={6} fill={color} fillOpacity={baseColorOpacity(door, 1)} stroke={panelStroke} strokeOpacity={panelStrokeOpacity} strokeWidth={panelStrokeWidth} />
        <line x1={horizontal ? cx : door.panel.x + 6} y1={horizontal ? door.panel.y + 6 : cy} x2={horizontal ? cx : door.panel.x + door.panel.width - 6} y2={horizontal ? door.panel.y + door.panel.height - 6 : cy} stroke="#e2e8f0" strokeOpacity="0.72" strokeWidth="4" strokeLinecap="round" />
        <rect x={cx - 16} y={cy - 11} width="32" height="22" rx="6" fill="#020617" fillOpacity="0.55" stroke="#e2e8f0" strokeOpacity="0.55" strokeWidth="2" pointerEvents="none" />
      </g>
      <EditorEntityLabel x={cx} y={door.panel.y + door.panel.height + 18} label={door.label ?? door.id} fill="#bfdbfe" />
      {selected ? <EditorEntityLabel x={cx} y={door.panel.y - 18} label={doorAccessModeLabel(accessMode)} fill="#fde68a" /> : null}
    </g>
  );
}

function LightGlyph({
  light,
  selected,
  onPointerDown,
}: {
  light: MysteryRoomLightDefinition;
  selected: boolean;
  onPointerDown: (event: PointerEvent<SVGGElement>) => void;
}) {
  const flickerMode = light.flickerMode ?? (light.flicker ? 'flicker' : 'steady');
  const color = numberToHexColor(light.color, '#fef3c7');
  const glowColor = numberToHexColor(light.color ?? kelvinToRgbNumber(light.colorTemperatureK ?? 4200), '#fef3c7');
  const bulbStroke = numberToHexColor(light.outlineColor, '#fff7ed');
  const bulbStrokeOpacity = outlineOpacity(light, 0.72);
  const bulbStrokeWidth = outlineWidth(light, 4);
  const glowOpacity = Math.max(0.035, Math.min(0.22, light.intensity * 0.13));
  const pulseValues = flickerMode === 'steady'
    ? `${glowOpacity}`
    : `${Math.max(0.02, glowOpacity * (1 - (light.flickerIntensity ?? 0.26)))};${glowOpacity};${Math.max(0.02, glowOpacity * 0.55)};${glowOpacity}`;
  const pulseDuration = flickerMode === 'random_bulb'
    ? `${Math.max(0.12, (light.flickerMaxIntervalMs ?? 260) / 1000)}s`
    : `${Math.max(0.1, (light.flickerMinIntervalMs ?? 120) / 1000)}s`;

  return (
    <g>
      <circle cx={light.position.x} cy={light.position.y} r={light.radius} fill={glowColor} fillOpacity={glowOpacity} stroke={glowColor} strokeOpacity={selected ? 0.32 : 0.14} strokeWidth={selected ? 3 : 1.5} pointerEvents="none">
        {flickerMode !== 'steady' ? <animate attributeName="fill-opacity" values={pulseValues} dur={pulseDuration} repeatCount="indefinite" /> : null}
      </circle>
      <circle cx={light.position.x} cy={light.position.y} r={Math.max(24, light.radius * 0.42)} fill={glowColor} fillOpacity={glowOpacity * 1.35} pointerEvents="none" />
      <g transform={`translate(${light.position.x} ${light.position.y})`} onPointerDown={onPointerDown} className="cursor-move">
        {selected ? <circle r={34} fill="none" stroke="#fde68a" strokeOpacity="0.9" strokeWidth={Math.max(4, bulbStrokeWidth + 3)} pointerEvents="none" /> : null}
        <circle r={32} fill={color} fillOpacity={baseColorOpacity(light, 0.96)} stroke={bulbStroke} strokeOpacity={bulbStrokeOpacity} strokeWidth={bulbStrokeWidth} />
        <TextureLayersSvg idPrefix={`light_${light.id}`} layers={light.textures ?? []} rect={{ x: -28, y: -28, width: 56, height: 56 }} />
        <path d="M -12 4 C -20 -9 -10 -24 0 -24 C 10 -24 20 -9 12 4 C 8 11 7 16 7 20 L -7 20 C -7 16 -8 11 -12 4 Z" fill={color} stroke="#fff7ed" strokeWidth="3" strokeLinejoin="round" />
        <path d="M -7 6 C -3 -1 3 -1 7 6" fill="none" stroke="#92400e" strokeWidth="3" strokeLinecap="round" />
        <line x1="-9" y1="26" x2="9" y2="26" stroke="#e2e8f0" strokeWidth="4" strokeLinecap="round" />
      </g>
      <EditorEntityLabel x={light.position.x} y={light.position.y + 44} label={light.label ?? light.id} fill="#fde68a" />
    </g>
  );
}

function InteractableGlyph({
  entry,
  selected,
  switchEnabled,
  onPointerDown,
}: {
  entry: MysteryRoomInteractableDefinition;
  selected: boolean;
  switchEnabled?: boolean;
  onPointerDown: (event: PointerEvent<SVGGElement>) => void;
}) {
  const defaultAccent = entry.kind === 'light_switch' ? '#facc15' : entry.kind === 'pickup' ? '#38bdf8' : entry.kind === 'access_grant' ? '#34d399' : '#c4b5fd';
  const accent = numberToHexColor(entry.color, defaultAccent);
  const panelOpacity = baseColorOpacity(entry, 1);
  const panelStroke = numberToHexColor(entry.outlineColor, defaultAccent);
  const panelStrokeOpacity = outlineOpacity(entry, 0.7);
  const panelStrokeWidth = outlineWidth(entry, 4);

  return (
    <g>
      {selected ? (
        <circle cx={entry.anchor.x} cy={entry.anchor.y} r={entry.interactionRadius} fill={accent} fillOpacity="0.05" stroke={accent} strokeOpacity="0.26" strokeWidth="3" strokeDasharray="16 10" pointerEvents="none" />
      ) : null}
      <g transform={`translate(${entry.anchor.x} ${entry.anchor.y})`} onPointerDown={onPointerDown} className="cursor-move">
        {entry.kind === 'light_switch' ? (
          <>
            <LightSwitchButtonGlyph
              enabled={switchEnabled ?? false}
              selected={selected}
              outlineColor={panelStroke}
              outlineOpacity={panelStrokeOpacity}
              outlineWidth={panelStrokeWidth}
            />
            <TextureLayersSvg idPrefix={`interactable_${entry.id}`} layers={entry.textures ?? []} rect={{ x: -29, y: -29, width: 58, height: 58 }} />
          </>
        ) : entry.kind === 'access_grant' ? (
          <>
            {selected ? <rect x="-26" y="-32" width="52" height="64" rx="13" fill="none" stroke="#fde68a" strokeOpacity="0.9" strokeWidth={Math.max(4, panelStrokeWidth + 3)} pointerEvents="none" /> : null}
            <rect x="-24" y="-30" width="48" height="60" rx="12" fill={accent} fillOpacity={panelOpacity} stroke={panelStroke} strokeOpacity={panelStrokeOpacity} strokeWidth={panelStrokeWidth} />
            <TextureLayersSvg idPrefix={`interactable_${entry.id}`} layers={entry.textures ?? []} rect={{ x: -24, y: -30, width: 48, height: 60 }} />
            <rect x="-12" y="-15" width="24" height="30" rx="5" fill="#f8fafc" />
            <line x1="-7" y1="3" x2="7" y2="3" stroke="#0f172a" strokeWidth="4" />
          </>
        ) : entry.kind === 'pickup' ? (
          <>
            {selected ? <rect x="-26" y="-32" width="52" height="64" rx="13" fill="none" stroke="#fde68a" strokeOpacity="0.9" strokeWidth={Math.max(4, panelStrokeWidth + 3)} pointerEvents="none" /> : null}
            <rect x="-24" y="-30" width="48" height="60" rx="12" fill={accent} fillOpacity={panelOpacity} stroke={panelStroke} strokeOpacity={panelStrokeOpacity} strokeWidth={panelStrokeWidth} />
            <TextureLayersSvg idPrefix={`interactable_${entry.id}`} layers={entry.textures ?? []} rect={{ x: -24, y: -30, width: 48, height: 60 }} />
            <path d="M -10 8 L 0 -16 L 10 8 Z" fill={accent} stroke="#f8fafc" strokeWidth="3" strokeLinejoin="round" />
          </>
        ) : (
          <>
            {selected ? <rect x="-26" y="-32" width="52" height="64" rx="13" fill="none" stroke="#fde68a" strokeOpacity="0.9" strokeWidth={Math.max(4, panelStrokeWidth + 3)} pointerEvents="none" /> : null}
            <rect x="-24" y="-30" width="48" height="60" rx="12" fill={accent} fillOpacity={panelOpacity} stroke={panelStroke} strokeOpacity={panelStrokeOpacity} strokeWidth={panelStrokeWidth} />
            <TextureLayersSvg idPrefix={`interactable_${entry.id}`} layers={entry.textures ?? []} rect={{ x: -24, y: -30, width: 48, height: 60 }} />
            <circle r="13" fill={accent} stroke="#f8fafc" strokeWidth="3" />
          </>
        )}
      </g>
      <EditorEntityLabel x={entry.anchor.x} y={entry.anchor.y + 42} label={entry.label} fill="#e0f2fe" />
    </g>
  );
}

function LightSwitchButtonGlyph({
  enabled,
  selected,
  outlineColor,
  outlineOpacity,
  outlineWidth,
}: {
  enabled: boolean;
  selected: boolean;
  outlineColor: string;
  outlineOpacity: number;
  outlineWidth: number;
}) {
  const statusFill = enabled ? '#22c55e' : '#ef4444';
  const statusStroke = enabled ? '#86efac' : '#fca5a5';

  return (
    <>
      {selected ? <rect x="-31" y="-31" width="62" height="62" rx="9" fill="none" stroke="#fde68a" strokeOpacity="0.9" strokeWidth={Math.max(4, outlineWidth + 3)} pointerEvents="none" /> : null}
      <rect x="-29" y="-29" width="58" height="58" rx="8" fill="#020617" stroke={outlineColor} strokeOpacity={outlineOpacity} strokeWidth={outlineWidth} />
      <rect x="-20" y="-20" width="40" height="40" rx="7" fill="#111827" stroke="#475569" strokeWidth="3" />
      <rect x="-13" y="-13" width="26" height="26" rx="6" fill={statusFill} stroke={statusStroke} strokeWidth="3" />
      <circle cx="7" cy="-7" r="3" fill="#f8fafc" fillOpacity="0.82" pointerEvents="none" />
    </>
  );
}

function isEditorLightSwitchEnabled(map: MysteryRoomMapDefinition, entry: MysteryRoomInteractableDefinition) {
  if (entry.kind !== 'light_switch') {
    return false;
  }

  const targetLightIds = new Set(getEditorLightSwitchTargetIds(map, entry));
  const targetLights = map.lights.filter((light) => targetLightIds.has(light.id));

  return entry.defaultEnabled ?? (targetLights.length > 0 && targetLights.some((light) => light.enabled ?? true));
}

function getEditorLightSwitchTargetIds(map: MysteryRoomMapDefinition, entry: MysteryRoomInteractableDefinition) {
  if (entry.kind !== 'light_switch') {
    return [];
  }

  if (entry.linkedLightIds) {
    return entry.linkedLightIds;
  }

  return map.lights
    .filter((light) => {
      if (light.switchId === entry.id) {
        return true;
      }
      return light.switchIds?.includes(entry.id) ?? false;
    })
    .map((light) => light.id);
}

function TriggerGlyph({
  trigger,
  selected,
  onPointerDown,
}: {
  trigger: MysteryRoomTriggerDefinition;
  selected: boolean;
  onPointerDown: (event: PointerEvent<SVGGElement>) => void;
}) {
  const cx = trigger.rect.x + trigger.rect.width / 2;
  const cy = trigger.rect.y + trigger.rect.height / 2;
  const color = trigger.color ?? 0xa78bfa;
  const fill = numberToHexColor(color);
  const bodyOpacity = baseColorOpacity(trigger, 0.12);
  const triggerStroke = numberToHexColor(trigger.outlineColor, '#c4b5fd');
  const triggerStrokeOpacity = outlineOpacity(trigger, 0.72);
  const triggerStrokeWidth = outlineWidth(trigger, 5);

  return (
    <g>
      {selected ? (
        <rect x={trigger.rect.x} y={trigger.rect.y} width={trigger.rect.width} height={trigger.rect.height} rx={12} fill="none" stroke="#fde68a" strokeOpacity="0.9" strokeWidth={Math.max(4, triggerStrokeWidth + 3)} strokeDasharray="18 10" pointerEvents="none" />
      ) : null}
      <rect x={trigger.rect.x} y={trigger.rect.y} width={trigger.rect.width} height={trigger.rect.height} rx={12} fill={fill} fillOpacity={bodyOpacity} stroke={triggerStroke} strokeOpacity={triggerStrokeOpacity} strokeWidth={triggerStrokeWidth} strokeDasharray="18 10" pointerEvents="none" />
      <TextureLayersSvg idPrefix={`trigger_${trigger.id}`} layers={trigger.textures ?? []} rect={trigger.rect} />
      <g transform={`translate(${cx} ${cy})`} onPointerDown={onPointerDown} className="cursor-move">
        <path d="M 0 -30 L 30 0 L 0 30 L -30 0 Z" fill={fill} fillOpacity={Math.max(bodyOpacity, selected ? 0.2 : 0.08)} stroke={triggerStroke} strokeOpacity={triggerStrokeOpacity} strokeWidth={Math.max(2, triggerStrokeWidth - 1)} />
        <path d="M -8 -6 H 2 L -3 8 H 9" fill="none" stroke="#c4b5fd" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
      </g>
      <EditorEntityLabel x={cx} y={trigger.rect.y + trigger.rect.height + 18} label={trigger.label} fill="#ddd6fe" />
    </g>
  );
}

function SoundSourceGlyph({
  source,
  selected,
  onPointerDown,
}: {
  source: MysteryRoomSoundSourceDefinition;
  selected: boolean;
  onPointerDown: (event: PointerEvent<SVGGElement>) => void;
}) {
  return (
    <g>
      {selected ? (
        <circle cx={source.position.x} cy={source.position.y} r={source.radius} fill="#22d3ee" fillOpacity="0.05" stroke="#22d3ee" strokeOpacity="0.28" strokeWidth="3" strokeDasharray="18 10" pointerEvents="none" />
      ) : null}
      <g transform={`translate(${source.position.x} ${source.position.y})`} onPointerDown={onPointerDown} className="cursor-move">
        <circle r={31} fill="#082f49" fillOpacity="0.96" stroke={selected ? '#fde68a' : '#e0f2fe'} strokeWidth={selected ? 7 : 4} />
        <path d="M -22 -11 H -11 L 3 -24 V 24 L -11 11 H -22 Z" fill="#22d3ee" stroke="#e0f2fe" strokeWidth="3" strokeLinejoin="round" />
        <path d="M 12 -13 Q 24 0 12 13" fill="none" stroke="#e0f2fe" strokeWidth="4" strokeLinecap="round" />
        <path d="M 20 -23 Q 40 0 20 23" fill="none" stroke="#67e8f9" strokeWidth="4" strokeLinecap="round" opacity="0.85" />
      </g>
      <EditorEntityLabel x={source.position.x} y={source.position.y + 44} label={source.label} fill="#a5f3fc" />
    </g>
  );
}

function HazardGlyph({
  hazard,
  selected,
  onPointerDown,
}: {
  hazard: MysteryRoomHazardDefinition;
  selected: boolean;
  onPointerDown: (event: PointerEvent<SVGGElement>) => void;
}) {
  const accent = hazard.kind === 'smoke' ? '#cbd5f5' : '#fbbf24';
  const coreFill = hazard.kind === 'smoke' ? '#475569' : '#78350f';
  return (
    <g>
      {selected ? (
        <circle
          cx={hazard.position.x}
          cy={hazard.position.y}
          r={hazard.radius}
          fill={accent}
          fillOpacity="0.05"
          stroke={accent}
          strokeOpacity="0.24"
          strokeWidth="3"
          strokeDasharray="16 10"
          pointerEvents="none"
        />
      ) : null}
      <g transform={`translate(${hazard.position.x} ${hazard.position.y})`} onPointerDown={onPointerDown} className="cursor-move">
        <circle r={31} fill={coreFill} fillOpacity="0.94" stroke={selected ? '#fde68a' : accent} strokeWidth={selected ? 7 : 4} />
        <TextureLayersSvg idPrefix={`hazard_${hazard.id}`} layers={hazard.textures ?? []} rect={{ x: -28, y: -28, width: 56, height: 56 }} />
        {hazard.kind === 'smoke' ? (
          <>
            <circle cx="-10" cy="2" r="9" fill="#cbd5e1" opacity="0.74" />
            <circle cx="2" cy="-6" r="12" fill="#e2e8f0" opacity="0.78" />
            <circle cx="13" cy="4" r="8" fill="#94a3b8" opacity="0.7" />
            <path d="M -16 15 C -8 8 7 8 16 15" fill="none" stroke="#f8fafc" strokeWidth="3" strokeLinecap="round" opacity="0.66" />
          </>
        ) : (
          <>
            <path d="M -18 8 L -4 -5 L 4 5 L 18 -12" fill="none" stroke="#fde68a" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M -8 20 L 2 8 L 9 18" fill="none" stroke="#f97316" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
          </>
        )}
      </g>
      <EditorEntityLabel
        x={hazard.position.x}
        y={hazard.position.y + 44}
        label={`${hazard.id} · ${hazardKindLabel(hazard.kind)}`}
        fill={hazard.kind === 'smoke' ? '#cbd5e1' : '#fde68a'}
      />
    </g>
  );
}

function IncidentAnchorGlyph({
  anchor,
  selected,
  onPointerDown,
}: {
  anchor: MysteryRoomIncidentAnchorDefinition;
  selected: boolean;
  onPointerDown: (event: PointerEvent<SVGGElement>) => void;
}) {
  const color = numberToHexColor(incidentAnchorColor(anchor.kind));
  return (
    <g>
      <g transform={`translate(${anchor.position.x} ${anchor.position.y})`} onPointerDown={onPointerDown} className="cursor-move">
        <path
          d="M 0 -31 L 28 0 L 0 31 L -28 0 Z"
          fill="#0f172a"
          stroke={selected ? '#fde68a' : color}
          strokeWidth={selected ? 7 : 4}
          strokeLinejoin="round"
        />
        <circle r="13" fill={color} fillOpacity="0.92" />
        <path d="M 0 -10 V 5" stroke="#fff7ed" strokeWidth="5" strokeLinecap="round" />
        <circle cx="0" cy="14" r="3.5" fill="#fff7ed" />
      </g>
      <EditorEntityLabel x={anchor.position.x} y={anchor.position.y + 44} label={`${anchor.id} · ${incidentAnchorKindLabel(anchor.kind)}`} fill={color} />
    </g>
  );
}

function WorkAnchorGlyph({
  anchor,
  selected,
  onPointerDown,
}: {
  anchor: MysteryRoomMapDefinition['workAnchors'][number];
  selected: boolean;
  onPointerDown: (event: PointerEvent<SVGGElement>) => void;
}) {
  const color = numberToHexColor(workAnchorColor(anchor.kind));
  const areaRadius = Math.max(8, anchor.taskAreaRadius ?? 84);
  return (
    <g>
      {anchor.taskAreaShape === 'square' ? (
        <rect
          x={anchor.position.x - areaRadius}
          y={anchor.position.y - areaRadius}
          width={areaRadius * 2}
          height={areaRadius * 2}
          fill={color}
          fillOpacity="0.08"
          stroke={selected ? '#fde68a' : color}
          strokeOpacity="0.46"
          strokeWidth={selected ? 5 : 3}
          strokeDasharray="14 10"
        />
      ) : (
        <circle
          cx={anchor.position.x}
          cy={anchor.position.y}
          r={areaRadius}
          fill={color}
          fillOpacity="0.08"
          stroke={selected ? '#fde68a' : color}
          strokeOpacity="0.46"
          strokeWidth={selected ? 5 : 3}
          strokeDasharray="14 10"
        />
      )}
      <g transform={`translate(${anchor.position.x} ${anchor.position.y})`} onPointerDown={onPointerDown} className="cursor-move">
        <circle r={30} fill="#0f172a" stroke={selected ? '#fde68a' : color} strokeWidth={selected ? 7 : 4} />
        <circle r={17} fill={color} fillOpacity="0.18" stroke={color} strokeWidth="3" strokeDasharray="6 6" />
        <path d="M -12 0 H 12 M 0 -12 V 12" stroke={color} strokeWidth="4" strokeLinecap="round" />
        <circle r="5" fill={color} />
      </g>
      <EditorEntityLabel x={anchor.position.x} y={anchor.position.y + 44} label={`${anchor.id} · ${workAnchorKindLabel(anchor.kind)}`} fill={color} />
    </g>
  );
}

function NavNodeGlyph({
  node,
  selected,
  onPointerDown,
}: {
  node: MysteryRoomNavNodeDefinition;
  selected: boolean;
  onPointerDown: (event: PointerEvent<SVGGElement>) => void;
}) {
  return (
    <g>
      <g onPointerDown={onPointerDown} className="cursor-move">
        <circle cx={node.position.x} cy={node.position.y} r={18} fill="#064e3b" stroke={selected ? '#fde68a' : '#d1fae5'} strokeWidth={selected ? 7 : 4} />
        <circle cx={node.position.x} cy={node.position.y} r={6} fill="#34d399" />
      </g>
      <EditorEntityLabel x={node.position.x} y={node.position.y + 28} label={node.id} fill="#bbf7d0" />
    </g>
  );
}

/** Фильтр отображения и попадания в выбор только для точек спавна (слой actors). */
type MysteryRoomEditorSpawnLayerFilter = {
  sideA: boolean;
  sideB: boolean;
  players: boolean;
  bots: boolean;
};

function editorSpawnSlotMatchesLayerFilter(
  slot: MysteryRoomMapDefinition['spawnSlots'][number],
  filter: MysteryRoomEditorSpawnLayerFilter,
): boolean {
  const side = slot.side === 'B' ? 'B' : 'A';
  const actor = slot.actor === 'bot' ? 'bot' : 'player';
  if (side === 'A' && !filter.sideA) return false;
  if (side === 'B' && !filter.sideB) return false;
  if (actor === 'player' && !filter.players) return false;
  if (actor === 'bot' && !filter.bots) return false;
  return true;
}

function PlayerSpawnGlyph({
  slot,
  index,
  selected,
  onPointerDown,
}: {
  slot: MysteryRoomMapDefinition['spawnSlots'][number];
  index: number;
  selected: boolean;
  onPointerDown: (event: PointerEvent<SVGGElement>) => void;
}) {
  const slotActor = slot.actor === 'bot' ? 'bot' : 'player';
  const slotSide = slot.side === 'B' ? 'B' : 'A';
  const ringColor = slotActor === 'bot' ? '#86efac' : '#fffbeb';
  const fillColor = slotActor === 'bot' ? '#22c55e' : '#f59e0b';
  const badge = `${slotSide}${slotActor === 'bot' ? 'B' : 'P'}`;
  return (
    <g>
      <g transform={`translate(${slot.x} ${slot.y})`} onPointerDown={onPointerDown} className="cursor-move">
        <circle r={31} fill={slotSide === 'A' ? '#78350f' : '#1f2937'} stroke={selected ? '#fde68a' : ringColor} strokeWidth={selected ? 7 : 5} />
        <circle r={18} fill={fillColor} fillOpacity="0.92" />
        <text
          textAnchor="middle"
          dominantBaseline="central"
          fill="#020617"
          fontSize="21"
          fontFamily="Inter, ui-sans-serif, system-ui"
          fontWeight="900"
          pointerEvents="none"
        >
          {badge}
        </text>
      </g>
      <EditorEntityLabel x={slot.x} y={slot.y + 42} label={slot.label ?? `Игрок ${index + 1}`} fill="#fed7aa" />
    </g>
  );
}

function ActorGlyph({
  npc,
  selected,
  onPointerDown,
}: {
  npc: MysteryRoomMapDefinition['npcs'][number];
  selected: boolean;
  onPointerDown: (event: PointerEvent<SVGGElement>) => void;
}) {
  const isBoss = npc.actorKind === 'boss';
  const radius = Math.max(20, (npc.radius ?? (isBoss ? 34 : 16)) * (npc.scale ?? 1));
  const color = numberToHexColor(npc.color, isBoss ? '#ef4444' : '#a5b4fc');
  const fillOpacity = npc.invisible ? 0.18 : 0.94;
  return (
    <g>
      {selected ? (
        <circle cx={npc.spawn.x} cy={npc.spawn.y} r={Math.max(radius + 14, 44)} fill={color} fillOpacity="0.08" stroke="#fde68a" strokeOpacity="0.45" strokeWidth="4" strokeDasharray="14 9" pointerEvents="none" />
      ) : null}
      <g transform={`translate(${npc.spawn.x} ${npc.spawn.y})`} onPointerDown={onPointerDown} className="cursor-move">
        {isBoss ? (
          <path
            d={`M 0 ${-radius} L ${radius * 0.85} ${-radius * 0.18} L ${radius * 0.52} ${radius * 0.92} L ${-radius * 0.52} ${radius * 0.92} L ${-radius * 0.85} ${-radius * 0.18} Z`}
            fill={color}
            fillOpacity={fillOpacity}
            stroke={selected ? '#fde68a' : '#fecaca'}
            strokeWidth={selected ? 7 : 4}
            strokeLinejoin="round"
          />
        ) : (
          <rect
            x={-radius * 0.62}
            y={-radius * 0.86}
            width={radius * 1.24}
            height={radius * 1.72}
            rx={Math.max(5, radius * 0.18)}
            fill={color}
            fillOpacity={fillOpacity}
            stroke={selected ? '#fde68a' : '#e2e8f0'}
            strokeWidth={selected ? 7 : 4}
          />
        )}
        <circle cx="0" cy={-radius * 0.22} r={Math.max(5, radius * 0.22)} fill="#f8fafc" fillOpacity={npc.invisible ? 0.35 : 0.82} />
        {npc.invisible ? <path d={`M ${-radius} ${radius} L ${radius} ${-radius}`} stroke="#e0f2fe" strokeWidth="5" strokeLinecap="round" opacity="0.8" /> : null}
      </g>
      <EditorEntityLabel x={npc.spawn.x} y={npc.spawn.y + radius + 18} label={`${npc.name} · ${isBoss ? 'Босс' : 'Бот'}`} fill={isBoss ? '#fecaca' : '#c4b5fd'} />
    </g>
  );
}

type EditorRenderItem = {
  key: string;
  layer: number;
  order: number;
  node: ReactNode;
};

type EditorLinkBadge = {
  key: string;
  selection: MysteryRoomEditorSelection;
  label: string;
};

function LinkBadgeOverlay({ map, badge }: { map: MysteryRoomMapDefinition; badge: EditorLinkBadge }) {
  const center = getSelectionGizmoCenter(map, badge.selection);
  const bounds = getSelectionGizmoBounds(map, badge.selection);
  if (!center || !bounds) return null;
  const label = compactSvgLabel(badge.label, 8);
  return (
    <g pointerEvents="none">
      <rect
        x={bounds.x - 14}
        y={bounds.y - 14}
        width={bounds.width + 28}
        height={bounds.height + 28}
        rx={18}
        fill="#22d3ee"
        fillOpacity="0.07"
        stroke="#67e8f9"
        strokeWidth="5"
        strokeDasharray="16 10"
      />
      <rect x={center.x + 24} y={center.y - 54} width="58" height="34" rx="10" fill="#0f172a" stroke="#67e8f9" strokeWidth="4" />
      <text
        x={center.x + 53}
        y={center.y - 36}
        textAnchor="middle"
        dominantBaseline="middle"
        fill="#e0f2fe"
        fontSize="18"
        fontFamily="Inter, ui-sans-serif, system-ui"
        fontWeight="900"
      >
        {label}
      </text>
    </g>
  );
}

function buildEditorLinkBadges(map: MysteryRoomMapDefinition, selection: MysteryRoomEditorSelection | null): EditorLinkBadge[] {
  if (!selection) return [];

  const labelsByTarget = new Map<string, string[]>();
  const counters = new Map<string, number>();
  const targetKey = (target: MysteryRoomEditorSelection) => `${target.type}:${target.id}`;
  const addBadge = (target: MysteryRoomEditorSelection, label: string) => {
    const key = targetKey(target);
    const labels = labelsByTarget.get(key) ?? [];
    if (!labels.includes(label)) labels.push(label);
    labelsByTarget.set(key, labels);
  };
  const addGroup = (prefix: string, targets: MysteryRoomEditorSelection[]) => {
    const uniqueTargets = Array.from(new Map(targets.map((target) => [targetKey(target), target])).values());
    if (uniqueTargets.length < 2) return;
    const nextIndex = (counters.get(prefix) ?? 0) + 1;
    counters.set(prefix, nextIndex);
    const label = `${prefix}${nextIndex}`;
    uniqueTargets.forEach((target) => addBadge(target, label));
  };
  const self = selection;
  const switchLightTargets = (switchId: string) => {
    const switchEntry = map.interactables.find((entry) => entry.id === switchId);
    const linkedLightIds = new Set(switchEntry?.linkedLightIds ?? []);
    map.lights.forEach((light) => {
      if (light.switchId === switchId || light.switchIds?.includes(switchId)) {
        linkedLightIds.add(light.id);
      }
    });
    return Array.from(linkedLightIds).map((id) => ({ type: 'light' as const, id }));
  };
  const addActionGroup = (prefix: string, source: MysteryRoomEditorSelection, action: MysteryRoomTriggerAction) => {
    if (action.kind === 'toggle_light' || action.kind === 'set_light' || action.kind === 'set_light_flicker') {
      addGroup(prefix, [source, ...action.lightIds.map((id) => ({ type: 'light' as const, id }))]);
    } else if (action.kind === 'play_sound') {
      addGroup(prefix, [source, ...action.soundSourceIds.map((id) => ({ type: 'soundSource' as const, id }))]);
    } else if (action.kind === 'open_door' || action.kind === 'close_door') {
      addGroup(prefix, [source, ...action.doorIds.map((id) => ({ type: 'door' as const, id }))]);
    } else if (action.kind === 'enable_equipment') {
      addGroup(prefix, [source, ...action.objectIds.map((id) => ({ type: 'customObject' as const, id }))]);
    } else if (action.kind === 'spawn_smoke' && action.hazardId) {
      addGroup(prefix, [source, { type: 'hazard' as const, id: action.hazardId }]);
    }
  };
  const addDoorCardGroupsForCard = (cardSelection: MysteryRoomEditorSelection, cardId: string) => {
    map.doors
      .filter((door) => (door.accessCardObjectIds ?? []).includes(cardId))
      .forEach((door) => addGroup('K', [{ type: 'door' as const, id: door.id }, cardSelection, ...doorAccessCardSelections(map, door)]));
  };

  if (selection.type === 'interactable') {
    const entry = map.interactables.find((item) => item.id === selection.id);
    if (entry?.kind === 'access_grant' || entry?.kind === 'pickup') {
      addDoorCardGroupsForCard(self, entry.itemId ?? entry.id);
    }
    if (entry?.kind === 'light_switch') {
      addGroup('L', [self, ...switchLightTargets(entry.id)]);
    }
    if (entry?.linkedObjectIds?.length) {
      addGroup('O', [self, ...entry.linkedObjectIds.map((id) => ({ type: 'customObject' as const, id }))]);
    }
    if (entry?.linkedDoorIds?.length) {
      addGroup('D', [self, ...entry.linkedDoorIds.map((id) => ({ type: 'door' as const, id }))]);
    }
    (entry?.onActivateActions ?? []).forEach((action) => addActionGroup('A', self, action));
    (entry?.onPickupActions ?? []).forEach((action) => addActionGroup('P', self, action));
  } else if (selection.type === 'light') {
    const light = map.lights.find((entry) => entry.id === selection.id);
    const switchIds = light ? light.switchIds ?? (light.switchId ? [light.switchId] : []) : [];
    switchIds.forEach((id) => addGroup('L', [self, { type: 'interactable', id }, ...switchLightTargets(id)]));
    map.interactables
      .filter((entry) => entry.linkedLightIds?.includes(selection.id))
      .forEach((entry) => addGroup('L', [self, { type: 'interactable', id: entry.id }, ...switchLightTargets(entry.id)]));
    map.triggers.forEach((trigger) => {
      trigger.actions.forEach((action) => {
        if (
          (action.kind === 'toggle_light' || action.kind === 'set_light' || action.kind === 'set_light_flicker')
          && action.lightIds.includes(selection.id)
        ) {
          addActionGroup('T', { type: 'trigger', id: trigger.id }, action);
        }
      });
    });
  } else if (selection.type === 'trigger') {
    const trigger = map.triggers.find((entry) => entry.id === selection.id);
    trigger?.actions.forEach((action) => addActionGroup('T', self, action));
  } else if (selection.type === 'hazard') {
    map.triggers.forEach((trigger) => {
      trigger.actions.forEach((action) => {
        if (action.kind === 'spawn_smoke' && action.hazardId === selection.id) {
          addActionGroup('T', { type: 'trigger', id: trigger.id }, action);
        }
      });
    });
  } else if (selection.type === 'customObject') {
    const object = map.customObjects.find((entry) => entry.id === selection.id);
    if (object?.kind === 'badge') {
      addDoorCardGroupsForCard(self, object.id);
    }
    if (object?.linkedObjectIds?.length) {
      addGroup('O', [self, ...object.linkedObjectIds.map((id) => ({ type: 'customObject' as const, id }))]);
    }
    if (object?.linkedLightIds?.length) {
      addGroup('L', [self, ...object.linkedLightIds.map((id) => ({ type: 'light' as const, id }))]);
    }
    if (object?.linkedDoorIds?.length) {
      addGroup('D', [self, ...object.linkedDoorIds.map((id) => ({ type: 'door' as const, id }))]);
    }
    (object?.onActivateActions ?? []).forEach((action) => addActionGroup('A', self, action));
    (object?.onPickupActions ?? []).forEach((action) => addActionGroup('P', self, action));
    map.triggers.forEach((trigger) => {
      trigger.actions.forEach((action) => {
        if (action.kind === 'enable_equipment' && action.objectIds.includes(selection.id)) {
          addActionGroup('T', { type: 'trigger', id: trigger.id }, action);
        }
      });
    });
  } else if (selection.type === 'door') {
    const door = map.doors.find((entry) => entry.id === selection.id);
    if (door) {
      addGroup('K', [self, ...doorAccessCardSelections(map, door)]);
    }
    map.triggers.forEach((trigger) => {
      trigger.actions.forEach((action) => {
        if ((action.kind === 'open_door' || action.kind === 'close_door') && action.doorIds.includes(selection.id)) {
          addActionGroup('T', { type: 'trigger', id: trigger.id }, action);
        }
      });
    });
    map.interactables
      .filter((entry) => entry.linkedDoorIds?.includes(selection.id))
      .forEach((entry) => addGroup('D', [self, { type: 'interactable', id: entry.id }]));
  } else if (selection.type === 'soundSource') {
    const source = map.soundSources.find((entry) => entry.id === selection.id);
    (source?.triggerIds ?? []).forEach((id) => addGroup('S', [self, { type: 'trigger', id }]));
    map.triggers.forEach((trigger) => {
      trigger.actions.forEach((action) => {
        if (action.kind === 'play_sound' && action.soundSourceIds.includes(selection.id)) {
          addActionGroup('T', { type: 'trigger', id: trigger.id }, action);
        }
      });
    });
  }

  return Array.from(labelsByTarget.entries()).map(([key, labels]) => {
    const [type, id] = key.split(':') as [MysteryRoomEditorSelection['type'], string];
    return { key, selection: { type, id }, label: labels.join('/') };
  });
}

export default function MysteryRoomMapWorkshop({ onPlayMap, initialMapId }: Props) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const lastSavedRevisionRef = useRef(0);
  const suppressPaletteClickRef = useRef(false);
  const autoOpenedMapIdRef = useRef<string | null>(null);
  const pendingAssetPreviewUrlsRef = useRef<Set<string>>(new Set());
  const lastMapPointerRef = useRef<MysteryRoomVec2 | null>(null);
  const suppressCanvasContextMenuRef = useRef(false);
  const [wheelElement, setWheelElement] = useState<SVGSVGElement | null>(null);
  const [isAdminEditor, setIsAdminEditor] = useState(false);
  const [authRequired, setAuthRequired] = useState(false);
  const [maps, setMaps] = useState<MrMapSummary[]>([]);
  const [activeMap, setActiveMap] = useState<MrMapFull | null>(null);
  const [draftMap, setDraftMap] = useState<MysteryRoomMapDefinition>(() => createBlankMysteryRoomEditorMap());
  const [objectSkins, setObjectSkins] = useState<MrObjectSkin[]>([]);
  const [title, setTitle] = useState('Новая лаборатория');
  const [description, setDescription] = useState('');
  const [selected, setSelected] = useState<MysteryRoomEditorSelection | null>(null);
  const [multiSelected, setMultiSelected] = useState<MysteryRoomEditorSelection[]>([]);
  const [clipboardSelections, setClipboardSelections] = useState<MysteryRoomEditorSelection[]>([]);
  const [tool, setTool] = useState<MysteryRoomEditorTool>('select');
  const [status, setStatus] = useState('Готово');
  const [publishNotice, setPublishNotice] = useState<PublishNotice | null>(null);
  const [query, setQuery] = useState('');
  const [toolQuery, setToolQuery] = useState('');
  const [resourceGenerationSeed, setResourceGenerationSeed] = useState('resources-01');
  const [resourceGenerationCounts, setResourceGenerationCounts] = useState({ bush: 8, scrap: 5, rock: 6 });
  const [dirtyRevision, setDirtyRevision] = useState(0);
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const [undoHistory, setUndoHistory] = useState<MysteryRoomEditorHistorySnapshot[]>([]);
  const [redoHistory, setRedoHistory] = useState<MysteryRoomEditorHistorySnapshot[]>([]);
  const [dragState, setDragState] = useState<DragState | null>(null);
  const [wallBuildDraft, setWallBuildDraft] = useState<WallBuildDraft | null>(null);
  const [wallPreviewPoint, setWallPreviewPoint] = useState<MysteryRoomVec2 | null>(null);
  const [paletteDrag, setPaletteDrag] = useState<PaletteDragState | null>(null);
  const [contextMenu, setContextMenu] = useState<EditorContextMenuState | null>(null);
  const [assetEditObjectId, setAssetEditObjectId] = useState<string | null>(null);
  const [assetViewMode, setAssetViewMode] = useState<AssetViewMode>('grid');
  const [pendingAssetUploads, setPendingAssetUploads] = useState<PendingMapAssetUpload[]>([]);
  const [viewBox, setViewBox] = useState<ViewBox>(() => fitViewBoxToEditorContent(createBlankMysteryRoomEditorMap()));
  const [svgViewportSize, setSvgViewportSize] = useState<SvgViewportSize>(() => ({ width: 1, height: 1 }));
  const [showLayers, setShowLayers] = useState({
    rooms: true,
    walls: true,
    doors: true,
    actors: true,
    objects: true,
    collisions: true,
    lights: true,
    triggers: true,
    effects: true,
    sounds: true,
    anchors: true,
    nav: true,
  });
  const [spawnLayerFilter, setSpawnLayerFilter] = useState<MysteryRoomEditorSpawnLayerFilter>({
    sideA: true,
    sideB: true,
    players: true,
    bots: true,
  });

  const validationIssues = useMemo(() => validateEditableMap(draftMap), [draftMap]);
  const activeSelections = useMemo(
    () => (selected ? uniqueEditorSelections([selected, ...multiSelected]) : []),
    [multiSelected, selected],
  );
  const selectedCount = activeSelections.length;
  const wallNodes = useMemo(() => {
    const nodes = new Map<string, MysteryRoomVec2>();
    draftMap.walls.forEach((wall) => {
      const start = { x: Math.round(wall.start.x), y: Math.round(wall.start.y) };
      const end = { x: Math.round(wall.end.x), y: Math.round(wall.end.y) };
      nodes.set(wallEditorNodeKey(start), start);
      nodes.set(wallEditorNodeKey(end), end);
    });
    return Array.from(nodes.values());
  }, [draftMap.walls]);
  const wallJointNodes = useMemo(() => {
    const nodes = new Map<string, { node: MysteryRoomVec2; walls: MysteryRoomMapDefinition['walls']; selected: boolean }>();
    draftMap.walls.forEach((wall, wallIndex) => {
      [wall.start, wall.end].forEach((point) => {
        const node = { x: Math.round(point.x), y: Math.round(point.y) };
        const key = wallEditorNodeKey(node);
        const entry = nodes.get(key) ?? { node, walls: [], selected: false };
        entry.walls.push(wall);
        entry.selected = entry.selected || isSelected(selected, 'wall', String(wallIndex), multiSelected);
        nodes.set(key, entry);
      });
    });
    return Array.from(nodes.values()).filter((entry) => entry.walls.length > 1);
  }, [draftMap.walls, multiSelected, selected]);
  const clipboardCount = clipboardSelections.length;
  const templates = useMemo(() => getMysteryRoomEditorTemplates(), []);
  const draftMaps = useMemo(() => maps.filter((map) => map.status === 'draft'), [maps]);
  const publishedOwnMaps = useMemo(() => maps.filter((map) => map.status !== 'draft'), [maps]);
  const canEditActiveMap = Boolean(activeMap && (activeMap.status === 'draft' || isAdminEditor));
  const filteredDraftMaps = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return draftMaps;
    return draftMaps.filter((map) => `${map.title} ${map.description} ${map.status}`.toLowerCase().includes(needle));
  }, [draftMaps, query]);
  const filteredPublishedMaps = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return publishedOwnMaps;
    return publishedOwnMaps.filter((map) => `${map.title} ${map.description} ${map.status}`.toLowerCase().includes(needle));
  }, [publishedOwnMaps, query]);
  const filteredTemplates = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return templates;
    return templates.filter((template) => `${template.title} ${template.description} ${template.badge}`.toLowerCase().includes(needle));
  }, [query, templates]);
  const filteredToolGroups = useMemo(() => {
    const needle = toolQuery.trim().toLowerCase();
    return (Object.keys(TOOL_GROUP_LABELS) as ToolDefinition['group'][]).map((group) => ({
      group,
      tools: TOOL_DEFINITIONS.filter((entry) => {
        if (entry.group !== group) return false;
        if (!needle) return true;
        return `${entry.label} ${entry.title} ${entry.shortcut} ${entry.keywords}`.toLowerCase().includes(needle);
      }),
    })).filter((entry) => entry.tools.length > 0);
  }, [toolQuery]);
  const gridBounds = useMemo(() => editorGridBoundsForViewBox(viewBox), [viewBox]);
  const viewScale = useMemo(() => Math.max(1, Math.round((4096 / Math.max(1, viewBox.width)) * 100)), [viewBox.width]);
  const saveLabel = useMemo(() => {
    if (!activeMap) return 'Выберите источник карты';
    if (activeMap.status !== 'draft' && !isAdminEditor) return 'Только чтение';
    if (saveState === 'saving') return 'Сохранение...';
    if (saveState === 'dirty') return 'Есть несохраненные изменения';
    if (saveState === 'saved') return lastSavedAt ? `Сохранено ${lastSavedAt}` : 'Сохранено';
    if (saveState === 'error') return 'Ошибка сохранения';
    return 'Автосохранение готово';
  }, [activeMap, isAdminEditor, lastSavedAt, saveState]);
  const canvasCursorClass = dragState?.kind === 'pan' || dragState?.kind === 'transform'
    ? 'cursor-grabbing'
    : tool === 'pan'
      ? 'cursor-grab'
      : tool === 'select'
        ? 'cursor-default'
      : tool === 'marquee'
          ? 'cursor-crosshair'
          : 'cursor-crosshair';
  const canvasCursorStyle = tool === 'wall' && !dragState ? { cursor: WALL_TROWEL_CURSOR } : undefined;
  const bindSvgRef = useCallback((node: SVGSVGElement | null) => {
    svgRef.current = node;
    setWheelElement(node);
  }, []);

  const loadMine = useCallback(async () => {
    try {
      const response = await mrApiMapsMine();
      const ownMaps = response.maps ?? [];
      if (!isAdminEditor) {
        setMaps(ownMaps);
      } else {
        try {
          const publishedResponse = await mrApiMapsPublished();
          const merged = [...ownMaps];
          for (const map of publishedResponse.maps ?? []) {
            if (!merged.some((entry) => entry.id === map.id)) {
              merged.push(map);
            }
          }
          setMaps(merged);
        } catch {
          setMaps(ownMaps);
        }
      }
      setAuthRequired(false);
    } catch (error: any) {
      if (error?.status === 401) {
        setAuthRequired(true);
        setStatus('Редактор требует авторизацию');
      } else {
        setStatus(mrApiErrorMessage(error, 'Не удалось загрузить мои карты'));
      }
    }
  }, [isAdminEditor]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const readAdminState = () => {
      try {
        const raw = getStoredUserRaw();
        if (!raw) {
          setIsAdminEditor(false);
          return;
        }
        const storedUser = JSON.parse(raw) as StoredUser;
        const roleName = String(storedUser?.role?.name ?? '');
        setIsAdminEditor(Boolean(storedUser?.is_superuser || storedUser?.is_staff || /админ|admin|модератор|staff/i.test(roleName)));
      } catch {
        setIsAdminEditor(false);
      }
    };
    readAdminState();
    window.addEventListener('lisad_auth_changed', readAdminState);
    return () => window.removeEventListener('lisad_auth_changed', readAdminState);
  }, []);

  useEffect(() => {
    let mounted = true;
    void mrApiProfile()
      .then(() => {
        if (!mounted) return;
        setAuthRequired(false);
        void loadMine();
      })
      .catch((error: any) => {
        if (!mounted) return;
        setAuthRequired(error?.status === 401);
        setStatus(error?.status === 401 ? 'Редактор требует авторизацию' : mrApiErrorMessage(error, 'Профиль недоступен'));
      });
    return () => {
      mounted = false;
    };
  }, [loadMine]);

  const reloadObjectSkins = useCallback(async () => {
    try {
      const response = await mrApiObjectSkins();
      setObjectSkins(response.skins ?? []);
    } catch (error: any) {
      setObjectSkins([]);
      if (error?.status !== 401) {
        setStatus(mrApiErrorMessage(error, 'Не удалось загрузить варианты внешнего вида'));
      }
    }
  }, []);

  useEffect(() => {
    void reloadObjectSkins();
  }, [reloadObjectSkins]);

  useEffect(() => {
    const previewUrls = pendingAssetPreviewUrlsRef.current;
    return () => {
      previewUrls.forEach((url) => URL.revokeObjectURL(url));
      previewUrls.clear();
    };
  }, []);

  useEffect(() => {
    if (tool === 'wall') return;
    setWallBuildDraft(null);
    setWallPreviewPoint(null);
  }, [tool]);

  const markDirty = useCallback(() => {
    setDirtyRevision((revision) => revision + 1);
    setSaveState('dirty');
  }, []);

  const LOBBY_MODE_TOGGLE_LABEL: Record<MysteryRoomGameMode, string> = {
    coop: 'Кооп',
    survival: 'Выживание',
    stealth: 'Скрытность',
    infection: 'Заражение',
  };

  const togglePlayableLobbyMode = useCallback(
    (mode: MysteryRoomGameMode) => {
      setDraftMap((cur) => {
        const limited = getMysteryRoomMapPlayableModes(cur);
        const active = new Set<MysteryRoomGameMode>(limited ?? MYSTERY_ROOM_ALL_GAME_MODES);
        if (active.has(mode)) active.delete(mode);
        else active.add(mode);
        if (active.size === 0 || active.size === MYSTERY_ROOM_ALL_GAME_MODES.length) {
          const { playableGameModes: _drop, ...rest } = cur;
          return rest as MysteryRoomMapDefinition;
        }
        return {
          ...cur,
          playableGameModes: MYSTERY_ROOM_ALL_GAME_MODES.filter((m) => active.has(m)),
        };
      });
      markDirty();
    },
    [markDirty],
  );

  const recordUndoSnapshot = useCallback((map: MysteryRoomMapDefinition, selection: MysteryRoomEditorSelection | null) => {
    setUndoHistory((current) => trimEditorHistory([...current, createEditorHistorySnapshot(map, selection)]));
    setRedoHistory([]);
  }, []);

  const clearEditorHistory = useCallback(() => {
    setUndoHistory([]);
    setRedoHistory([]);
  }, []);

  const syncMapList = useCallback((full: MrMapFull) => {
    setMaps((current) => {
      const summary = toMapSummary(full);
      const withoutCurrent = current.filter((map) => map.id !== summary.id);
      return [summary, ...withoutCurrent].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    });
  }, []);

  const updateMapAssetSummary = useCallback((mapId: string, byteDelta: number, updatedAt?: string) => {
    setMaps((current) =>
      current
        .map((map) =>
          map.id === mapId
            ? {
                ...map,
                assetBytesTotal: Math.max(0, map.assetBytesTotal + byteDelta),
                updatedAt: updatedAt || new Date().toISOString(),
              }
            : map,
        )
        .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()),
    );
  }, []);

  const applyFullMap = useCallback((full: MrMapFull) => {
    const nextMap = bindPersistedMapId(full.map, full.id);
    const effectiveFull =
      isAdminEditor && full.status !== 'draft'
        ? { ...full, status: 'draft' as const, map: nextMap }
        : { ...full, map: nextMap };
    setActiveMap(effectiveFull);
    setDraftMap(nextMap);
    setTitle(effectiveFull.title || nextMap.title);
    setDescription(effectiveFull.description ?? '');
    setPublishNotice(null);
    setSelected(null);
    setMultiSelected([]);
    setViewBox(fitViewBoxToEditorContent(nextMap));
    setSaveState('saved');
    clearEditorHistory();
    const savedAt = formatSaveTime(new Date());
    setLastSavedAt(savedAt);
    setDirtyRevision((revision) => {
      lastSavedRevisionRef.current = revision;
      return revision;
    });
    registerMysteryRoomMapDefinition(nextMap);
  }, [clearEditorHistory, isAdminEditor]);

  const openMap = useCallback(
    async (mapId: string) => {
      setStatus('Загружаю карту...');
      try {
        const response = await mrApiMapGet(mapId);
        applyFullMap(response.map);
        setStatus('Карта загружена');
      } catch (error: any) {
        setStatus(mrApiErrorMessage(error, 'Не удалось открыть карту'));
      }
    },
    [applyFullMap],
  );

  useEffect(() => {
    if (!initialMapId) return;
    if (activeMap?.id === initialMapId) return;
    if (autoOpenedMapIdRef.current === initialMapId) return;
    autoOpenedMapIdRef.current = initialMapId;
    void openMap(initialMapId);
  }, [activeMap?.id, initialMapId, openMap]);

  const createDraftFromTemplate = useCallback(async (template: MysteryRoomEditorTemplate) => {
    const map = template.createMap();
    const newTitle = map.title || template.title;
    setPublishNotice(null);
    setStatus(`Создаю черновик: ${template.title}...`);
    try {
      const response = await mrApiMapCreate({
        title: newTitle,
        description: template.id === 'blank' ? '' : `Создано из шаблона: ${template.title}`,
        map,
        schemaVersion: 1,
      });
      applyFullMap(response.map);
      await loadMine();
      setStatus('Черновик создан');
    } catch (error: any) {
      setStatus(mrApiErrorMessage(error, 'Не удалось создать карту'));
    }
  }, [applyFullMap, loadMine]);

  const createDraft = useCallback(async () => {
    const blankTemplate = templates.find((template) => template.id === 'blank') ?? {
      id: 'blank',
      title: 'Пустая карта',
      description: '',
      badge: 'empty',
      createMap: () => createBlankMysteryRoomEditorMap(),
    };
    await createDraftFromTemplate(blankTemplate);
  }, [createDraftFromTemplate, templates]);

  const saveDraft = useCallback(async (options?: { autosave?: boolean }) => {
    if (!activeMap) {
      setStatus('Сначала создайте черновик на сервере');
      return null;
    }
    if (!canEditActiveMap) {
      setStatus('Недостаточно прав на редактирование этой карты');
      return null;
    }
    const mapToSave = normalizeEditableMap({ ...draftMap, id: activeMap.id, title });
    const autosave = options?.autosave === true;
    setSaveState('saving');
    if (!autosave) setStatus('Сохраняю карту...');
    try {
      const response = await mrApiMapUpdate(activeMap.id, {
        title,
        description,
        map: mapToSave,
        schemaVersion: activeMap.schemaVersion || 1,
      });
      const nextMap = bindPersistedMapId(response.map.map, response.map.id);
      setActiveMap({ ...response.map, map: nextMap });
      syncMapList(response.map);
      registerMysteryRoomMapDefinition(nextMap);
      lastSavedRevisionRef.current = dirtyRevision;
      const savedAt = formatSaveTime(new Date());
      setLastSavedAt(savedAt);
      setSaveState('saved');
      setStatus(autosave ? `Автосохранено ${savedAt}` : 'Сохранено');
      return response.map;
    } catch (error: any) {
      setSaveState('error');
      setStatus(mrApiErrorMessage(error, 'Не удалось сохранить карту'));
      return null;
    }
  }, [activeMap, canEditActiveMap, description, dirtyRevision, draftMap, syncMapList, title]);

  useEffect(() => {
    if (!canEditActiveMap) return;
    if (dirtyRevision <= lastSavedRevisionRef.current) return;
    if (dragState) return;
    const timeout = window.setTimeout(() => {
      void saveDraft({ autosave: true });
    }, AUTOSAVE_DELAY_MS);
    return () => window.clearTimeout(timeout);
  }, [canEditActiveMap, dirtyRevision, dragState, saveDraft]);

  const returnToStart = useCallback(async () => {
    if (canEditActiveMap && dirtyRevision > lastSavedRevisionRef.current) {
      await saveDraft({ autosave: true });
    }
    setActiveMap(null);
    setSelected(null);
    setMultiSelected([]);
  }, [canEditActiveMap, dirtyRevision, saveDraft]);

  const duplicateMapFromList = useCallback(async (mapId: string) => {
    setPublishNotice(null);
    setStatus('Дублирую карту...');
    try {
      const response = await mrApiMapDuplicate(mapId);
      applyFullMap(response.map);
      await loadMine();
      setStatus('Копия создана');
    } catch (error: any) {
      setStatus(mrApiErrorMessage(error, 'Не удалось дублировать карту'));
    }
  }, [applyFullMap, loadMine]);

  const deleteDraftFromList = useCallback(async (map: MrMapSummary) => {
    if (map.status !== 'draft') return;
    if (typeof window !== 'undefined' && !window.confirm(`Удалить черновик "${map.title}"?`)) {
      return;
    }
    setStatus('Удаляю черновик...');
    try {
      await mrApiMapDelete(map.id);
      await loadMine();
      setStatus('Черновик удален');
    } catch (error: any) {
      setStatus(mrApiErrorMessage(error, 'Не удалось удалить черновик'));
    }
  }, [loadMine]);

  const renameMapFromList = useCallback(async (map: MrMapSummary) => {
    const currentTitle = map.title || 'Карта';
    const nextTitle = typeof window === 'undefined'
      ? currentTitle
      : window.prompt('Новое название карты', currentTitle)?.trim();
    if (!nextTitle || nextTitle === currentTitle) return;
    setPublishNotice(null);
    setStatus('Переименовываю карту...');
    try {
      const response = await mrApiMapUpdate(map.id, {
        title: nextTitle,
        description: map.description ?? '',
      });
      syncMapList(response.map);
      await loadMine();
      setStatus('Название обновлено');
    } catch (error: any) {
      setStatus(mrApiErrorMessage(error, 'Не удалось переименовать карту'));
    }
  }, [loadMine, syncMapList]);

  const publishMapFromList = useCallback(async (mapId: string) => {
    setPublishNotice(null);
    setStatus('Публикую карту...');
    try {
      const response = await mrApiMapPublish(mapId);
      syncMapList(response.map);
      await loadMine();
      setStatus('Карта опубликована');
    } catch (error: any) {
      const mapTitle = maps.find((map) => map.id === mapId)?.title ?? 'Карта';
      setPublishNotice({ mapTitle, issues: publishIssuesFromError(error) });
      setStatus('Публикация пока не готова: проверьте список ниже.');
    }
  }, [loadMine, maps, syncMapList]);

  const playActiveMap = useCallback(async () => {
    if (!activeMap || !onPlayMap) {
      setStatus('Сначала создайте или откройте карту');
      return;
    }
    let mapForPreview = bindPersistedMapId(draftMap, activeMap.id);
    let previewTitle = title || activeMap.title || mapForPreview.title;
    if (canEditActiveMap && dirtyRevision > lastSavedRevisionRef.current) {
      setStatus('Сохраняю черновик перед тестом...');
      const saved = await saveDraft({ autosave: true });
      if (!saved) return;
      mapForPreview = bindPersistedMapId(saved.map, saved.id);
      previewTitle = saved.title || mapForPreview.title;
    }
    setStatus('Запускаю тест карты...');
    onPlayMap(activeMap.id, mapForPreview, previewTitle);
  }, [activeMap, canEditActiveMap, dirtyRevision, draftMap, onPlayMap, saveDraft, title]);

  const commitMapChange = useCallback(
    (
      updater: (map: MysteryRoomMapDefinition) => MysteryRoomMapDefinition,
      options?: { selection?: MysteryRoomEditorSelection | null; recordHistory?: boolean },
    ) => {
      const nextMap = normalizeEditableMap(updater(draftMap));
      if (editorMapsEqual(draftMap, nextMap)) {
        if (Object.prototype.hasOwnProperty.call(options ?? {}, 'selection')) {
          setSelected(options?.selection ?? null);
          setMultiSelected([]);
        }
        return false;
      }
      if (options?.recordHistory !== false) {
        recordUndoSnapshot(draftMap, selected);
      } else {
        setRedoHistory([]);
      }
      setDraftMap(nextMap);
      if (Object.prototype.hasOwnProperty.call(options ?? {}, 'selection')) {
        setSelected(options?.selection ?? null);
        setMultiSelected([]);
      }
      markDirty();
      return true;
    },
    [draftMap, markDirty, recordUndoSnapshot, selected],
  );

  const updateMap = useCallback(
    (
      updater: (map: MysteryRoomMapDefinition) => MysteryRoomMapDefinition,
      options?: { selection?: MysteryRoomEditorSelection | null; recordHistory?: boolean },
    ) => {
      commitMapChange(updater, options);
    },
    [commitMapChange],
  );

  const applySelectionGroup = useCallback((selections: MysteryRoomEditorSelection[], primary?: MysteryRoomEditorSelection | null) => {
    const nextSelections = uniqueEditorSelections(selections).filter((entry) => entry.type !== 'spawn' || selections.length === 1);
    const nextPrimary = primary && nextSelections.some((entry) => editorSelectionsEqual(entry, primary))
      ? primary
      : nextSelections[0] ?? null;
    setSelected(nextPrimary);
    setMultiSelected(nextPrimary ? nextSelections.filter((entry) => !editorSelectionsEqual(entry, nextPrimary)) : []);
  }, []);

  const clearSelectionGroup = useCallback(() => {
    setSelected(null);
    setMultiSelected([]);
  }, []);

  const generateResourcesOnMap = useCallback(() => {
    if (!canEditActiveMap) {
      setStatus('Карта доступна только для чтения');
      return;
    }
    const result = generateEditorResourceObjects(draftMap, {
      seed: resourceGenerationSeed,
      bushCount: resourceGenerationCounts.bush,
      scrapCount: resourceGenerationCounts.scrap,
      rockCount: resourceGenerationCounts.rock,
    });
    if (!result) {
      setStatus('Генерация ресурсов: нет свободных точек');
      return;
    }
    const changed = commitMapChange(() => result.map, { selection: result.selections[0] ?? null });
    if (changed) {
      applySelectionGroup(result.selections, result.selections[0] ?? null);
      setStatus(`Сгенерировано ресурсов: ${result.selections.length}`);
    }
  }, [applySelectionGroup, canEditActiveMap, commitMapChange, draftMap, resourceGenerationCounts, resourceGenerationSeed]);

  const updateObjectAssetTransform = useCallback(
    (
      objectId: string,
      updater: (transform: ReturnType<typeof normalizeEditorAssetTransform>) => ReturnType<typeof normalizeEditorAssetTransform>,
    ) => {
      updateMap((draft) => ({
        ...draft,
        customObjects: draft.customObjects.map((object) => {
          if (object.id !== objectId) return object;
          return { ...object, assetTransform: updater(normalizeEditorAssetTransform(object)) };
        }),
      }));
    },
    [updateMap],
  );

  const svgPointFromClient = useCallback((clientX: number, clientY: number): MysteryRoomVec2 | null => {
    const svg = svgRef.current;
    if (!svg) return null;
    const matrix = svg.getScreenCTM();
    if (!matrix) return null;
    const point = svg.createSVGPoint();
    point.x = clientX;
    point.y = clientY;
    const transformed = point.matrixTransform(matrix.inverse());
    return { x: transformed.x, y: transformed.y };
  }, []);

  const isClientPointInsideCanvas = useCallback((clientX: number, clientY: number) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return false;
    return clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom;
  }, []);

  const svgPointFromEvent = useCallback(
    (event: PointerEvent<SVGSVGElement | SVGElement>): MysteryRoomVec2 | null => svgPointFromClient(event.clientX, event.clientY),
    [svgPointFromClient],
  );

  const startTransformDrag = useCallback(
    (event: PointerEvent<SVGGElement>, handle: MysteryRoomEditorTransformHandle) => {
      if (!selected) return;
      if (!canEditActiveMap) {
        setStatus('Трансформации доступны только в черновике');
        return;
      }
      const point = svgPointFromEvent(event);
      if (!point) return;
      event.currentTarget.setPointerCapture?.(event.pointerId);
      setDragState({
        kind: 'transform',
        selection: selected,
        handle,
        startPoint: point,
        startMap: cloneEditorMap(draftMap),
        changed: false,
        pointerId: event.pointerId,
      });
    },
    [canEditActiveMap, draftMap, selected, svgPointFromEvent],
  );

  const changeSelectionLayer = useCallback(
    (event: PointerEvent<SVGGElement>, delta: number) => {
      event.preventDefault();
      event.stopPropagation();
      if (!selected) return;
      if (!canEditActiveMap) {
        setStatus('Слои можно менять только в черновике');
        return;
      }
      const next = adjustEditorSelectionLayer(draftMap, selected, delta);
      const nextLayer = getEditorSelectionLayer(next, selected);
      commitMapChange(() => next);
      setStatus(nextLayer === null ? 'Слой не изменен' : `Слой выбранного объекта: ${nextLayer}`);
    },
    [canEditActiveMap, commitMapChange, draftMap, selected],
  );

  const createEntityAtPoint = useCallback(
    (nextTool: MysteryRoomEditorTool, point: MysteryRoomVec2) => {
      if (!isCreationTool(nextTool)) return false;
      if (!activeMap) {
        setStatus('Сначала создайте или откройте черновик');
        return false;
      }
      if (!canEditActiveMap) {
        setStatus('Редактировать можно только черновик');
        return false;
      }
      const result = createEditorEntity(draftMap, nextTool, point);
      if (!result) return false;
      const mapWithDefaultSkin =
        result.selection.type === 'customObject'
          ? {
              ...result.map,
              customObjects: result.map.customObjects.map((object) => {
                if (object.id !== result.selection.id) return object;
                const skin = defaultObjectSkinForObject(objectSkins, object);
                return skin ? applyObjectSkinToObject(object, skin) : object;
              }),
            }
          : result.map;
      commitMapChange(() => mapWithDefaultSkin, { selection: result.selection });
      setTool('select');
      setStatus(`Добавлен объект: ${selectedEntityLabel(mapWithDefaultSkin, result.selection)}`);
      return true;
    },
    [activeMap, canEditActiveMap, commitMapChange, draftMap, objectSkins],
  );

  const createEntityAtClientPoint = useCallback(
    (nextTool: MysteryRoomEditorTool, clientX: number, clientY: number) => {
      if (!isClientPointInsideCanvas(clientX, clientY)) return false;
      const point = svgPointFromClient(clientX, clientY);
      return point ? createEntityAtPoint(nextTool, point) : false;
    },
    [createEntityAtPoint, isClientPointInsideCanvas, svgPointFromClient],
  );

  const startPanDrag = useCallback(
    (event: PointerEvent<SVGSVGElement | SVGElement>) => {
      event.preventDefault();
      event.stopPropagation();
      svgRef.current?.setPointerCapture?.(event.pointerId);
      setDragState({
        kind: 'pan',
        startClient: { x: event.clientX, y: event.clientY },
        startViewBox: viewBox,
        pointerId: event.pointerId,
      });
      return true;
    },
    [viewBox],
  );

  const collectSelectableSelections = useCallback(() => {
    const candidates: MysteryRoomEditorSelection[] = [];
    if (showLayers.rooms) draftMap.rooms.forEach((room) => candidates.push({ type: 'room', id: room.id }));
    if (showLayers.walls) draftMap.walls.forEach((_wall, index) => candidates.push({ type: 'wall', id: String(index) }));
    if (showLayers.doors) draftMap.doors.forEach((door) => candidates.push({ type: 'door', id: door.id }));
    if (showLayers.actors) {
      normalizeEditorSpawnSlots(draftMap).forEach((slot, index) => {
        if (editorSpawnSlotMatchesLayerFilter(slot, spawnLayerFilter)) {
          candidates.push({ type: 'spawn', id: String(index) });
        }
      });
    }
    if (showLayers.actors) draftMap.npcs.forEach((npc) => candidates.push({ type: 'npc', id: npc.id }));
    if (showLayers.objects) draftMap.customObjects.forEach((object) => candidates.push({ type: 'customObject', id: object.id }));
    if (showLayers.objects) draftMap.interactables.forEach((entry) => candidates.push({ type: 'interactable', id: entry.id }));
    if (showLayers.objects) draftMap.lights.forEach((light) => candidates.push({ type: 'light', id: light.id }));
    if (showLayers.triggers) draftMap.triggers.forEach((trigger) => candidates.push({ type: 'trigger', id: trigger.id }));
    if (showLayers.effects) draftMap.hazards.forEach((hazard) => candidates.push({ type: 'hazard', id: hazard.id }));
    if (showLayers.sounds) draftMap.soundSources.forEach((source) => candidates.push({ type: 'soundSource', id: source.id }));
    if (showLayers.anchors) draftMap.incidentAnchors.forEach((anchor) => candidates.push({ type: 'incidentAnchor', id: anchor.id }));
    if (showLayers.anchors) draftMap.workAnchors.forEach((anchor) => candidates.push({ type: 'workAnchor', id: anchor.id }));
    if (showLayers.nav) draftMap.navNodes.forEach((node) => candidates.push({ type: 'navNode', id: node.id }));
    return candidates;
  }, [draftMap, showLayers, spawnLayerFilter]);

  const startSelectBoxDrag = useCallback(
    (event: PointerEvent<SVGSVGElement | SVGElement>, point: MysteryRoomVec2, addToExisting: boolean) => {
      event.currentTarget.setPointerCapture?.(event.pointerId);
      setDragState({
        kind: 'select-box',
        start: point,
        current: point,
        addToExisting,
        moved: false,
        pointerId: event.pointerId,
      });
    },
    [],
  );

  const clearWallBuildDraft = useCallback((restoreLonelyPoint: boolean) => {
    if (restoreLonelyPoint && wallBuildDraft && wallBuildDraft.createdSegments === 0 && !editorMapsEqual(draftMap, wallBuildDraft.startMap)) {
      setDraftMap(cloneEditorMap(wallBuildDraft.startMap));
      markDirty();
      setStatus('Черновая точка стены удалена');
    } else if (wallBuildDraft) {
      setStatus('Построение стен завершено');
    }
    setWallBuildDraft(null);
    setWallPreviewPoint(null);
  }, [draftMap, markDirty, wallBuildDraft]);

  const handleWallBuildClick = useCallback(
    (point: MysteryRoomVec2) => {
      if (!activeMap) {
        setStatus('Сначала создайте или откройте черновик');
        return;
      }
      if (!canEditActiveMap) {
        setStatus('Редактировать можно только черновик');
        return;
      }
      const beforeClick = cloneEditorMap(draftMap);
      const resolved = resolveEditorWallBuildPoint(draftMap, point);
      if (!wallBuildDraft) {
        if (!editorMapsEqual(draftMap, resolved.map)) {
          recordUndoSnapshot(draftMap, selected);
          setDraftMap(resolved.map);
          markDirty();
        }
        setWallBuildDraft({ anchor: resolved.point, startMap: beforeClick, createdSegments: 0 });
        setWallPreviewPoint(resolved.point);
        setStatus('Поставлена первая точка стены');
        return;
      }

      const connected = connectEditorWallPoints(resolved.map, wallBuildDraft.anchor, resolved.point, resolved.sourceWallIndex);
      if (!editorMapsEqual(draftMap, connected.map)) {
        recordUndoSnapshot(draftMap, selected);
        setDraftMap(connected.map);
        markDirty();
      }
      if (connected.wallIndex !== null) {
        const chain = getConnectedEditorWallIndices(connected.map, connected.wallIndex).map((wallIndex) => ({ type: 'wall' as const, id: String(wallIndex) }));
        applySelectionGroup(chain, { type: 'wall', id: String(connected.wallIndex) });
      }
      setWallBuildDraft({
        anchor: resolved.point,
        startMap: wallBuildDraft.startMap,
        createdSegments: wallBuildDraft.createdSegments + (connected.created ? 1 : 0),
      });
      setWallPreviewPoint(resolved.point);
      setStatus(connected.created ? 'Отрезок стены добавлен' : 'Точка стены выбрана без дубля отрезка');
    },
    [activeMap, canEditActiveMap, applySelectionGroup, draftMap, markDirty, recordUndoSnapshot, selected, wallBuildDraft],
  );

  const selectWallChainFromClick = useCallback(
    (selection: MysteryRoomEditorSelection) => {
      const wallIndex = Number(selection.id);
      const alreadySelected = activeSelections.some((entry) => editorSelectionsEqual(entry, selection));
      if (alreadySelected && activeSelections.length > 1) {
        applySelectionGroup([selection], selection);
        setStatus('Выбран отрезок стены');
        return [selection];
      }
      const chain = getConnectedEditorWallIndices(draftMap, wallIndex).map((index) => ({ type: 'wall' as const, id: String(index) }));
      const nextSelections = chain.length ? chain : [selection];
      applySelectionGroup(nextSelections, selection);
      setStatus(nextSelections.length > 1 ? `Выбрана связанная стена: ${nextSelections.length} отрезков` : 'Выбран отрезок стены');
      return nextSelections;
    },
    [activeSelections, applySelectionGroup, draftMap],
  );

  const startWallNodeDrag = useCallback(
    (event: PointerEvent<SVGCircleElement>, node: MysteryRoomVec2) => {
      if (tool !== 'select') return;
      if (!canEditActiveMap) {
        setStatus('Точки стен можно двигать только в черновике');
        return;
      }
      const point = svgPointFromEvent(event);
      if (!point) return;
      event.preventDefault();
      event.stopPropagation();
      svgRef.current?.setPointerCapture?.(event.pointerId);
      const touchingWall = draftMap.walls.findIndex(
        (wall) => Math.hypot(wall.start.x - node.x, wall.start.y - node.y) <= 0.5 || Math.hypot(wall.end.x - node.x, wall.end.y - node.y) <= 0.5,
      );
      if (touchingWall >= 0) {
        const chain = getConnectedEditorWallIndices(draftMap, touchingWall).map((index) => ({ type: 'wall' as const, id: String(index) }));
        applySelectionGroup(chain, { type: 'wall', id: String(touchingWall) });
      }
      setDragState({
        kind: 'wall-node',
        node,
        last: point,
        startMap: cloneEditorMap(draftMap),
        moved: false,
        pointerId: event.pointerId,
      });
    },
    [canEditActiveMap, applySelectionGroup, draftMap, svgPointFromEvent, tool],
  );

  const handleCanvasPointerDown = useCallback(
    (event: PointerEvent<SVGSVGElement>) => {
      if (event.button === MIDDLE_MOUSE_BUTTON || (tool === 'pan' && event.button === LEFT_MOUSE_BUTTON)) {
        setContextMenu(null);
        startPanDrag(event);
        return;
      }
      if (tool === 'wall' && event.button === RIGHT_MOUSE_BUTTON) {
        event.preventDefault();
        clearWallBuildDraft(true);
        return;
      }
      if (event.button !== LEFT_MOUSE_BUTTON) return;
      setContextMenu(null);
      const point = svgPointFromEvent(event);
      if (!point) return;
      lastMapPointerRef.current = point;
      if (tool === 'select' || tool === 'marquee') {
        startSelectBoxDrag(event, point, event.shiftKey || event.ctrlKey || event.metaKey);
        return;
      }
      if (tool === 'wall') {
        handleWallBuildClick(point);
        return;
      }
      createEntityAtPoint(tool, point);
    },
    [clearWallBuildDraft, createEntityAtPoint, handleWallBuildClick, startPanDrag, startSelectBoxDrag, svgPointFromEvent, tool],
  );

  const handleEntityPointerDown = useCallback(
    (event: PointerEvent<SVGElement>, selection: MysteryRoomEditorSelection) => {
      if (event.button === MIDDLE_MOUSE_BUTTON || (tool === 'pan' && event.button === LEFT_MOUSE_BUTTON)) {
        setContextMenu(null);
        startPanDrag(event);
        return;
      }
      event.stopPropagation();
      const point = svgPointFromEvent(event);
      if (point) {
        lastMapPointerRef.current = point;
      }
      if (tool === 'wall' && event.button === RIGHT_MOUSE_BUTTON) {
        event.preventDefault();
        suppressCanvasContextMenuRef.current = true;
        clearWallBuildDraft(true);
        return;
      }
      if (event.button === RIGHT_MOUSE_BUTTON) {
        event.preventDefault();
        suppressCanvasContextMenuRef.current = true;
        const alreadySelected = activeSelections.some((entry) => editorSelectionsEqual(entry, selection));
        if (!alreadySelected) {
          applySelectionGroup([selection], selection);
        } else {
          applySelectionGroup(activeSelections, selection);
        }
        setContextMenu({
          kind: 'object',
          clientX: event.clientX,
          clientY: event.clientY,
          point,
          selection,
        });
        return;
      }
      if (event.button !== LEFT_MOUSE_BUTTON) return;
      setContextMenu(null);
      if (tool === 'wall' && point) {
        handleWallBuildClick(point);
        return;
      }
      if ((tool === 'select' || tool === 'marquee') && tool === 'marquee' && point) {
        startSelectBoxDrag(event, point, event.shiftKey || event.ctrlKey || event.metaKey);
        return;
      }
      if (tool !== 'select') {
        if (point && isCreationTool(tool)) {
          createEntityAtPoint(tool, point);
        }
        return;
      }
      const alreadySelected = activeSelections.some((entry) => editorSelectionsEqual(entry, selection));
      if (event.shiftKey || event.ctrlKey || event.metaKey) {
        const nextSelections = alreadySelected
          ? activeSelections.filter((entry) => !editorSelectionsEqual(entry, selection))
          : [...activeSelections, selection];
        applySelectionGroup(nextSelections, alreadySelected ? nextSelections[0] : selection);
        setStatus(nextSelections.length > 1 ? `Выбрано объектов: ${nextSelections.length}` : 'Выбор обновлен');
        return;
      }
      const nextSelections = selection.type === 'wall'
        ? selectWallChainFromClick(selection)
        : alreadySelected && activeSelections.length > 1
          ? activeSelections
          : [selection];
      if (selection.type !== 'wall') {
        applySelectionGroup(nextSelections, selection);
      }
      if (point) {
        event.currentTarget.setPointerCapture?.(event.pointerId);
        if (nextSelections.length === 1 && selection.type === 'customObject' && assetEditObjectId === selection.id) {
          setDragState({
            kind: 'asset-pan',
            objectId: selection.id,
            last: point,
            startMap: cloneEditorMap(draftMap),
            moved: false,
            pointerId: event.pointerId,
          });
          return;
        }
        setDragState({
          kind: 'move',
          selection,
          selections: nextSelections,
          last: point,
          startMap: cloneEditorMap(draftMap),
          moved: false,
          pointerId: event.pointerId,
        });
      }
    },
    [activeSelections, applySelectionGroup, assetEditObjectId, clearWallBuildDraft, createEntityAtPoint, draftMap, handleWallBuildClick, selectWallChainFromClick, startPanDrag, startSelectBoxDrag, svgPointFromEvent, tool],
  );

  const handlePalettePointerDown = useCallback((event: PointerEvent<HTMLButtonElement>, nextTool: MysteryRoomEditorTool) => {
    if (event.button !== LEFT_MOUSE_BUTTON) return;
    setTool(nextTool);
    if (nextTool === 'wall') {
      setStatus('Режим стен: ставьте точки левой кнопкой, правая кнопка завершает чертеж');
      return;
    }
    if (!isCreationTool(nextTool)) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setPaletteDrag({
      tool: nextTool,
      pointerId: event.pointerId,
      clientX: event.clientX,
      clientY: event.clientY,
    });
    setStatus(`Перетащите инструмент "${toolDefinitionById(nextTool)?.label ?? nextTool}" на карту`);
  }, []);

  const handlePalettePointerMove = useCallback((event: PointerEvent<HTMLButtonElement>) => {
    setPaletteDrag((current) =>
      current?.pointerId === event.pointerId
        ? { ...current, clientX: event.clientX, clientY: event.clientY }
        : current,
    );
  }, []);

  const handlePalettePointerUp = useCallback(
    (event: PointerEvent<HTMLButtonElement>) => {
      if (!paletteDrag || paletteDrag.pointerId !== event.pointerId) return;
      event.currentTarget.releasePointerCapture?.(event.pointerId);
      const didCreate = createEntityAtClientPoint(paletteDrag.tool, event.clientX, event.clientY);
      suppressPaletteClickRef.current = didCreate;
      setPaletteDrag(null);
    },
    [createEntityAtClientPoint, paletteDrag],
  );

  const handlePalettePointerCancel = useCallback((event: PointerEvent<HTMLButtonElement>) => {
    if (paletteDrag?.pointerId === event.pointerId) {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
      setPaletteDrag(null);
    }
  }, [paletteDrag]);

  const handlePointerMove = useCallback(
    (event: PointerEvent<SVGSVGElement>) => {
      const currentPoint = svgPointFromEvent(event);
      if (currentPoint) {
        lastMapPointerRef.current = currentPoint;
        if (tool === 'wall') {
          setWallPreviewPoint(findEditorWallNode(draftMap, currentPoint, MR_EDITOR_WALL_NODE_SNAP_RADIUS) ?? currentPoint);
        }
      }
      if (!dragState) return;
      if (dragState.kind === 'pan') {
        const rect = svgRef.current?.getBoundingClientRect();
        if (!rect?.width || !rect.height) return;
        const deltaClientX = event.clientX - dragState.startClient.x;
        const deltaClientY = event.clientY - dragState.startClient.y;
        const deltaWorldX = (deltaClientX / rect.width) * dragState.startViewBox.width;
        const deltaWorldY = (deltaClientY / rect.height) * dragState.startViewBox.height;
        setViewBox(
          clampViewBoxToMap(
            {
              ...dragState.startViewBox,
              x: dragState.startViewBox.x - deltaWorldX,
              y: dragState.startViewBox.y - deltaWorldY,
            },
            draftMap.width,
            draftMap.height,
          ),
        );
        return;
      }
      if (dragState.kind === 'transform') {
        const point = currentPoint;
        if (!point) return;
        setDraftMap(normalizeEditableMap(dragTransformEditorSelection(dragState.startMap, dragState.selection, dragState.handle, dragState.startPoint, point)));
        setDragState({ ...dragState, changed: true });
        return;
      }
      if (dragState.kind === 'select-box') {
        const point = currentPoint;
        if (!point) return;
        const moved = dragState.moved || Math.abs(point.x - dragState.start.x) > 8 || Math.abs(point.y - dragState.start.y) > 8;
        setDragState({ ...dragState, current: point, moved });
        return;
      }
      if (dragState.kind === 'asset-pan') {
        const point = currentPoint;
        if (!point) return;
        const delta = { x: point.x - dragState.last.x, y: point.y - dragState.last.y };
        setDraftMap((current) => moveCustomObjectAssetTransform(current, dragState.objectId, delta));
        setDragState({
          ...dragState,
          last: point,
          moved: dragState.moved || Math.abs(delta.x) > 0.001 || Math.abs(delta.y) > 0.001,
        });
        return;
      }
      if (dragState.kind === 'wall-node') {
        const point = currentPoint;
        if (!point) return;
        setDraftMap(normalizeEditableMap(moveEditorWallNode(dragState.startMap, dragState.node, point)));
        setDragState({
          ...dragState,
          last: point,
          moved: dragState.moved || Math.abs(point.x - dragState.last.x) > 0.001 || Math.abs(point.y - dragState.last.y) > 0.001,
        });
        return;
      }
      const point = currentPoint;
      if (!point) return;
      const delta = { x: point.x - dragState.last.x, y: point.y - dragState.last.y };
      setDraftMap((current) => normalizeEditableMap(moveEditorSelections(current, dragState.selections, delta)));
      setDragState({
        kind: 'move',
        selection: dragState.selection,
        selections: dragState.selections,
        last: point,
        startMap: dragState.startMap,
        moved: dragState.moved || Math.abs(delta.x) > 0.001 || Math.abs(delta.y) > 0.001,
        pointerId: dragState.pointerId,
      });
    },
    [draftMap, dragState, svgPointFromEvent, tool],
  );

  const handlePointerUp = useCallback((event?: PointerEvent<SVGSVGElement>) => {
    if (dragState?.kind === 'move' && dragState.moved) {
      recordUndoSnapshot(dragState.startMap, dragState.selection);
      markDirty();
    }
    if (dragState?.kind === 'transform' && dragState.changed) {
      recordUndoSnapshot(dragState.startMap, dragState.selection);
      markDirty();
    }
    if (dragState?.kind === 'asset-pan' && dragState.moved) {
      recordUndoSnapshot(dragState.startMap, { type: 'customObject', id: dragState.objectId });
      markDirty();
    }
    if (dragState?.kind === 'wall-node' && dragState.moved) {
      recordUndoSnapshot(dragState.startMap, selected);
      markDirty();
    }
    if (dragState?.kind === 'select-box') {
      if (dragState.moved) {
        const selectionRect = selectionRectFromPoints(dragState.start, dragState.current);
        const matches = collectSelectableSelections().filter((entry) => {
          const bounds = getEditorSelectionBounds(draftMap, entry);
          return Boolean(bounds && rectsIntersect(bounds, selectionRect));
        });
        const nextSelections = dragState.addToExisting ? uniqueEditorSelections([...activeSelections, ...matches]) : matches;
        applySelectionGroup(nextSelections);
        setStatus(nextSelections.length > 0 ? `Выбрано объектов рамкой: ${nextSelections.length}` : 'В рамке нет объектов');
      } else if (!dragState.addToExisting) {
        clearSelectionGroup();
      }
    }
    const root = svgRef.current;
    if (dragState && root?.hasPointerCapture?.(dragState.pointerId)) {
      root.releasePointerCapture(dragState.pointerId);
    } else if (dragState && event?.currentTarget.hasPointerCapture?.(dragState.pointerId)) {
      event.currentTarget.releasePointerCapture(dragState.pointerId);
    }
    setDragState(null);
  }, [activeSelections, applySelectionGroup, clearSelectionGroup, collectSelectableSelections, draftMap, dragState, markDirty, recordUndoSnapshot]);

  const zoomAtPoint = useCallback((factor: number, focus?: MysteryRoomVec2 | null) => {
    setViewBox((current) => {
      const anchor = focus ?? { x: current.x + current.width / 2, y: current.y + current.height / 2 };
      const localX = (anchor.x - current.x) / current.width;
      const localY = (anchor.y - current.y) / current.height;
      const width = current.width * factor;
      const height = current.height * factor;
      return clampViewBoxToMap(
        {
          x: anchor.x - width * localX,
          y: anchor.y - height * localY,
          width,
          height,
        },
        draftMap.width,
        draftMap.height,
      );
    });
  }, [draftMap.height, draftMap.width]);

  const zoom = useCallback((factor: number) => {
    zoomAtPoint(factor);
  }, [zoomAtPoint]);

  const handleCanvasWheel = useCallback(
    (event: globalThis.WheelEvent) => {
      event.preventDefault();
      const boundedDelta = Math.max(-240, Math.min(240, event.deltaY));
      const factor = Math.exp(boundedDelta * WHEEL_ZOOM_SENSITIVITY);
      zoomAtPoint(factor, svgPointFromClient(event.clientX, event.clientY));
    },
    [svgPointFromClient, zoomAtPoint],
  );

  useEffect(() => {
    if (!wheelElement) return undefined;
    wheelElement.addEventListener('wheel', handleCanvasWheel, { passive: false });
    return () => wheelElement.removeEventListener('wheel', handleCanvasWheel);
  }, [handleCanvasWheel, wheelElement]);

  useEffect(() => {
    if (!wheelElement) return undefined;
    const updateSize = () => {
      const rect = wheelElement.getBoundingClientRect();
      setSvgViewportSize({
        width: Math.max(1, rect.width),
        height: Math.max(1, rect.height),
      });
    };
    updateSize();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateSize);
      return () => window.removeEventListener('resize', updateSize);
    }
    const observer = new ResizeObserver(updateSize);
    observer.observe(wheelElement);
    return () => observer.disconnect();
  }, [wheelElement]);

  const resetView = useCallback(() => {
    setViewBox(fitViewBoxToEditorContent(draftMap));
  }, [draftMap]);

  const canUndo = canEditActiveMap && undoHistory.length > 0;
  const canRedo = canEditActiveMap && redoHistory.length > 0;

  const undoEditorAction = useCallback(() => {
    if (!canEditActiveMap) {
      setStatus('Отмена доступна только в черновике');
      return;
    }
    const previous = undoHistory.at(-1);
    if (!previous) {
      setStatus('Нет действий для отмены');
      return;
    }
    setUndoHistory((current) => current.slice(0, -1));
    setRedoHistory((current) => trimEditorHistory([...current, createEditorHistorySnapshot(draftMap, selected)]));
    setDraftMap(cloneEditorMap(previous.map));
    setSelected(cloneEditorSelection(previous.selection));
    setMultiSelected([]);
    setDragState(null);
    setPaletteDrag(null);
    markDirty();
    setStatus('Действие отменено');
  }, [canEditActiveMap, draftMap, markDirty, selected, undoHistory]);

  const redoEditorAction = useCallback(() => {
    if (!canEditActiveMap) {
      setStatus('Возврат доступен только в черновике');
      return;
    }
    const next = redoHistory.at(-1);
    if (!next) {
      setStatus('Нет отмененных действий');
      return;
    }
    setRedoHistory((current) => current.slice(0, -1));
    setUndoHistory((current) => trimEditorHistory([...current, createEditorHistorySnapshot(draftMap, selected)]));
    setDraftMap(cloneEditorMap(next.map));
    setSelected(cloneEditorSelection(next.selection));
    setMultiSelected([]);
    setDragState(null);
    setPaletteDrag(null);
    markDirty();
    setStatus('Отмененное действие возвращено');
  }, [canEditActiveMap, draftMap, markDirty, redoHistory, selected]);

  const deleteSelected = useCallback(() => {
    if (!selected) return;
    if (!canEditActiveMap) {
      setStatus('Удалять объекты можно только в черновике');
      return;
    }
    const selectionsToDelete = activeSelections.length > 0 ? activeSelections : [selected];
    const sortedSelections = [...selectionsToDelete].sort((left, right) => {
      if (left.type === 'wall' && right.type === 'wall') return Number(right.id) - Number(left.id);
      return left.type === 'wall' ? 1 : right.type === 'wall' ? -1 : 0;
    });
    const changed = commitMapChange(
      (current) => sortedSelections.reduce((next, entry) => deleteEditorSelection(next, entry), current),
      { selection: null },
    );
    if (changed) {
      setMultiSelected([]);
      setStatus(sortedSelections.length > 1 ? `Удалено объектов: ${sortedSelections.length}` : 'Выбранный объект удален');
    }
  }, [canEditActiveMap, activeSelections, commitMapChange, selected]);

  const copySelectedToClipboard = useCallback(() => {
    const copyableSelections = activeSelections.filter((entry) => entry.type !== 'spawn');
    if (copyableSelections.length === 0) {
      setStatus('Выберите объект для копирования');
      return;
    }
    setClipboardSelections(copyableSelections);
    setStatus(copyableSelections.length > 1 ? `Скопировано объектов: ${copyableSelections.length}` : `Скопировано: ${selectedEntityLabel(draftMap, copyableSelections[0])}`);
  }, [activeSelections, draftMap]);

  const pasteSelections = useCallback(
    (sourceSelections: MysteryRoomEditorSelection[], options?: { atPointer?: boolean; statusPrefix?: string }) => {
      const copyableSelections = uniqueEditorSelections(sourceSelections).filter((entry) => entry.type !== 'spawn');
      if (copyableSelections.length === 0) {
        setStatus('Нет объектов для вставки');
        return;
      }
      if (!canEditActiveMap) {
        setStatus('Копировать объекты можно только в черновике');
        return;
      }
      const targetPoint = options?.atPointer
        ? lastMapPointerRef.current ?? { x: viewBox.x + viewBox.width / 2, y: viewBox.y + viewBox.height / 2 }
        : null;
      const result = duplicateEditorSelections(draftMap, copyableSelections, targetPoint ? { targetPoint } : { offset: { x: 160, y: 0 } });
      if (!result) {
        setStatus('Не удалось создать копию выбранных объектов');
        return;
      }
      const [primary, ...rest] = result.selections;
      const changed = commitMapChange(() => result.map, { selection: primary });
      if (changed) {
        setMultiSelected(rest);
        const suffix = result.selections.length > 1 ? `${result.selections.length} объектов` : selectedEntityLabel(result.map, primary);
        setStatus(`${options?.statusPrefix ?? 'Вставлено'}: ${suffix}`);
      }
    },
    [canEditActiveMap, commitMapChange, draftMap, viewBox.height, viewBox.width, viewBox.x, viewBox.y],
  );

  const duplicateSelected = useCallback(() => {
    pasteSelections(activeSelections, { statusPrefix: 'Дубликат создан' });
  }, [activeSelections, pasteSelections]);

  const pasteClipboardAtPointer = useCallback(() => {
    pasteSelections(clipboardSelections, { atPointer: true, statusPrefix: 'Копия вставлена у указателя' });
  }, [clipboardSelections, pasteSelections]);

  const handleCanvasContextMenu = useCallback(
    (event: ReactMouseEvent<SVGSVGElement>) => {
      event.preventDefault();
      if (tool === 'wall') {
        clearWallBuildDraft(true);
        return;
      }
      if (suppressCanvasContextMenuRef.current) {
        suppressCanvasContextMenuRef.current = false;
        return;
      }
      const point = svgPointFromClient(event.clientX, event.clientY);
      if (point) {
        lastMapPointerRef.current = point;
      }
      setContextMenu({
        kind: 'canvas',
        clientX: event.clientX,
        clientY: event.clientY,
        point,
      });
    },
    [clearWallBuildDraft, svgPointFromClient, tool],
  );

  const pasteClipboardFromContextMenu = useCallback(() => {
    if (contextMenu?.point) {
      lastMapPointerRef.current = contextMenu.point;
    }
    pasteClipboardAtPointer();
    setContextMenu(null);
  }, [contextMenu, pasteClipboardAtPointer]);

  const createEntityFromContextMenu = useCallback(
    (nextTool: MysteryRoomEditorTool) => {
      if (!contextMenu?.point) return;
      if (nextTool === 'wall') {
        setTool('wall');
        handleWallBuildClick(contextMenu.point);
        setContextMenu(null);
        return;
      }
      createEntityAtPoint(nextTool, contextMenu.point);
      setContextMenu(null);
    },
    [contextMenu, createEntityAtPoint, handleWallBuildClick],
  );

  const selectVisibleObjectsInViewport = useCallback(() => {
    const viewportRect = { x: viewBox.x, y: viewBox.y, width: viewBox.width, height: viewBox.height };
    const visible = collectSelectableSelections().filter((entry) => {
      const bounds = getEditorSelectionBounds(draftMap, entry);
      if (!bounds) return false;
      return rectsIntersect(bounds, viewportRect);
    });
    applySelectionGroup(visible);
    setStatus(visible.length > 0 ? `Выбрано видимых объектов: ${visible.length}` : 'В текущей области нет видимых объектов');
  }, [applySelectionGroup, collectSelectableSelections, draftMap, viewBox.height, viewBox.width, viewBox.x, viewBox.y]);

  useEffect(() => {
    if (!contextMenu) return undefined;
    const close = () => setContextMenu(null);
    window.addEventListener('click', close);
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close, true);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [contextMenu]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const key = event.key.toLowerCase();
      const code = event.code;
      const hasCommandModifier = event.ctrlKey || event.metaKey;
      if (hasCommandModifier && !event.altKey) {
        if (key === 'z' || code === 'KeyZ') {
          event.preventDefault();
          event.stopPropagation();
          if (event.shiftKey) {
            redoEditorAction();
          } else {
            undoEditorAction();
          }
          return;
        }
        if (key === 'y' || code === 'KeyY') {
          event.preventDefault();
          event.stopPropagation();
          redoEditorAction();
          return;
        }
        if (key === 's' || code === 'KeyS') {
          event.preventDefault();
          event.stopPropagation();
          void saveDraft();
          return;
        }
        if (!isEditableTarget(event.target) && (key === 'd' || code === 'KeyD')) {
          event.preventDefault();
          event.stopPropagation();
          if (clipboardSelections.length > 0) {
            pasteClipboardAtPointer();
          } else {
            duplicateSelected();
          }
          return;
        }
        if (!isEditableTarget(event.target) && (key === 'c' || code === 'KeyC')) {
          event.preventDefault();
          event.stopPropagation();
          copySelectedToClipboard();
          return;
        }
        if (!isEditableTarget(event.target) && (key === 'v' || code === 'KeyV')) {
          event.preventDefault();
          event.stopPropagation();
          pasteClipboardAtPointer();
          return;
        }
        if (!isEditableTarget(event.target) && (key === 'a' || code === 'KeyA')) {
          event.preventDefault();
          event.stopPropagation();
          selectVisibleObjectsInViewport();
          return;
        }
      }
      if (isEditableTarget(event.target) || event.ctrlKey || event.metaKey || event.altKey) return;
      const nextTool = TOOL_SHORTCUTS[event.key.toLowerCase()];
      if (nextTool) {
        event.preventDefault();
        setTool(nextTool);
        return;
      }
      if ((event.key === 'Delete' || event.key === 'Backspace') && selected) {
        event.preventDefault();
        deleteSelected();
        return;
      }
      if (event.key === 'Escape') {
        setContextMenu(null);
        clearSelectionGroup();
        setDragState(null);
        setPaletteDrag(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    clearSelectionGroup,
    clipboardSelections.length,
    copySelectedToClipboard,
    deleteSelected,
    duplicateSelected,
    pasteClipboardAtPointer,
    redoEditorAction,
    saveDraft,
    selectVisibleObjectsInViewport,
    selected,
    undoEditorAction,
  ]);

  const applyImageAssetToSelectedTarget = useCallback(
    (asset: MrMapAsset) => {
      if (!isMysteryRoomMapImageAsset(asset)) {
        setStatus('Этот ассет не является изображением');
        return false;
      }
      if (!selected || !isTextureAssignableSelection(selected)) {
        setStatus('Выберите видимый объект, чтобы назначить текстуру');
        return false;
      }
      const appendLayer = (layers: MysteryRoomTextureLayer[] | undefined) => [
        ...(layers ?? []),
                createTextureLayerFromAsset(asset, layers?.length ?? 0, selected.type === 'customObject' ? 'contain' : 'repeat'),
      ];
      if (selected.type === 'room') {
        updateMap((map) => ({
          ...map,
          rooms: map.rooms.map((room) =>
            room.id === selected.id
              ? { ...room, textures: appendLayer(room.textures), floorTexture: room.floorTexture ?? createSurfaceTextureFromAsset(asset, 'repeat') }
              : room,
          ),
        }));
        setStatus(`Слой текстуры добавлен: ${asset.name}`);
        return true;
      }
      if (selected.type === 'wall') {
        const wallIndex = Number(selected.id);
        updateMap((map) => ({
          ...map,
          walls: map.walls.map((wall, index) =>
            index === wallIndex
              ? { ...wall, textures: appendLayer(wall.textures), texture: wall.texture ?? createSurfaceTextureFromAsset(asset, 'repeat') }
              : wall,
          ),
        }));
        setStatus(`Слой текстуры добавлен: ${asset.name}`);
        return true;
      }
      if (selected.type === 'door') {
        updateMap((map) => ({ ...map, doors: map.doors.map((door) => door.id === selected.id ? { ...door, textures: appendLayer(door.textures) } : door) }));
        setStatus(`Слой текстуры добавлен: ${asset.name}`);
        return true;
      }
      if (selected.type === 'light') {
        updateMap((map) => ({ ...map, lights: map.lights.map((light) => light.id === selected.id ? { ...light, textures: appendLayer(light.textures) } : light) }));
        setStatus(`Слой текстуры добавлен: ${asset.name}`);
        return true;
      }
      if (selected.type === 'interactable') {
        updateMap((map) => ({ ...map, interactables: map.interactables.map((entry) => entry.id === selected.id ? { ...entry, textures: appendLayer(entry.textures) } : entry) }));
        setStatus(`Слой текстуры добавлен: ${asset.name}`);
        return true;
      }
      if (selected.type === 'trigger') {
        updateMap((map) => ({ ...map, triggers: map.triggers.map((trigger) => trigger.id === selected.id ? { ...trigger, textures: appendLayer(trigger.textures) } : trigger) }));
        setStatus(`Слой текстуры добавлен: ${asset.name}`);
        return true;
      }
      if (selected.type === 'hazard') {
        updateMap((map) => ({ ...map, hazards: map.hazards.map((hazard) => hazard.id === selected.id ? { ...hazard, textures: appendLayer(hazard.textures) } : hazard) }));
        setStatus(`Слой текстуры добавлен: ${asset.name}`);
        return true;
      }
      updateMap((map) => ({
        ...map,
        customObjects: map.customObjects.map((object) =>
          object.id === selected.id
            ? {
                ...object,
                kind: object.kind === 'image' ? 'primitive' : object.kind,
                textures: appendLayer(object.textures),
                assetId: undefined,
                assetUrl: undefined,
                assetName: undefined,
                assetMimeType: undefined,
                assetFit: undefined,
                assetTransform: undefined,
                assetAlphaCollisionEnabled: false,
                visualSkinId: undefined,
                visualSkinName: undefined,
              }
            : object,
        ),
      }));
      setStatus(`Слой текстуры добавлен: ${asset.name}`);
      return true;
    },
    [selected, updateMap],
  );

  const applyAudioAssetToSelectedSoundSource = useCallback(
    (asset: MrMapAsset) => {
      if (!isMysteryRoomMapAudioAsset(asset)) {
        setStatus('Этот ассет не является аудио');
        return false;
      }
      if (selected?.type !== 'soundSource') {
        setStatus('Выберите источник звука на карте, чтобы назначить ему аудио');
        return false;
      }
      const soundId = `${asset.id}_sound`;
      updateMap((map) => ({
        ...map,
        soundDefinitions: [
          ...map.soundDefinitions.filter((sound) => sound.id !== soundId),
          {
            id: soundId,
            label: asset.name,
            kind: 'custom',
            assetId: asset.id,
            assetName: asset.name,
            assetMimeType: asset.type,
            url: asset.url,
          },
        ],
        soundSources: map.soundSources.map((source) =>
          source.id === selected.id ? { ...source, soundId, label: source.label || asset.name } : source,
        ),
      }));
      setStatus(`Аудио назначено источнику звука: ${asset.name}`);
      return true;
    },
    [selected, updateMap],
  );

  const uploadAsset = useCallback(
    async (file: File) => {
      if (!activeMap) {
        setStatus('Сначала создайте или откройте черновик');
        return;
      }
      if (!canEditActiveMap) {
        setStatus('Ассеты можно добавлять только в черновик');
        return;
      }
      const assetKind = classifyMysteryRoomMapAsset(file);
      if (!assetKind) {
        setStatus(`Формат не поддерживается. Изображения: ${MR_MAP_IMAGE_FORMAT_LABEL}. Аудио: ${MR_MAP_AUDIO_FORMAT_LABEL}.`);
        return;
      }
      let fileToUpload = file;
      if (assetKind === 'image') {
        setStatus(`Оптимизирую текстуру ${file.name}...`);
        try {
          const optimized = await optimizeImageForTextureUpload(file);
          if (optimized?.size > 0) {
            fileToUpload = optimized;
          }
        } catch {
          fileToUpload = file;
        }
      }
      if (activeMap.assetBytesTotal + fileToUpload.size > MR_MAP_ASSET_LIMIT_BYTES) {
        setStatus('Лимит ассетов карты 100 MB');
        return;
      }
      const pendingId = `asset_upload_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      const previewUrl = assetKind === 'image' ? URL.createObjectURL(fileToUpload) : undefined;
      if (previewUrl) {
        pendingAssetPreviewUrlsRef.current.add(previewUrl);
      }
      const pendingUpload: PendingMapAssetUpload = {
        id: pendingId,
        name: fileToUpload.name,
        type: fileToUpload.type || assetKind,
        size: fileToUpload.size,
        kind: assetKind,
        previewUrl,
      };
      setPendingAssetUploads((current) => [pendingUpload, ...current]);
      const compressionLabel =
        assetKind === 'image' && fileToUpload.size < file.size
          ? ` (${formatMapAssetBytes(file.size)} -> ${formatMapAssetBytes(fileToUpload.size)})`
          : '';
      setStatus(`Загружаю ${fileToUpload.name}${compressionLabel}...`);
      try {
        const response = await mrApiMapUploadAsset(activeMap.id, fileToUpload);
        const asset = extractUploadedMapAsset(response);
        if (!asset) {
          throw new Error('Сервер загрузил файл, но не вернул описание ассета');
        }
        const assetAlreadyKnown = activeMap.assets?.some((entry) => entry.id === asset.id) ?? false;
        const assetSizeDelta = assetAlreadyKnown ? 0 : asset.size;
        setActiveMap((current) =>
          current && current.id === activeMap.id
            ? {
                ...current,
                assetBytesTotal: current.assetBytesTotal + assetSizeDelta,
                assets: [asset, ...(current.assets ?? []).filter((entry) => entry.id !== asset.id)],
                updatedAt: asset.createdAt || current.updatedAt,
              }
            : current,
        );
        updateMapAssetSummary(activeMap.id, assetSizeDelta, asset.createdAt);
        void mrApiMapGet(activeMap.id)
          .then((fresh) => {
            setActiveMap((current) =>
              current && current.id === fresh.map.id
                ? {
                    ...current,
                    assets: fresh.map.assets ?? [],
                    assetBytesTotal: fresh.map.assetBytesTotal,
                    updatedAt: fresh.map.updatedAt,
                  }
                : current,
            );
            syncMapList(fresh.map);
          })
          .catch(() => undefined);
        const uploadedKind = classifyMysteryRoomMapAsset(asset) ?? assetKind;
        const applied =
          uploadedKind === 'image' && (selected?.type === 'customObject' || selected?.type === 'room' || selected?.type === 'wall')
            ? applyImageAssetToSelectedTarget(asset)
            : uploadedKind === 'audio' && selected?.type === 'soundSource'
              ? applyAudioAssetToSelectedSoundSource(asset)
              : false;
        if (!applied) {
          setStatus(
            uploadedKind === 'image'
              ? 'Изображение загружено. Выберите объект и нажмите "Назначить".'
              : 'Аудио загружено. Выберите источник звука и нажмите "Назначить".',
          );
        }
      } catch (error: any) {
        setStatus(mrApiErrorMessage(error, 'Не удалось загрузить ассет'));
      } finally {
        setPendingAssetUploads((current) => current.filter((entry) => entry.id !== pendingId));
        if (previewUrl) {
          pendingAssetPreviewUrlsRef.current.delete(previewUrl);
          URL.revokeObjectURL(previewUrl);
        }
      }
    },
    [activeMap, canEditActiveMap, applyAudioAssetToSelectedSoundSource, applyImageAssetToSelectedTarget, selected, syncMapList, updateMapAssetSummary],
  );

  const uploadAssets = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;
      if (!activeMap) {
        setStatus('Сначала создайте или откройте черновик');
        return;
      }
      for (const file of files) {
        await uploadAsset(file);
      }
    },
    [activeMap, uploadAsset],
  );

  const deleteAsset = useCallback(
    async (asset: MrMapAsset) => {
      if (!activeMap) return;
      setStatus('Удаляю ассет...');
      try {
        await mrApiMapDeleteAsset(activeMap.id, asset.id);
        setActiveMap((current) =>
          current
            ? {
                ...current,
                assetBytesTotal: Math.max(0, current.assetBytesTotal - asset.size),
                assets: current.assets.filter((entry) => entry.id !== asset.id),
                updatedAt: new Date().toISOString(),
              }
            : current,
        );
        updateMapAssetSummary(activeMap.id, -asset.size);
        updateMap(
          (map) => ({
            ...map,
            rooms: map.rooms.map((room) =>
              room.floorTexture?.assetId === asset.id || room.textures?.some((texture) => texture.assetId === asset.id)
                ? {
                    ...room,
                    floorTexture: room.floorTexture?.assetId === asset.id ? undefined : room.floorTexture,
                    textures: room.textures?.filter((texture) => texture.assetId !== asset.id),
                  }
                : room,
            ),
            walls: map.walls.map((wall) =>
              wall.texture?.assetId === asset.id || wall.textures?.some((texture) => texture.assetId === asset.id)
                ? {
                    ...wall,
                    texture: wall.texture?.assetId === asset.id ? undefined : wall.texture,
                    textures: wall.textures?.filter((texture) => texture.assetId !== asset.id),
                  }
                : wall,
            ),
            doors: map.doors.map((door) =>
              door.textures?.some((texture) => texture.assetId === asset.id)
                ? { ...door, textures: door.textures.filter((texture) => texture.assetId !== asset.id) }
                : door,
            ),
            lights: map.lights.map((light) =>
              light.textures?.some((texture) => texture.assetId === asset.id)
                ? { ...light, textures: light.textures.filter((texture) => texture.assetId !== asset.id) }
                : light,
            ),
            interactables: map.interactables.map((entry) =>
              entry.textures?.some((texture) => texture.assetId === asset.id) || entry.inventoryIconAssetId === asset.id
                ? {
                    ...entry,
                    textures: entry.textures?.filter((texture) => texture.assetId !== asset.id),
                    inventoryIconAssetId: entry.inventoryIconAssetId === asset.id ? undefined : entry.inventoryIconAssetId,
                    inventoryIconAssetUrl: entry.inventoryIconAssetId === asset.id ? undefined : entry.inventoryIconAssetUrl,
                    inventoryIconAssetName: entry.inventoryIconAssetId === asset.id ? undefined : entry.inventoryIconAssetName,
                    inventoryIconAssetMimeType: entry.inventoryIconAssetId === asset.id ? undefined : entry.inventoryIconAssetMimeType,
                  }
                : entry,
            ),
            triggers: map.triggers.map((trigger) =>
              trigger.textures?.some((texture) => texture.assetId === asset.id)
                ? { ...trigger, textures: trigger.textures.filter((texture) => texture.assetId !== asset.id) }
                : trigger,
            ),
            hazards: map.hazards.map((hazard) =>
              hazard.textures?.some((texture) => texture.assetId === asset.id)
                ? { ...hazard, textures: hazard.textures.filter((texture) => texture.assetId !== asset.id) }
                : hazard,
            ),
            customObjects: map.customObjects.map((object) =>
              object.assetId === asset.id ||
              object.inventoryIconAssetId === asset.id ||
              object.textures?.some((texture) => texture.assetId === asset.id)
                ? {
                    ...object,
                    textures: object.textures?.filter((texture) => texture.assetId !== asset.id),
                    assetId: object.assetId === asset.id ? undefined : object.assetId,
                    assetUrl: object.assetId === asset.id ? undefined : object.assetUrl,
                    assetName: object.assetId === asset.id ? undefined : object.assetName,
                    assetMimeType: object.assetId === asset.id ? undefined : object.assetMimeType,
                    assetFit: object.assetId === asset.id ? undefined : object.assetFit,
                    assetTransform: object.assetId === asset.id ? undefined : object.assetTransform,
                    assetAlphaCollisionEnabled: object.assetId === asset.id ? false : object.assetAlphaCollisionEnabled,
                    inventoryIconAssetId: object.inventoryIconAssetId === asset.id ? undefined : object.inventoryIconAssetId,
                    inventoryIconAssetUrl: object.inventoryIconAssetId === asset.id ? undefined : object.inventoryIconAssetUrl,
                    inventoryIconAssetName: object.inventoryIconAssetId === asset.id ? undefined : object.inventoryIconAssetName,
                    inventoryIconAssetMimeType: object.inventoryIconAssetId === asset.id ? undefined : object.inventoryIconAssetMimeType,
                    kind: object.kind === 'image' ? 'primitive' : object.kind,
                  }
                : object,
            ),
            soundDefinitions: map.soundDefinitions.map((sound) =>
              sound.assetId === asset.id
                ? { ...sound, assetId: undefined, assetName: undefined, assetMimeType: undefined, url: undefined }
                : sound,
            ),
          }),
          { recordHistory: false },
        );
        setStatus('Ассет удален');
      } catch (error: any) {
        setStatus(mrApiErrorMessage(error, 'Не удалось удалить ассет'));
      }
    },
    [activeMap, updateMap, updateMapAssetSummary],
  );

  const editorRenderItems = useMemo<EditorRenderItem[]>(() => {
    const items: EditorRenderItem[] = [];
    const readLayer = (value: number | undefined, fallback: number) => clampEditorLayer(value ?? fallback);
    if (showLayers.walls) {
      draftMap.walls.forEach((wall, index) => {
        items.push({
          key: `wall:${index}`,
          layer: readLayer(wall.layer, 1),
          order: 100 + index,
          node: (
            <WallGlyph
              key={`wall:${index}`}
              wall={wall}
              selected={isSelected(selected, 'wall', String(index), multiSelected)}
              className={tool === 'wall' ? '' : 'cursor-move'}
              onPointerDown={(event) => handleEntityPointerDown(event, { type: 'wall', id: String(index) })}
            />
          ),
        });
      });
    }
    if (showLayers.doors) {
      draftMap.doors.forEach((door, index) => {
        items.push({
          key: `door:${door.id}`,
          layer: readLayer(door.layer, 2),
          order: 200 + index,
          node: (
            <DoorGlyph
              key={door.id}
              door={door}
              selected={isSelected(selected, 'door', door.id, multiSelected)}
              onPointerDown={(event) => handleEntityPointerDown(event, { type: 'door', id: door.id })}
            />
          ),
        });
      });
    }
    if (showLayers.objects) {
      draftMap.customObjects.forEach((object, index) => {
        const layer = readLayer(object.layer, object.kind === 'badge' || object.kind === 'weapon' ? 5 : 2);
        items.push({
          key: `object:${object.id}`,
          layer,
          order: 300 + index,
          node: (
            <CustomObjectGlyph
              key={object.id}
              object={object}
              selected={isSelected(selected, 'customObject', object.id, multiSelected)}
              onPointerDown={(event) => handleEntityPointerDown(event, { type: 'customObject', id: object.id })}
            />
          ),
        });
        if (showLayers.collisions && object.collision.kind === 'rect') {
          items.push({
            key: `object:${object.id}:collision`,
            layer: layer + 0.04,
            order: 340 + index,
            node: <rect key={`${object.id}:collision`} x={object.collision.rect.x} y={object.collision.rect.y} width={object.collision.rect.width} height={object.collision.rect.height} fill="none" stroke="#fb7185" strokeWidth="4" strokeDasharray="12 8" pointerEvents="none" />,
          });
        } else if (showLayers.collisions && object.collision.kind === 'circle') {
          items.push({
            key: `object:${object.id}:collision`,
            layer: layer + 0.04,
            order: 340 + index,
            node: <circle key={`${object.id}:collision`} cx={object.collision.center.x} cy={object.collision.center.y} r={object.collision.radius} fill="none" stroke="#fb7185" strokeWidth="4" strokeDasharray="12 8" pointerEvents="none" />,
          });
        } else if (showLayers.collisions && object.collision.kind === 'polygon') {
          items.push({
            key: `object:${object.id}:collision`,
            layer: layer + 0.04,
            order: 340 + index,
            node: (
              <polygon
                key={`${object.id}:collision`}
                points={object.collision.points.map((point) => `${point.x},${point.y}`).join(' ')}
                fill="none"
                stroke="#fb7185"
                strokeWidth="4"
                strokeDasharray="12 8"
                pointerEvents="none"
              />
            ),
          });
        }
      });
    }
    if (showLayers.lights) {
      draftMap.interactables.forEach((entry, index) => {
        items.push({
          key: `interactable:${entry.id}`,
          layer: readLayer(entry.layer, 7),
          order: 500 + index,
          node: (
            <InteractableGlyph
              key={entry.id}
              entry={entry}
              selected={isSelected(selected, 'interactable', entry.id, multiSelected)}
              switchEnabled={isEditorLightSwitchEnabled(draftMap, entry)}
              onPointerDown={(event) => handleEntityPointerDown(event, { type: 'interactable', id: entry.id })}
            />
          ),
        });
      });
      draftMap.lights.forEach((light, index) => {
        items.push({
          key: `light:${light.id}`,
          layer: readLayer(light.layer, 20),
          order: 600 + index,
          node: (
            <LightGlyph
              key={light.id}
              light={light}
              selected={isSelected(selected, 'light', light.id, multiSelected)}
              onPointerDown={(event) => handleEntityPointerDown(event, { type: 'light', id: light.id })}
            />
          ),
        });
      });
    }
    if (showLayers.triggers) {
      draftMap.triggers.forEach((trigger, index) => {
        items.push({
          key: `trigger:${trigger.id}`,
          layer: readLayer(trigger.layer, 8),
          order: 700 + index,
          node: (
            <TriggerGlyph
              key={trigger.id}
              trigger={trigger}
              selected={isSelected(selected, 'trigger', trigger.id, multiSelected)}
              onPointerDown={(event) => handleEntityPointerDown(event, { type: 'trigger', id: trigger.id })}
            />
          ),
        });
      });
    }
    if (showLayers.effects) {
      draftMap.hazards.forEach((hazard, index) => {
        items.push({
          key: `hazard:${hazard.id}`,
          layer: 8.5,
          order: 760 + index,
          node: (
            <HazardGlyph
              key={hazard.id}
              hazard={hazard}
              selected={isSelected(selected, 'hazard', hazard.id, multiSelected)}
              onPointerDown={(event) => handleEntityPointerDown(event, { type: 'hazard', id: hazard.id })}
            />
          ),
        });
      });
    }
    if (showLayers.sounds) {
      draftMap.soundSources.forEach((source, index) => {
        items.push({
          key: `sound:${source.id}`,
          layer: readLayer(source.layer, 9),
          order: 800 + index,
          node: (
            <SoundSourceGlyph
              key={source.id}
              source={source}
              selected={isSelected(selected, 'soundSource', source.id, multiSelected)}
              onPointerDown={(event) => handleEntityPointerDown(event, { type: 'soundSource', id: source.id })}
            />
          ),
        });
      });
    }
    if (showLayers.anchors) {
      draftMap.incidentAnchors.forEach((anchor, index) => {
        items.push({
          key: `incident:${anchor.id}`,
          layer: 10,
          order: 860 + index,
          node: (
            <IncidentAnchorGlyph
              key={anchor.id}
              anchor={anchor}
              selected={isSelected(selected, 'incidentAnchor', anchor.id, multiSelected)}
              onPointerDown={(event) => handleEntityPointerDown(event, { type: 'incidentAnchor', id: anchor.id })}
            />
          ),
        });
      });
      draftMap.workAnchors.forEach((anchor, index) => {
        items.push({
          key: `work:${anchor.id}`,
          layer: 10,
          order: 890 + index,
          node: (
            <WorkAnchorGlyph
              key={anchor.id}
              anchor={anchor}
              selected={isSelected(selected, 'workAnchor', anchor.id, multiSelected)}
              onPointerDown={(event) => handleEntityPointerDown(event, { type: 'workAnchor', id: anchor.id })}
            />
          ),
        });
      });
    }
    if (showLayers.nav) {
      draftMap.navNodes.forEach((node, index) => {
        items.push({
          key: `nav:${node.id}`,
          layer: 21,
          order: 900 + index,
          node: (
            <NavNodeGlyph
              key={node.id}
              node={node}
              selected={isSelected(selected, 'navNode', node.id, multiSelected)}
              onPointerDown={(event) => handleEntityPointerDown(event, { type: 'navNode', id: node.id })}
            />
          ),
        });
      });
    }
    if (showLayers.actors) {
      normalizeEditorSpawnSlots(draftMap).forEach((slot, index) => {
        if (!editorSpawnSlotMatchesLayerFilter(slot, spawnLayerFilter)) return;
        items.push({
          key: `spawn:${index}`,
          layer: 21,
          order: 1000 + index,
          node: (
            <PlayerSpawnGlyph
              key={`spawn:${index}`}
              slot={slot}
              index={index}
              selected={isSelected(selected, 'spawn', String(index), multiSelected)}
              onPointerDown={(event) => handleEntityPointerDown(event, { type: 'spawn', id: String(index) })}
            />
          ),
        });
      });
      draftMap.npcs.forEach((npc, index) => {
        items.push({
          key: `npc:${npc.id}`,
          layer: 21,
          order: 1100 + index,
          node: (
            <ActorGlyph
              key={npc.id}
              npc={npc}
              selected={isSelected(selected, 'npc', npc.id, multiSelected)}
              onPointerDown={(event) => handleEntityPointerDown(event, { type: 'npc', id: npc.id })}
            />
          ),
        });
      });
    }
    return items.sort((left, right) => left.layer - right.layer || left.order - right.order);
  }, [draftMap, handleEntityPointerDown, multiSelected, selected, showLayers, spawnLayerFilter, tool]);

  const linkBadges = useMemo(() => buildEditorLinkBadges(draftMap, selected), [draftMap, selected]);
  const selectionGroupBounds = useMemo(
    () => (selectedCount > 1 ? getEditorSelectionsBounds(draftMap, activeSelections) : null),
    [activeSelections, draftMap, selectedCount],
  );
  const selectionBoxRect = dragState?.kind === 'select-box'
    ? selectionRectFromPoints(dragState.start, dragState.current)
    : null;

  if (authRequired) {
    return (
      <section className="flex min-h-0 flex-1 items-center justify-center p-6">
        <div className="max-w-xl rounded-[24px] border border-site-warning/20 bg-site-warning/10 p-6 text-site-warning">
          <div className="text-sm uppercase tracking-[0.22em] text-site-warning/80">Mystery Room Editor</div>
          <h2 className="mt-2 text-2xl font-black text-site-text">Нужна авторизация</h2>
          <p className="mt-3 text-sm leading-6 text-site-warning/90">
            Создание и публикация лабораторий доступны только авторизованным пользователям. Дефолтная LISAD-9 остается доступной в игре.
          </p>
        </div>
      </section>
    );
  }

  if (!activeMap) {
    return (
      <MapStartScreen
        query={query}
        setQuery={setQuery}
        drafts={filteredDraftMaps}
        templates={filteredTemplates}
        publishedMaps={filteredPublishedMaps}
        publishNotice={publishNotice}
        status={status}
        onRefresh={loadMine}
        onOpenDraft={openMap}
        onCreateFromTemplate={createDraftFromTemplate}
        onPublishDraft={publishMapFromList}
        onDuplicateMap={duplicateMapFromList}
        onRenameMap={renameMapFromList}
        onDeleteDraft={deleteDraftFromList}
      />
    );
  }

  const draggedTool = paletteDrag ? toolDefinitionById(paletteDrag.tool) : null;
  const activeAssets = activeMap.assets ?? [];
  const hasAssetEntries = activeAssets.length > 0 || pendingAssetUploads.length > 0;

  return (
    <>
      {paletteDrag && draggedTool && (
        <div
          className="pointer-events-none fixed z-[90] grid grid-cols-[32px_minmax(0,1fr)] items-center gap-2 rounded-[12px] border border-site-accent/45 bg-site-bg/95 px-3 py-2 text-sm font-black text-site-text shadow-2xl shadow-cyan-950/40"
          style={{ left: paletteDrag.clientX + 14, top: paletteDrag.clientY + 14 }}
        >
          <span className="grid size-8 place-items-center rounded-[9px] bg-site-accent text-site-on-accent">{draggedTool.icon}</span>
          <span className="max-w-[170px] truncate">{draggedTool.label}</span>
        </div>
      )}
      {contextMenu && (
        <div
          className="fixed z-[120] min-w-[220px] max-w-[280px] overflow-hidden rounded-[14px] border border-site-border bg-site-bg/98 p-1.5 text-sm text-site-text shadow-2xl shadow-black/55 backdrop-blur-xl"
          style={{ left: contextMenu.clientX, top: contextMenu.clientY }}
          onClick={(event) => event.stopPropagation()}
          onContextMenu={(event) => event.preventDefault()}
        >
          {contextMenu.kind === 'object' ? (
            <>
              <div className="border-b border-site-border px-3 py-2">
                <div className="text-[0.65rem] font-black uppercase tracking-[0.16em] text-site-accent/80">Объект</div>
                <div className="mt-1 truncate text-xs text-site-muted">{selectedEntityLabel(draftMap, contextMenu.selection)}</div>
              </div>
              <button
                type="button"
                onClick={() => {
                  copySelectedToClipboard();
                  setContextMenu(null);
                }}
                disabled={!canEditActiveMap}
                className="mt-1 flex w-full items-center justify-between rounded-[10px] px-3 py-2 text-left text-site-text transition enabled:hover:bg-site-text/10 disabled:text-site-muted"
              >
                <span>Копировать</span>
                <span className="text-[0.65rem] text-site-muted">Ctrl+C</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  deleteSelected();
                  setContextMenu(null);
                }}
                disabled={!canEditActiveMap}
                className="flex w-full items-center justify-between rounded-[10px] px-3 py-2 text-left text-site-danger transition enabled:hover:bg-site-danger/12 disabled:text-site-muted"
              >
                <span>Удалить</span>
                <span className="text-[0.65rem] text-site-danger/55">Del</span>
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={pasteClipboardFromContextMenu}
                disabled={clipboardCount === 0 || !canEditActiveMap}
                className="flex w-full items-center justify-between rounded-[10px] px-3 py-2 text-left text-site-warning transition enabled:hover:bg-site-warning/12 disabled:text-site-muted"
              >
                <span>Вставить</span>
                <span className="text-[0.65rem] text-site-warning/55">Ctrl+V</span>
              </button>
              <div className="mt-1 border-t border-site-border pt-1">
                <div className="px-3 py-1.5 text-[0.65rem] font-black uppercase tracking-[0.16em] text-site-accent/80">Создать</div>
                {CONTEXT_CREATE_GROUPS.map((group) => (
                  <div key={group.label} className="mt-1">
                    <div className="px-3 py-1 text-[0.68rem] font-semibold uppercase tracking-[0.12em] text-site-muted">{group.label}</div>
                    {group.tools.map((entryTool) => {
                      const definition = toolDefinitionById(entryTool);
                      return (
                        <button
                          key={entryTool}
                          type="button"
                          onClick={() => createEntityFromContextMenu(entryTool)}
                          disabled={!canEditActiveMap || !contextMenu.point}
                          className="grid w-full grid-cols-[24px_minmax(0,1fr)] items-center gap-2 rounded-[10px] px-3 py-1.5 text-left text-site-text transition enabled:hover:bg-site-text/10 disabled:text-site-muted"
                        >
                          <span className="grid size-6 place-items-center rounded-[7px] bg-site-text/8 text-xs">{definition?.icon ?? '+'}</span>
                          <span className="truncate">{definition?.label ?? entryTool}</span>
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
      <section className="grid min-h-0 flex-1 grid-cols-[300px_minmax(0,1fr)_420px] gap-3 overflow-hidden p-3">
      <aside className="flex min-h-0 flex-col gap-3 overflow-y-auto rounded-[20px] border border-site-border bg-site-bg/88 p-4">
        <div>
          <div className="text-[0.68rem] uppercase tracking-[0.22em] text-site-accent/80">Редактор карт</div>
          <h2 className="mt-2 text-2xl font-black text-site-text">Инструменты</h2>
          <p className="mt-2 text-sm leading-6 text-site-secondary">Выберите инструмент и размещайте элементы на полотне. Средняя кнопка мыши: перемещение полотна. Колесо мыши: масштабирование.</p>
        </div>

        <div className="rounded-[16px] border border-site-accent/20 bg-site-accent/10 px-3 py-3">
          <div className="text-xs uppercase tracking-[0.18em] text-site-accent/80">Черновик</div>
          <div className="mt-1 truncate text-sm font-semibold text-site-text">{activeMap.title}</div>
          <div className="mt-1 text-xs text-site-muted">{saveLabel}</div>
        </div>

        <input
          value={toolQuery}
          onChange={(event) => setToolQuery(event.target.value)}
          placeholder="Фильтр инструментов"
          className="rounded-[14px] border border-site-border bg-site-bg/80 px-3 py-2 text-sm text-site-text outline-none placeholder:text-site-muted focus:border-site-accent/40"
        />

        <div className="space-y-3">
          {filteredToolGroups.map(({ group, tools }) => (
            <div key={group} className="rounded-[16px] border border-site-border bg-site-surface/[0.03] p-2">
              <div className="px-2 pb-2 text-[0.62rem] uppercase tracking-[0.18em] text-site-muted">{TOOL_GROUP_LABELS[group]}</div>
              <div className="grid gap-2">
                {tools.map((entry) => (
                  <button
                    key={entry.id}
                    type="button"
                    title={`${entry.title} (${entry.shortcut})`}
                    aria-grabbed={paletteDrag?.tool === entry.id}
                    onPointerDown={(event) => handlePalettePointerDown(event, entry.id)}
                    onPointerMove={handlePalettePointerMove}
                    onPointerUp={handlePalettePointerUp}
                    onPointerCancel={handlePalettePointerCancel}
                    onClick={(event) => {
                      if (suppressPaletteClickRef.current) {
                        suppressPaletteClickRef.current = false;
                        event.preventDefault();
                        return;
                      }
                      setTool(entry.id);
                    }}
                    className={`grid grid-cols-[36px_minmax(0,1fr)_28px] items-center gap-2 rounded-[12px] border px-2 py-2 text-left transition ${
                      tool === entry.id ? 'border-site-accent/70 bg-site-accent text-site-on-accent' : 'border-site-border bg-site-bg/50 text-site-text hover:border-site-border hover:bg-site-surface/[0.06]'
                    }`}
                  >
                    <span className={`grid size-9 place-items-center rounded-[10px] text-lg ${tool === entry.id ? 'bg-site-bg text-site-accent' : 'bg-site-surface/[0.06] text-site-accent'}`}>{entry.icon}</span>
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-black">{entry.label}</span>
                      <span className={`mt-0.5 block truncate text-xs ${tool === entry.id ? 'text-site-muted/78' : 'text-site-muted'}`}>{entry.title}</span>
                    </span>
                    <span className={`rounded-full px-2 py-1 text-center text-[0.62rem] font-black ${tool === entry.id ? 'bg-site-bg text-site-accent' : 'border border-site-border text-site-muted'}`}>{entry.shortcut}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
          {filteredToolGroups.length === 0 && <div className="rounded-[16px] border border-dashed border-site-border p-4 text-sm text-site-muted">Инструменты не найдены.</div>}
        </div>

        <div className="mt-auto grid gap-2">
          <button type="button" onClick={createDraft} className="rounded-full border border-site-accent/30 bg-site-accent/10 px-4 py-2 text-sm font-semibold text-site-accent transition hover:border-site-accent/60">
            Новый пустой черновик
          </button>
          <button type="button" onClick={() => void loadMine()} className="rounded-full border border-site-border bg-site-surface/[0.04] px-4 py-2 text-sm text-site-text transition hover:border-site-border">
            Обновить черновики
          </button>
        </div>
      </aside>

      <main className="flex min-h-0 flex-col overflow-hidden rounded-[20px] border border-site-border bg-site-bg/82">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-site-border px-4 py-3">
          <div className="min-w-0">
            <div className="text-[0.62rem] uppercase tracking-[0.2em] text-site-muted">Canvas</div>
            <div className="mt-1 truncate text-lg font-semibold text-site-text">{title}</div>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <div className="flex items-center gap-1 rounded-full border border-site-border bg-site-surface/[0.04] p-1" aria-label="История действий">
              <button
                type="button"
                onClick={undoEditorAction}
                disabled={!canUndo}
                title="Отменить действие (Ctrl+Z)"
                aria-label="Отменить действие"
                className="grid size-8 place-items-center rounded-full text-base font-black text-site-text transition enabled:hover:bg-site-text/10 disabled:text-site-muted"
              >
                ↶
              </button>
              <button
                type="button"
                onClick={redoEditorAction}
                disabled={!canRedo}
                title="Вернуть действие (Ctrl+Y)"
                aria-label="Вернуть действие"
                className="grid size-8 place-items-center rounded-full text-base font-black text-site-text transition enabled:hover:bg-site-text/10 disabled:text-site-muted"
              >
                ↷
              </button>
            </div>
            {activeMap && onPlayMap && (
              <button
                type="button"
                onClick={() => void playActiveMap()}
                className="rounded-full bg-site-warning px-4 py-2 text-xs font-black uppercase tracking-[0.12em] text-site-on-accent shadow-[0_0_24px_rgba(252,211,77,0.28)] transition hover:bg-site-warning"
              >
                Играть
              </button>
            )}
            <button
              type="button"
              onClick={() => void returnToStart()}
              className="rounded-full border border-site-accent/30 bg-site-accent/10 px-4 py-2 text-xs font-black uppercase tracking-[0.1em] text-site-accent transition hover:border-site-accent/60"
            >
              Выбрать карту
            </button>
            <span className="rounded-full border border-site-border bg-site-surface/[0.04] px-3 py-2 text-xs font-semibold text-site-secondary">{saveLabel}</span>
            <span className="rounded-full border border-site-accent/25 bg-site-accent/10 px-3 py-2 text-xs font-semibold text-site-accent">
              {TOOL_DEFINITIONS.find((entry) => entry.id === tool)?.label ?? tool}
            </span>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 border-b border-site-border px-4 py-2 text-xs text-site-secondary">
          {Object.entries(showLayers).map(([key, enabled]) => (
            <label key={key} className="flex items-center gap-1 rounded-full border border-site-border bg-site-surface/[0.03] px-2 py-1">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(event) => setShowLayers((current) => ({ ...current, [key]: event.target.checked }))}
              />
              {key}
            </label>
          ))}
          <div className="ml-auto flex gap-2">
            <span className="rounded-full border border-site-border bg-site-surface/[0.03] px-3 py-1 text-site-muted">{viewScale}%</span>
            <button type="button" onClick={() => zoom(0.78)} className="rounded-full border border-site-border px-3 py-1 hover:border-site-border">+</button>
            <button type="button" onClick={() => zoom(1.28)} className="rounded-full border border-site-border px-3 py-1 hover:border-site-border">-</button>
            <button type="button" onClick={resetView} className="rounded-full border border-site-border px-3 py-1 hover:border-site-border">fit</button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-hidden">
          <svg
            ref={bindSvgRef}
            viewBox={`${viewBox.x} ${viewBox.y} ${viewBox.width} ${viewBox.height}`}
            className={`h-full w-full touch-none bg-[#050b1a] ${canvasCursorClass}`}
            style={canvasCursorStyle}
            aria-label="Средняя кнопка мыши: перемещение полотна. Колесо мыши: масштабирование."
            onPointerDown={handleCanvasPointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerLeave={handlePointerUp}
            onAuxClick={(event) => event.preventDefault()}
            onContextMenu={handleCanvasContextMenu}
          >
            <defs>
              <pattern id="mr-editor-grid" width="96" height="96" patternUnits="userSpaceOnUse">
                <path d="M 96 0 L 0 0 0 96" fill="none" stroke="rgba(148,163,184,0.18)" strokeWidth="1" />
              </pattern>
            </defs>
            <rect x={gridBounds.x} y={gridBounds.y} width={gridBounds.width} height={gridBounds.height} fill="url(#mr-editor-grid)" />
            <rect
              x={0}
              y={0}
              width={draftMap.width}
              height={draftMap.height}
              fill="none"
              stroke="rgba(103,232,249,0.34)"
              strokeWidth={Math.max(2, viewBox.width / 1800)}
              strokeDasharray={`${Math.max(28, viewBox.width / 140)} ${Math.max(18, viewBox.width / 220)}`}
              pointerEvents="none"
            />
            {showLayers.rooms &&
              draftMap.rooms.map((room) => (
                <g key={room.id} onPointerDown={(event) => handleEntityPointerDown(event, { type: 'room', id: room.id })}>
                  {room.floor.map((floor, index) => (
                    <RoomFloorSurface
                      key={`${room.id}:${index}`}
                      room={room}
                      floor={floor}
                      index={index}
                    selected={isSelected(selected, 'room', room.id, multiSelected)}
                    />
                  ))}
                  <text x={room.labelPoint.x} y={room.labelPoint.y} textAnchor="middle" fill="#f8fafc" fontSize="34" fontFamily="monospace">
                    {room.title}
                  </text>
                </g>
              ))}
            {editorRenderItems.map((item) => (
              <g key={item.key}>{item.node}</g>
            ))}
            {showLayers.walls ? (
              <g>
                {wallJointNodes.map((entry) => (
                  <WallJointGlyph key={wallEditorNodeKey(entry.node)} node={entry.node} walls={entry.walls} selected={entry.selected} />
                ))}
              </g>
            ) : null}
            {showLayers.walls && wallBuildDraft && wallPreviewPoint ? (
              <g pointerEvents="none">
                <line
                  x1={wallBuildDraft.anchor.x}
                  y1={wallBuildDraft.anchor.y}
                  x2={wallPreviewPoint.x}
                  y2={wallPreviewPoint.y}
                  stroke="#fbbf24"
                  strokeWidth={Math.max(5, viewBox.width / 900)}
                  strokeDasharray={`${Math.max(24, viewBox.width / 180)} ${Math.max(16, viewBox.width / 260)}`}
                  strokeLinecap="square"
                />
                <circle cx={wallBuildDraft.anchor.x} cy={wallBuildDraft.anchor.y} r={Math.max(9, viewBox.width / 360)} fill="#f59e0b" stroke="#fef3c7" strokeWidth={Math.max(3, viewBox.width / 1200)} />
              </g>
            ) : null}
            {showLayers.walls && tool === 'select' ? (
              <g>
                {wallNodes.map((node) => (
                  <circle
                    key={`${node.x}:${node.y}`}
                    cx={node.x}
                    cy={node.y}
                    r={Math.max(5, viewBox.width / 720)}
                    fill="#020617"
                    fillOpacity={0.92}
                    stroke="#67e8f9"
                    strokeWidth={Math.max(3, viewBox.width / 1200)}
                    className="cursor-grab"
                    onPointerDown={(event) => startWallNodeDrag(event, node)}
                  />
                ))}
              </g>
            ) : null}
            {linkBadges.map((badge) => (
              <LinkBadgeOverlay key={badge.key} map={draftMap} badge={badge} />
            ))}
            {selectionGroupBounds && (
              <rect
                x={selectionGroupBounds.x - 18}
                y={selectionGroupBounds.y - 18}
                width={selectionGroupBounds.width + 36}
                height={selectionGroupBounds.height + 36}
                fill="rgba(103,232,249,0.08)"
                stroke="#22d3ee"
                strokeWidth={Math.max(5, viewBox.width / 700)}
                strokeDasharray={`${Math.max(22, viewBox.width / 160)} ${Math.max(12, viewBox.width / 260)}`}
                pointerEvents="none"
              />
            )}
            {selectionBoxRect && (
              <rect
                x={selectionBoxRect.x}
                y={selectionBoxRect.y}
                width={Math.max(1, selectionBoxRect.width)}
                height={Math.max(1, selectionBoxRect.height)}
                fill="rgba(34,211,238,0.16)"
                stroke="#a5f3fc"
                strokeWidth={Math.max(4, viewBox.width / 850)}
                strokeDasharray={`${Math.max(18, viewBox.width / 180)} ${Math.max(10, viewBox.width / 300)}`}
                pointerEvents="none"
              />
            )}
            <SelectionTransformGizmo
              map={draftMap}
              selection={selectedCount <= 1 ? selected : null}
              viewBox={viewBox}
              viewportSize={svgViewportSize}
              activeHandle={dragState?.kind === 'transform' ? dragState.handle : null}
              onHandlePointerDown={startTransformDrag}
              onLayerChange={changeSelectionLayer}
            />
          </svg>
        </div>
      </main>

      <aside className="flex min-h-0 flex-col gap-2 overflow-y-auto rounded-[20px] border border-site-border bg-site-bg/88 p-3">
        <Panel title="Карта">
          <div className="space-y-2">
            <PropertyTextInput label="Имя" value={title} onChange={(value) => { setTitle(value); setDraftMap((current) => ({ ...current, title: value })); markDirty(); }} />
            <PropertyTextareaInput label="Описание" value={description} rows={2} onChange={(value) => { setDescription(value); markDirty(); }} />
            <PropertySection title="Режимы лобби">
              <p className="text-[0.65rem] leading-relaxed text-site-muted">
                По умолчанию карта во всех режимах. Снимите галочки с лишних — в «Создании лобби» карта появится только при подходящем режиме.
              </p>
              <div className="mt-2 flex flex-wrap gap-1.5" role="group" aria-label="Доступные режимы лобби для карты">
                {MYSTERY_ROOM_ALL_GAME_MODES.map((mode) => {
                  const limited = getMysteryRoomMapPlayableModes(draftMap);
                  const active = new Set<MysteryRoomGameMode>(limited ?? MYSTERY_ROOM_ALL_GAME_MODES);
                  const on = active.has(mode);
                  return (
                    <button
                      key={mode}
                      type="button"
                      aria-pressed={on}
                      disabled={!canEditActiveMap}
                      onClick={() => togglePlayableLobbyMode(mode)}
                      className={`rounded-lg px-2.5 py-1 text-[0.65rem] font-semibold border transition disabled:opacity-45 ${
                        on ? 'border-site-accent/50 bg-site-accent/15 text-site-accent' : 'border-site-border bg-site-surface/[0.04] text-site-muted'
                      }`}
                    >
                      {LOBBY_MODE_TOGGLE_LABEL[mode]}
                    </button>
                  );
                })}
              </div>
            </PropertySection>
            <PropertySection title="Состояние">
              <ReadonlyProperty label="Статус" value={activeMap?.status ?? 'local'} />
              <ReadonlyProperty label="Ассеты" value={`${formatMapAssetBytes(activeMap?.assetBytesTotal ?? 0)} / 100 MB`} />
              <ReadonlyProperty label="Сохранение" value={saveLabel} />
            </PropertySection>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => void saveDraft()} className="rounded-[10px] bg-site-accent px-3 py-2 text-xs font-black text-site-on-accent transition hover:bg-site-accent">Сохранить</button>
              {activeMap && onPlayMap && (
                <button type="button" onClick={() => void playActiveMap()} className="col-span-2 rounded-[10px] border border-site-warning/30 bg-site-warning/10 px-3 py-2 text-xs font-semibold text-site-warning transition hover:border-site-warning/60">
                  Тестировать карту
                </button>
              )}
            </div>
            <div className="rounded-[12px] border border-site-border bg-site-surface/[0.03] px-3 py-2 text-xs leading-5 text-site-secondary">{status}</div>
          </div>
        </Panel>

        <Panel title="Свойства">
          <Inspector
            map={draftMap}
            assets={activeMap?.assets ?? []}
            selection={selected}
            activeSelections={activeSelections}
            updateMap={updateMap}
            assetEditObjectId={assetEditObjectId}
            setAssetEditObjectId={setAssetEditObjectId}
            updateObjectAssetTransform={updateObjectAssetTransform}
            canEdit={canEditActiveMap}
            selectedCount={selectedCount}
            onDuplicateSelection={duplicateSelected}
            onCopySelection={copySelectedToClipboard}
          />
          <div className="mt-3 flex flex-wrap gap-2">
            <button type="button" onClick={duplicateSelected} disabled={!selected || selected.type === 'spawn' || !canEditActiveMap} className="rounded-[10px] border border-site-accent/20 bg-site-accent/10 px-3 py-1.5 text-xs text-site-accent transition enabled:hover:border-site-accent/50 disabled:opacity-45">
              Дублировать
            </button>
            <button type="button" onClick={copySelectedToClipboard} disabled={!selected || selected.type === 'spawn'} className="rounded-[10px] border border-site-border bg-site-surface/[0.04] px-3 py-1.5 text-xs text-site-text transition enabled:hover:border-site-border disabled:opacity-45">
              Копировать
            </button>
            <button type="button" onClick={pasteClipboardAtPointer} disabled={clipboardCount === 0 || !canEditActiveMap} className="rounded-[10px] border border-site-warning/20 bg-site-warning/10 px-3 py-1.5 text-xs text-site-warning transition enabled:hover:border-site-warning/50 disabled:opacity-45">
              Вставить у указателя
            </button>
            <button type="button" onClick={selectVisibleObjectsInViewport} disabled={!canEditActiveMap} className="rounded-[10px] border border-site-border bg-site-surface/[0.04] px-3 py-1.5 text-xs text-site-text transition enabled:hover:border-site-border disabled:opacity-45">
              Выделить видимое
            </button>
            <button type="button" onClick={deleteSelected} disabled={!selected || !canEditActiveMap} className="rounded-[10px] border border-site-danger/20 bg-site-danger/10 px-3 py-1.5 text-xs text-site-danger transition enabled:hover:border-site-danger/50 disabled:opacity-45">
              Удалить выбранное
            </button>
            <span className="rounded-[10px] border border-site-border bg-site-surface/[0.03] px-3 py-1.5 text-xs text-site-muted">
              Выбрано: {selectedCount} · Буфер: {clipboardCount}
            </span>
          </div>
        </Panel>

        <Panel title="Генерация ресурсов">
          <div className="space-y-2">
            <PropertyTextInput
              label="Seed"
              value={resourceGenerationSeed}
              onChange={setResourceGenerationSeed}
            />
            <div className="grid grid-cols-3 gap-2">
              <NumberInput
                label="Кусты"
                value={resourceGenerationCounts.bush}
                onChange={(bush) => setResourceGenerationCounts((current) => ({ ...current, bush: Math.max(0, Math.floor(bush)) }))}
              />
              <NumberInput
                label="Металл"
                value={resourceGenerationCounts.scrap}
                onChange={(scrap) => setResourceGenerationCounts((current) => ({ ...current, scrap: Math.max(0, Math.floor(scrap)) }))}
              />
              <NumberInput
                label="Камни"
                value={resourceGenerationCounts.rock}
                onChange={(rock) => setResourceGenerationCounts((current) => ({ ...current, rock: Math.max(0, Math.floor(rock)) }))}
              />
            </div>
            <button
              type="button"
              onClick={generateResourcesOnMap}
              disabled={!canEditActiveMap}
              className="w-full rounded-[10px] border border-site-success/24 bg-site-success/12 px-3 py-2 text-xs font-black uppercase tracking-[0.12em] text-site-success transition enabled:hover:border-site-success/55 disabled:opacity-45"
            >
              Сгенерировать
            </button>
          </div>
        </Panel>

        <Panel title="Ассеты карты">
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-xs font-semibold text-site-text">{activeAssets.length} файлов</div>
                <div className="mt-0.5 text-[0.68rem] text-site-muted">{formatMapAssetBytes(activeMap.assetBytesTotal)} / 100 MB</div>
              </div>
              <div className="grid grid-cols-2 rounded-full border border-site-border bg-site-bg/70 p-1 text-[0.68rem] font-black" aria-label="Вид ассетов">
                <button
                  type="button"
                  onClick={() => setAssetViewMode('grid')}
                  className={`rounded-full px-2.5 py-1 transition ${assetViewMode === 'grid' ? 'bg-site-accent text-site-on-accent' : 'text-site-muted hover:text-site-text'}`}
                  title="Плитка с предпросмотром"
                >
                  Плитка
                </button>
                <button
                  type="button"
                  onClick={() => setAssetViewMode('list')}
                  className={`rounded-full px-2.5 py-1 transition ${assetViewMode === 'list' ? 'bg-site-accent text-site-on-accent' : 'text-site-muted hover:text-site-text'}`}
                  title="Компактный список"
                >
                  Список
                </button>
              </div>
            </div>

            <label className="block rounded-[14px] border border-dashed border-site-border bg-site-surface/[0.03] px-3 py-3 text-center text-sm text-site-secondary transition hover:border-site-accent/40">
              <span className="block font-semibold text-site-text">Загрузить файлы карты</span>
              <span className="mt-1 block text-xs text-site-muted">
                PNG/GIF/WebP/JPG и MP3/WAV/OGG/Opus/M4A/AAC/WebM. Можно выбрать несколько файлов.
              </span>
              <input
                type="file"
                accept={MR_MAP_ASSET_ACCEPT}
                multiple
                className="hidden"
                onChange={(event) => {
                  const files = Array.from(event.target.files ?? []);
                  event.currentTarget.value = '';
                  if (files.length > 0) void uploadAssets(files);
                }}
              />
            </label>

            <div className={assetViewMode === 'grid' ? 'grid grid-cols-1 gap-2' : 'space-y-1.5'}>
              {pendingAssetUploads.map((upload) => (
                <div
                  key={upload.id}
                  className={
                    assetViewMode === 'grid'
                      ? 'rounded-[14px] border border-site-accent/18 bg-site-accent/[0.06] px-3 py-2'
                      : 'grid grid-cols-[42px_minmax(0,1fr)] items-center gap-2 rounded-[10px] border border-site-accent/18 bg-site-accent/[0.06] px-2 py-1.5'
                  }
                >
                  {assetViewMode === 'list' && (
                    <div className="grid size-10 place-items-center overflow-hidden rounded-[8px] border border-site-border bg-site-bg/70 text-xs font-black text-site-accent">
                      {upload.previewUrl ? <img src={upload.previewUrl} alt="" className="h-full w-full object-cover" /> : upload.kind === 'audio' ? 'AUD' : 'IMG'}
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate text-sm font-semibold text-site-text">{upload.name}</span>
                      <span className="shrink-0 text-[0.65rem] font-black uppercase tracking-[0.14em] text-site-accent">загрузка</span>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-site-muted">
                      <span>{upload.type || upload.kind}</span>
                      <span>·</span>
                      <span>{formatMapAssetBytes(upload.size)}</span>
                    </div>
                    {assetViewMode === 'grid' && upload.previewUrl ? (
                      <div className="mt-2 flex h-20 w-full items-center justify-center overflow-hidden rounded-[10px] border border-site-border bg-site-bg/80">
                        <img src={upload.previewUrl} alt="" className="max-h-full max-w-full object-contain" />
                      </div>
                    ) : null}
                  </div>
                </div>
              ))}

              {activeAssets.map((asset) => {
                const imageAsset = isMysteryRoomMapImageAsset(asset);
                const audioAsset = isMysteryRoomMapAudioAsset(asset);
                const previewUrl = mapAssetPreviewUrl(asset);
                const canAssignImage = Boolean(selected && isTextureAssignableSelection(selected));
                if (assetViewMode === 'list') {
                  return (
                    <div key={asset.id} className="grid grid-cols-[42px_minmax(0,1fr)_auto] items-center gap-2 rounded-[10px] border border-site-border bg-site-surface/[0.03] px-2 py-1.5">
                      <div
                        className="grid size-10 place-items-center overflow-hidden rounded-[8px] border border-site-border text-[0.62rem] font-black text-site-secondary"
                        style={imageAsset ? mapAssetCheckerBackground() : { backgroundColor: '#020617' }}
                      >
                        {imageAsset ? <img src={previewUrl} alt="" className="h-full w-full object-contain" /> : audioAsset ? 'AUD' : 'FILE'}
                      </div>
                      <div className="min-w-0">
                        <div className="truncate text-sm font-semibold text-site-text">{asset.name}</div>
                        <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[0.68rem] text-site-muted">
                          <span className="truncate">{asset.type || 'unknown'}</span>
                          <span>·</span>
                          <span>{formatMapAssetBytes(asset.size)}</span>
                          {asset.type === 'image/gif' ? <span className="text-fuchsia-200">GIF</span> : null}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        {imageAsset ? (
                          <button
                            type="button"
                            onClick={() => applyImageAssetToSelectedTarget(asset)}
                            disabled={!canAssignImage}
                            className="rounded-full border border-site-accent/20 bg-site-accent/10 px-2 py-1 text-[0.68rem] font-semibold text-site-accent transition enabled:hover:border-site-accent/50 disabled:opacity-45"
                          >
                            Назначить
                          </button>
                        ) : null}
                        {audioAsset ? (
                          <button
                            type="button"
                            onClick={() => applyAudioAssetToSelectedSoundSource(asset)}
                            disabled={selected?.type !== 'soundSource'}
                            className="rounded-full border border-site-success/20 bg-site-success/10 px-2 py-1 text-[0.68rem] font-semibold text-site-success transition enabled:hover:border-site-success/50 disabled:opacity-45"
                          >
                            Назначить
                          </button>
                        ) : null}
                        <button type="button" onClick={() => void deleteAsset(asset)} className="rounded-full px-2 py-1 text-[0.68rem] text-site-danger hover:bg-site-danger/10 hover:text-site-danger">Удалить</button>
                      </div>
                    </div>
                  );
                }
                return (
                  <div key={asset.id} className="rounded-[14px] border border-site-border bg-site-surface/[0.03] px-3 py-2">
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate text-sm font-semibold text-site-text">{asset.name}</span>
                      <button type="button" onClick={() => void deleteAsset(asset)} className="text-xs text-site-danger hover:text-site-danger">Удалить</button>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-site-muted">
                      <span>{asset.type || 'unknown'}</span>
                      <span>·</span>
                      <span>{formatMapAssetBytes(asset.size)}</span>
                      {asset.type === 'image/png' || asset.type === 'image/gif' || asset.type === 'image/webp' ? (
                        <span className="rounded-full border border-site-accent/15 bg-site-accent/10 px-2 py-0.5 text-site-accent">прозрачность</span>
                      ) : null}
                      {asset.type === 'image/gif' ? (
                        <span className="rounded-full border border-fuchsia-200/15 bg-fuchsia-200/10 px-2 py-0.5 text-fuchsia-100">анимация GIF</span>
                      ) : null}
                      {audioAsset && !asset.type.includes('wav') ? (
                        <span className="rounded-full border border-site-success/15 bg-site-success/10 px-2 py-0.5 text-site-success">экономный формат</span>
                      ) : null}
                    </div>
                    {imageAsset && (
                      <>
                        <div
                          className="mt-2 flex h-24 w-full items-center justify-center overflow-hidden rounded-[10px] border border-site-border"
                          style={mapAssetCheckerBackground()}
                        >
                          <img src={previewUrl} alt="" className="max-h-full max-w-full object-contain" />
                        </div>
                        <button
                          type="button"
                          onClick={() => applyImageAssetToSelectedTarget(asset)}
                          disabled={!canAssignImage}
                          className="mt-2 w-full rounded-[10px] border border-site-accent/20 bg-site-accent/10 px-3 py-1.5 text-xs font-semibold text-site-accent transition enabled:hover:border-site-accent/50 disabled:opacity-45"
                        >
                          Назначить выбранному
                        </button>
                      </>
                    )}
                    {audioAsset && (
                      <>
                        <audio src={asset.url} controls preload="metadata" className="mt-2 w-full" />
                        <button
                          type="button"
                          onClick={() => applyAudioAssetToSelectedSoundSource(asset)}
                          disabled={selected?.type !== 'soundSource'}
                          className="mt-2 w-full rounded-[10px] border border-site-success/20 bg-site-success/10 px-3 py-1.5 text-xs font-semibold text-site-success transition enabled:hover:border-site-success/50 disabled:opacity-45"
                        >
                          Назначить выбранному источнику звука
                        </button>
                      </>
                    )}
                  </div>
                );
              })}
              {!hasAssetEntries && <div className="text-sm text-site-muted">Ассеты появятся после загрузки в черновик.</div>}
            </div>
          </div>
        </Panel>

        <Panel title="Проверка">
          <div className="space-y-2 text-sm">
            {validationIssues.length > 0 ? (
              <div className="rounded-[12px] border border-site-warning/20 bg-site-warning/10 px-3 py-2 text-site-warning">
                <div className="text-xs font-black uppercase tracking-[0.16em] text-site-warning/80">Перед публикацией</div>
                <ul className="mt-2 list-disc space-y-1 pl-5 leading-5">
                  {validationIssues.map((issue) => (
                    <li key={issue}>{formatMysteryRoomPublishIssue(issue)}</li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="rounded-[12px] border border-site-success/20 bg-site-success/10 px-3 py-2 text-site-success">Локальная проверка без ошибок.</div>
            )}
          </div>
        </Panel>

      </aside>
      </section>
    </>
  );
}

function MapStartScreen({
  query,
  setQuery,
  drafts,
  templates,
  publishedMaps,
  publishNotice,
  status,
  onRefresh,
  onOpenDraft,
  onCreateFromTemplate,
  onPublishDraft,
  onDuplicateMap,
  onRenameMap,
  onDeleteDraft,
}: {
  query: string;
  setQuery: (value: string) => void;
  drafts: MrMapSummary[];
  templates: MysteryRoomEditorTemplate[];
  publishedMaps: MrMapSummary[];
  publishNotice: PublishNotice | null;
  status: string;
  onRefresh: () => Promise<void>;
  onOpenDraft: (mapId: string) => Promise<void>;
  onCreateFromTemplate: (template: MysteryRoomEditorTemplate) => Promise<void>;
  onPublishDraft: (mapId: string) => Promise<void>;
  onDuplicateMap: (mapId: string) => Promise<void>;
  onRenameMap: (map: MrMapSummary) => Promise<void>;
  onDeleteDraft: (map: MrMapSummary) => Promise<void>;
}) {
  const blankTemplate = templates.find((template) => template.id === 'blank');
  const mapTemplates = templates.filter((template) => template.id !== 'blank');

  return (
    <section className="min-h-0 flex-1 overflow-y-auto p-4">
      <div className="mx-auto grid w-full max-w-[1700px] gap-4 xl:grid-cols-[360px_minmax(0,1fr)]">
        <aside className="rounded-[22px] border border-site-border bg-site-bg/88 p-5">
          <div className="text-[0.68rem] uppercase tracking-[0.22em] text-site-accent/80">Mystery Room Editor</div>
          <h2 className="mt-2 text-3xl font-black text-site-text">Создание карты</h2>
          <p className="mt-3 text-sm leading-6 text-site-secondary">
            Выберите пустую карту, существующий черновик или шаблон. Пустая карта и шаблон сразу создают серверный черновик с автосохранением.
          </p>
          <div className="mt-5 grid grid-cols-3 gap-2 text-xs">
            <Metric label="Черновики" value={drafts.length} />
            <Metric label="Шаблоны" value={templates.length} />
            <Metric label="Published" value={publishedMaps.length} />
          </div>
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Поиск по черновикам и шаблонам"
            className="mt-5 w-full rounded-[14px] border border-site-border bg-site-bg/80 px-3 py-2 text-sm text-site-text outline-none placeholder:text-site-muted focus:border-site-accent/40"
          />
          <button
            type="button"
            onClick={() => void onRefresh()}
            className="mt-3 rounded-full border border-site-border bg-site-surface/[0.04] px-4 py-2 text-sm font-semibold text-site-text transition hover:border-site-border"
          >
            Обновить список
          </button>
          <div className="mt-4 rounded-[14px] border border-site-border bg-site-surface/[0.03] px-3 py-2 text-xs leading-5 text-site-secondary">{status}</div>
          <div className="mt-4 rounded-[14px] border border-site-accent/18 bg-site-accent/8 px-3 py-2 text-xs leading-5 text-site-accent">
            Средняя кнопка мыши: перемещение полотна. Колесо мыши: масштабирование.
          </div>
        </aside>

        <div className="grid gap-4">
          {blankTemplate && (
            <section className="rounded-[22px] border border-site-accent/18 bg-site-bg/82 p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="text-[0.64rem] uppercase tracking-[0.2em] text-site-accent/80">Пустая карта</div>
                  <h3 className="mt-1 text-xl font-black text-site-text">{blankTemplate.title}</h3>
                  <p className="mt-2 max-w-2xl text-sm leading-6 text-site-secondary">{blankTemplate.description}</p>
                </div>
                <button
                  type="button"
                  onClick={() => void onCreateFromTemplate(blankTemplate)}
                  className="rounded-full bg-site-accent px-4 py-2 text-sm font-black text-site-on-accent transition hover:bg-site-accent"
                >
                  Создать черновик
                </button>
              </div>
            </section>
          )}

          {publishNotice && (
            <section className="rounded-[22px] border border-site-warning/24 bg-site-warning/10 p-4 text-site-warning">
              <div className="text-[0.64rem] font-black uppercase tracking-[0.2em] text-site-warning/80">Публикация</div>
              <h3 className="mt-1 text-xl font-black text-site-text">{publishNotice.mapTitle}</h3>
              <p className="mt-2 text-sm leading-6 text-site-warning/90">
                Карту пока не опубликовали. Перед публикацией нужно спокойно поправить эти пункты:
              </p>
              <ul className="mt-3 list-disc space-y-1 pl-5 text-sm leading-6">
                {publishNotice.issues.map((issue) => (
                  <li key={issue}>{issue}</li>
                ))}
              </ul>
            </section>
          )}

          <section className="rounded-[22px] border border-site-border bg-site-bg/82 p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-[0.64rem] uppercase tracking-[0.2em] text-site-muted">Продолжить работу</div>
                <h3 className="mt-1 text-xl font-black text-site-text">Черновики</h3>
              </div>
              <span className="rounded-full border border-site-border bg-site-surface/[0.04] px-3 py-1 text-xs font-semibold text-site-secondary">{drafts.length}</span>
            </div>
            <div className="mt-4 grid gap-3 md:grid-cols-2 2xl:grid-cols-3">
              {drafts.map((map) => (
                <article
                  key={map.id}
                  className="rounded-[18px] border border-site-border bg-site-surface/[0.035] px-4 py-4 text-left transition hover:border-site-accent/34 hover:bg-site-accent/8"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-base font-black text-site-text">{map.title}</span>
                    <span className="rounded-full border border-site-warning/25 bg-site-warning/10 px-2 py-0.5 text-[0.6rem] uppercase tracking-[0.14em] text-site-warning">draft</span>
                  </div>
                  <div className="mt-2 min-h-[2.5rem] overflow-hidden text-sm leading-5 text-site-muted">{map.description || 'Без описания'}</div>
                  <div className="mt-3 text-xs text-site-muted">{formatMapAssetBytes(map.assetBytesTotal)} · {formatDate(map.updatedAt)}</div>
                  <div className="mt-4 grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => void onOpenDraft(map.id)}
                      className="rounded-[10px] bg-site-accent px-3 py-2 text-xs font-black text-site-on-accent transition hover:bg-site-accent"
                    >
                      Редактировать
                    </button>
                    <button
                      type="button"
                      onClick={() => void onPublishDraft(map.id)}
                      className="rounded-[10px] bg-site-success px-3 py-2 text-xs font-black text-site-on-accent transition hover:bg-site-success"
                    >
                      Опубликовать
                    </button>
                    <button
                      type="button"
                      onClick={() => void onDuplicateMap(map.id)}
                      className="rounded-[10px] border border-site-border bg-site-surface/[0.04] px-3 py-2 text-xs text-site-text transition hover:border-site-border"
                    >
                      Дублировать
                    </button>
                    <button
                      type="button"
                      onClick={() => void onRenameMap(map)}
                      className="rounded-[10px] border border-site-accent/20 bg-site-accent/10 px-3 py-2 text-xs text-site-accent transition hover:border-site-accent/50"
                    >
                      Переименовать
                    </button>
                    <button
                      type="button"
                      onClick={() => void onDeleteDraft(map)}
                      className="rounded-[10px] border border-site-danger/20 bg-site-danger/10 px-3 py-2 text-xs text-site-danger transition hover:border-site-danger/50"
                    >
                      Удалить
                    </button>
                  </div>
                </article>
              ))}
              {drafts.length === 0 && (
                <div className="rounded-[18px] border border-dashed border-site-border px-4 py-5 text-sm text-site-muted">Черновиков пока нет.</div>
              )}
            </div>
          </section>

          {publishedMaps.length > 0 && (
            <section className="rounded-[22px] border border-site-border bg-site-bg/82 p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-[0.64rem] uppercase tracking-[0.2em] text-site-muted">Готовые карты</div>
                  <h3 className="mt-1 text-xl font-black text-site-text">Опубликованные</h3>
                </div>
                <span className="rounded-full border border-site-success/20 bg-site-success/10 px-3 py-1 text-xs font-semibold text-site-success">{publishedMaps.length}</span>
              </div>
              <div className="mt-4 grid gap-3 md:grid-cols-2 2xl:grid-cols-3">
                {publishedMaps.map((map) => (
                  <article key={map.id} className="rounded-[18px] border border-site-border bg-site-surface/[0.035] px-4 py-4">
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate text-base font-black text-site-text">{map.title}</span>
                      <span className="rounded-full border border-site-success/25 bg-site-success/10 px-2 py-0.5 text-[0.6rem] uppercase tracking-[0.14em] text-site-success">published</span>
                    </div>
                    <div className="mt-2 min-h-[2.5rem] overflow-hidden text-sm leading-5 text-site-muted">{map.description || 'Без описания'}</div>
                    <div className="mt-3 text-xs text-site-muted">{formatMapAssetBytes(map.assetBytesTotal)} · {formatDate(map.updatedAt)}</div>
                    <div className="mt-4 grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => void onOpenDraft(map.id)}
                        className="rounded-[10px] border border-site-border bg-site-surface/[0.04] px-3 py-2 text-xs text-site-text transition hover:border-site-border"
                      >
                        Открыть
                      </button>
                      <button
                        type="button"
                        onClick={() => void onDuplicateMap(map.id)}
                        className="rounded-[10px] border border-site-accent/20 bg-site-accent/10 px-3 py-2 text-xs font-semibold text-site-accent transition hover:border-site-accent/50"
                      >
                        Дублировать
                      </button>
                      <button
                        type="button"
                        onClick={() => void onRenameMap(map)}
                        className="col-span-2 rounded-[10px] border border-site-border bg-site-surface/[0.04] px-3 py-2 text-xs text-site-text transition hover:border-site-border"
                      >
                        Переименовать
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          )}

          <section className="rounded-[22px] border border-site-border bg-site-bg/82 p-4">
            <div className="text-[0.64rem] uppercase tracking-[0.2em] text-site-muted">Основа для новой карты</div>
            <h3 className="mt-1 text-xl font-black text-site-text">Шаблоны карт</h3>
            <div className="mt-4 grid gap-3 md:grid-cols-2 2xl:grid-cols-3">
              {mapTemplates.map((template) => (
                <button
                  key={template.id}
                  type="button"
                  onClick={() => void onCreateFromTemplate(template)}
                  className="rounded-[18px] border border-site-border bg-site-surface/[0.035] px-4 py-4 text-left transition hover:border-site-accent/34 hover:bg-site-accent/8"
                >
                  <span className="rounded-full border border-site-accent/20 bg-site-accent/10 px-2 py-1 text-[0.6rem] uppercase tracking-[0.16em] text-site-accent">{template.badge}</span>
                  <div className="mt-3 text-base font-black text-site-text">{template.title}</div>
                  <div className="mt-2 min-h-[3.75rem] text-sm leading-5 text-site-muted">{template.description}</div>
                  <div className="mt-3 text-xs font-semibold text-site-accent">Создать черновик из шаблона</div>
                </button>
              ))}
              {mapTemplates.length === 0 && (
                <div className="rounded-[18px] border border-dashed border-site-border px-4 py-5 text-sm text-site-muted">Шаблоны не найдены по фильтру.</div>
              )}
            </div>
          </section>
        </div>
      </div>
    </section>
  );
}

function Inspector({
  map,
  assets,
  selection,
  activeSelections,
  updateMap,
  assetEditObjectId,
  setAssetEditObjectId,
  updateObjectAssetTransform,
  canEdit,
  selectedCount,
  onDuplicateSelection,
  onCopySelection,
}: {
  map: MysteryRoomMapDefinition;
  assets: MrMapAsset[];
  selection: MysteryRoomEditorSelection | null;
  activeSelections: MysteryRoomEditorSelection[];
  updateMap: (updater: (map: MysteryRoomMapDefinition) => MysteryRoomMapDefinition) => void;
  assetEditObjectId: string | null;
  setAssetEditObjectId: (objectId: string | null) => void;
  updateObjectAssetTransform: (
    objectId: string,
    updater: (transform: ReturnType<typeof normalizeEditorAssetTransform>) => ReturnType<typeof normalizeEditorAssetTransform>,
  ) => void;
  canEdit: boolean;
  selectedCount: number;
  onDuplicateSelection: () => void;
  onCopySelection: () => void;
}) {
  const label = selectedEntityLabel(map, selection);
  const imageAssets = useMemo(() => assets.filter(isMysteryRoomMapImageAsset), [assets]);
  const canCopySelection = Boolean(selection && canEdit);
  const layerControls = isLayerQuickControlSelection(selection) ? (
    <LayerControls map={map} selection={selection} updateMap={updateMap} />
  ) : null;

  if (!selection) {
    return (
      <div className="space-y-2">
        <PropertySection title="Карта">
          <ReadonlyProperty label="ID" value={map.id} />
          <ReadonlyProperty label="Полотно" value={`${map.width} x ${map.height}`} />
          <ReadonlyProperty label="Комнаты" value={map.rooms.length} />
          <ReadonlyProperty label="Объекты" value={map.customObjects.length} />
        </PropertySection>
        <div className="rounded-[12px] border border-dashed border-site-border px-3 py-3 text-xs leading-5 text-site-muted">
          Выберите объект на карте. Свойства появятся списком: слева параметр, справа редактирование.
        </div>
      </div>
    );
  }

  if (selection.type === 'room') {
    const room = map.rooms.find((entry) => entry.id === selection.id);
    if (!room) return <MissingSelection label={label} />;
    const rect = room.floor[0] ?? { x: 0, y: 0, width: 100, height: 100 };
    return (
      <div className="space-y-2">
        <SelectionActionSection label={label} selectedCount={selectedCount} canCopy={canCopySelection} onDuplicate={onDuplicateSelection} onCopy={onCopySelection} />
        <PropertySection title="Основное">
          <ReadonlyProperty label="ID" value={room.id} />
          <TextInput label="Название" value={room.title} onChange={(value) => updateMap((draft) => ({ ...draft, rooms: draft.rooms.map((entry) => entry.id === room.id ? { ...entry, title: value } : entry) }))} />
          <SelectInput label="Доступ" value={room.accessLevel} options={ACCESS_LEVELS} onChange={(value) => updateMap((draft) => ({ ...draft, rooms: draft.rooms.map((entry) => entry.id === room.id ? { ...entry, accessLevel: value as MysteryRoomAccessLevel } : entry) }))} />
        </PropertySection>
        <TextureLayerSection
          layers={textureLayersFromLegacy(room.textures, room.floorTexture, room.id)}
          onChange={(textures) => updateMap((draft) => ({
            ...draft,
            rooms: draft.rooms.map((entry) =>
              entry.id === room.id ? { ...entry, textures, floorTexture: textureLayerToSurfaceTexture(textures[0]) } : entry,
            ),
          }))}
          baseColor={{
            color: room.color,
            colorEnabled: room.colorEnabled,
            colorOpacity: room.colorOpacity,
            outlineColor: room.outlineColor,
            outlineEnabled: room.outlineEnabled,
            outlineOpacity: room.outlineOpacity,
            outlineWidth: room.outlineWidth,
            onColorChange: (color) => updateMap((draft) => ({ ...draft, rooms: draft.rooms.map((entry) => entry.id === room.id ? { ...entry, color } : entry) })),
            onColorEnabledChange: (colorEnabled) => updateMap((draft) => ({ ...draft, rooms: draft.rooms.map((entry) => entry.id === room.id ? { ...entry, colorEnabled } : entry) })),
            onColorOpacityChange: (colorOpacity) => updateMap((draft) => ({ ...draft, rooms: draft.rooms.map((entry) => entry.id === room.id ? { ...entry, colorOpacity } : entry) })),
            onOutlineColorChange: (outlineColor) => updateMap((draft) => ({ ...draft, rooms: draft.rooms.map((entry) => entry.id === room.id ? { ...entry, outlineColor } : entry) })),
            onOutlineEnabledChange: (outlineEnabled) => updateMap((draft) => ({ ...draft, rooms: draft.rooms.map((entry) => entry.id === room.id ? { ...entry, outlineEnabled } : entry) })),
            onOutlineOpacityChange: (outlineOpacity) => updateMap((draft) => ({ ...draft, rooms: draft.rooms.map((entry) => entry.id === room.id ? { ...entry, outlineOpacity } : entry) })),
            onOutlineWidthChange: (outlineWidth) => updateMap((draft) => ({ ...draft, rooms: draft.rooms.map((entry) => entry.id === room.id ? { ...entry, outlineWidth } : entry) })),
          }}
        />
        <PropertySection title="Transform">
          <RectInputs rect={rect} onChange={(nextRect) => updateMap((draft) => ({ ...draft, rooms: draft.rooms.map((entry) => entry.id === room.id ? { ...entry, floor: [nextRect, ...entry.floor.slice(1)], labelPoint: { x: nextRect.x + nextRect.width / 2, y: nextRect.y + 22 } } : entry) }))} />
        </PropertySection>
      </div>
    );
  }

  if (selection.type === 'customObject') {
    const object = map.customObjects.find((entry) => entry.id === selection.id);
    if (!object) return <MissingSelection label={label} />;
    const assetTransform = normalizeEditorAssetTransform(object);
    const objectPickupKind = object.pickupKind ?? (
      object.kind === 'resource' || object.resourceKind
        ? 'resource'
        : object.kind === 'weapon'
          ? 'weapon'
          : object.kind === 'badge'
            ? 'access_card'
            : 'tool'
    );
    const objectAccessCardLevel = normalizeMysteryRoomAccessCardLevel(object.accessCardLevel);
    const objectIsPickup = Boolean(object.pickupKind || object.itemId || object.resourceKind || object.kind === 'weapon' || object.kind === 'badge' || object.kind === 'resource');
    return (
      <div className="space-y-2">
        <SelectionActionSection label={label} selectedCount={selectedCount} canCopy={canCopySelection} onDuplicate={onDuplicateSelection} onCopy={onCopySelection} />
        <PropertySection title="Основное">
          <ReadonlyProperty label="ID" value={object.id} />
          <TextInput label="Имя" value={object.label} onChange={(value) => updateMap((draft) => ({ ...draft, customObjects: draft.customObjects.map((entry) => entry.id === object.id ? { ...entry, label: value } : entry) }))} />
          <WorldLabelVisibilityInput
            value={object.worldLabelVisibility}
            onChange={(worldLabelVisibility) => updateMap((draft) => ({ ...draft, customObjects: draft.customObjects.map((entry) => entry.id === object.id ? { ...entry, worldLabelVisibility } : entry) }))}
          />
          <SelectInput
            label="Тип"
            value={object.kind}
            options={OBJECT_KINDS}
            onChange={(value) => updateMap((draft) => ({
              ...draft,
              customObjects: draft.customObjects.map((entry) => {
                if (entry.id !== object.id) return entry;
                const kind = value as MysteryRoomCustomObjectKind;
                if (objectIsPickup && kind === 'weapon') return { ...entry, ...itemPatchForPickupKind(entry, 'weapon') };
                if (objectIsPickup && kind === 'badge') return { ...entry, ...itemPatchForPickupKind(entry, 'access_card') };
                if (objectIsPickup && kind === 'resource') return { ...entry, ...itemPatchForPickupKind(entry, 'resource') };
                if (objectIsPickup && kind === 'equipment' && (entry.itemId === 'pistol' || entry.tags.includes('pistol'))) {
                  return { ...entry, ...itemPatchForPickupKind(entry, 'tool') };
                }
                return { ...entry, kind };
              }),
            }))}
          />
          <ZoneInput map={map} value={object.zoneId} onChange={(zoneId) => updateMap((draft) => ({ ...draft, customObjects: draft.customObjects.map((entry) => entry.id === object.id ? { ...entry, zoneId } : entry) }))} />
        </PropertySection>
        <ObjectAppearanceAssetSection
          object={object}
          imageAssets={imageAssets}
          onChange={(patch) =>
            updateMap((draft) => ({
              ...draft,
              customObjects: draft.customObjects.map((entry) => (entry.id === object.id ? { ...entry, ...patch } : entry)),
            }))
          }
        />
        <PropertySection title="Предмет и инвентарь">
          <ToggleInput
            label="Подбираемый"
            value={objectIsPickup}
            onChange={(pickupEnabled) =>
              updateMap((draft) => ({
                ...draft,
                customObjects: draft.customObjects.map((entry) =>
                  entry.id === object.id
                    ? {
                        ...entry,
                        interactive: pickupEnabled ? true : entry.interactive,
                        pickupKind: pickupEnabled ? entry.pickupKind ?? objectPickupKind : undefined,
                        itemId: pickupEnabled
                          ? entry.itemId ?? (
                              objectPickupKind === 'access_card'
                                ? entry.id as any
                                : objectPickupKind === 'resource' && entry.resourceKind
                                  ? mysteryRoomResourceItemId(entry.resourceKind)
                                  : undefined
                            )
                          : undefined,
                      }
                    : entry,
                ),
              }))
            }
          />
          {objectIsPickup ? (
            <>
              <SelectInput
                label="Тип предмета"
                value={objectPickupKind}
                options={[...PICKUP_KINDS]}
                onChange={(pickupKind) =>
                  updateMap((draft) => ({
                    ...draft,
                    customObjects: draft.customObjects.map((entry) =>
                      entry.id === object.id
                        ? {
                            ...entry,
                            ...itemPatchForPickupKind(entry, pickupKind as typeof PICKUP_KINDS[number]),
                          }
                        : entry,
                    ),
                  }))
                }
              />
              {objectPickupKind === 'access_card' ? (
                <SelectInput
                  label="Уровень пропуска"
                  value={objectAccessCardLevel}
                  options={[...MYSTERY_ROOM_ACCESS_CARD_LEVELS]}
                  onChange={(accessCardLevel) =>
                    updateMap((draft) => ({
                      ...draft,
                      customObjects: draft.customObjects.map((entry) =>
                        entry.id === object.id
                          ? {
                              ...entry,
                              ...accessCardAppearancePatch(accessCardLevel as MysteryRoomAccessCardLevel),
                            }
                          : entry,
                      ),
                    }))
                  }
                />
              ) : null}
              {objectPickupKind === 'resource' ? (
                <>
                  <SelectInput
                    label="Ресурс"
                    value={object.resourceKind ?? 'wood'}
                    options={[...MYSTERY_ROOM_RESOURCE_KINDS]}
                    onChange={(resourceKindValue) => {
                      const resourceKind = resourceKindValue as MysteryRoomResourceKind;
                      const resource = MYSTERY_ROOM_RESOURCE_DEFINITIONS[resourceKind];
                      const sourceKind = RESOURCE_SOURCE_KIND_BY_RESOURCE[resourceKind];
                      const source = MYSTERY_ROOM_RESOURCE_SOURCE_DEFINITIONS[sourceKind];
                      updateMap((draft) => ({
                        ...draft,
                        customObjects: draft.customObjects.map((entry) =>
                          entry.id === object.id
                            ? {
                                ...entry,
                                kind: 'resource',
                                pickupKind: 'resource',
                                resourceKind,
                                resourceSourceKind: sourceKind,
                                resourceAmount: entry.resourceAmount ?? source.amount,
                                itemId: mysteryRoomResourceItemId(resourceKind),
                                inventoryIcon: resource.icon,
                                color: source.color,
                                accentColor: source.accentColor,
                                outlineColor: source.accentColor,
                                interactive: true,
                                tags: Array.from(new Set(['pickup', 'resource', sourceKind, resourceKind])),
                              }
                            : entry,
                        ),
                      }));
                    }}
                  />
                  <NumberInput
                    label="Количество"
                    value={object.resourceAmount ?? 1}
                    onChange={(resourceAmount) =>
                      updateMap((draft) => ({
                        ...draft,
                        customObjects: draft.customObjects.map((entry) =>
                          entry.id === object.id ? { ...entry, resourceAmount: Math.max(1, Math.floor(resourceAmount)) } : entry,
                        ),
                      }))
                    }
                  />
                </>
              ) : null}
              <TextInput
                label="Item ID"
                value={object.itemId ?? ''}
                onChange={(itemId) =>
                  updateMap((draft) => ({
                    ...draft,
                    customObjects: draft.customObjects.map((entry) =>
                      entry.id === object.id
                        ? {
                            ...entry,
                            itemId: itemId.trim() ? itemId.trim() as any : undefined,
                            ...(getMysteryRoomDefaultItemVisual(itemId.trim()) ? defaultItemAppearancePatch(itemId.trim()) : {}),
                          }
                        : entry,
                    ),
                  }))
                }
              />
              <InventoryIconFields
                entry={object}
                imageAssets={imageAssets}
                onChange={(patch) =>
                  updateMap((draft) => ({
                    ...draft,
                    customObjects: draft.customObjects.map((entry) => (entry.id === object.id ? { ...entry, ...patch } : entry)),
                  }))
                }
              />
            </>
          ) : null}
        </PropertySection>
        {layerControls}
        <PropertySection title="Transform">
          <RectInputs rect={object.rect} onChange={(rect) => updateMap((draft) => ({ ...draft, customObjects: draft.customObjects.map((entry) => entry.id === object.id ? syncCustomObjectCollisionForInspector({ ...entry, rect }) : entry) }))} />
          <NumberInput label="Rotation" value={object.rotation} onChange={(rotation) => updateMap((draft) => ({ ...draft, customObjects: draft.customObjects.map((entry) => entry.id === object.id ? syncCustomObjectCollisionForInspector({ ...entry, rotation }) : entry) }))} />
          <NumberInput
            label="Углов / точек"
            value={getCustomObjectShapePointCount(object)}
            step={1}
            onChange={(pointCount) =>
              updateMap((draft) => ({
                ...draft,
                customObjects: draft.customObjects.map((entry) =>
                  entry.id === object.id ? setCustomObjectShapePointCount(entry, pointCount) : entry,
                ),
              }))
            }
          />
        </PropertySection>
        <PropertySection title="Вид и физика">
          <ToggleInput label="Коллизия" value={isCustomObjectCollisionEnabled(object)} onChange={(collisionEnabled) => updateMap((draft) => ({ ...draft, customObjects: draft.customObjects.map((entry) => entry.id === object.id ? setCustomObjectCollisionEnabled(entry, collisionEnabled) : entry) }))} />
          <ToggleInput label="Физика" value={object.physicsEnabled ?? false} onChange={(physicsEnabled) => updateMap((draft) => ({ ...draft, customObjects: draft.customObjects.map((entry) => entry.id === object.id ? { ...entry, physicsEnabled } : entry) }))} />
          <NumberInput label="Вес, кг" value={normalizeInspectorWeight(object.weightKg)} step={1} onChange={(weightKg) => updateMap((draft) => ({ ...draft, customObjects: draft.customObjects.map((entry) => entry.id === object.id ? { ...entry, weightKg: normalizeInspectorWeight(weightKg) } : entry) }))} />
          <ToggleInput label="Блокирует обзор" value={object.blocksSight} onChange={(blocksSight) => updateMap((draft) => ({ ...draft, customObjects: draft.customObjects.map((entry) => entry.id === object.id ? { ...entry, blocksSight, collision: entry.collision.kind !== 'none' ? { ...entry.collision, blocksSight } : entry.collision } : entry) }))} />
          <ToggleInput label="Видно за стенами" value={object.visibleThroughWalls ?? false} onChange={(visibleThroughWalls) => updateMap((draft) => ({ ...draft, customObjects: draft.customObjects.map((entry) => entry.id === object.id ? { ...entry, visibleThroughWalls } : entry) }))} />
          <ToggleInput label="Интерактивный" value={object.interactive} onChange={(interactive) => updateMap((draft) => ({ ...draft, customObjects: draft.customObjects.map((entry) => entry.id === object.id ? { ...entry, interactive } : entry) }))} />
        </PropertySection>
        <TextureLayerSection
          layers={textureLayersFromLegacy(
            object.textures,
            object.assetUrl
              ? {
                  assetId: object.assetId,
                  assetUrl: object.assetUrl,
                  assetName: object.assetName,
                  assetMimeType: object.assetMimeType,
                  fit: object.assetFit ?? 'cover',
                  opacity: 0.98,
                  offsetX: assetTransform.offsetX,
                  offsetY: assetTransform.offsetY,
                  scaleX: assetTransform.scaleX,
                  scaleY: assetTransform.scaleY,
                  rotation: assetTransform.rotation,
                }
              : undefined,
            object.id,
          )}
          onChange={(textures) => updateMap((draft) => ({
            ...draft,
            customObjects: draft.customObjects.map((entry) =>
              entry.id === object.id
                ? {
                    ...entry,
                    textures,
                    kind: entry.kind === 'image' ? 'primitive' : entry.kind,
                    assetId: undefined,
                    assetUrl: undefined,
                    assetName: undefined,
                    assetMimeType: undefined,
                    assetFit: undefined,
                    assetTransform: undefined,
                    assetAlphaCollisionEnabled: false,
                  }
                : entry,
            ),
          }))}
          baseColor={{
            color: object.color,
            colorEnabled: object.colorEnabled,
            colorOpacity: object.colorOpacity,
            outlineColor: object.outlineColor ?? object.accentColor,
            outlineEnabled: object.outlineEnabled,
            outlineOpacity: object.outlineOpacity,
            outlineWidth: object.outlineWidth,
            onColorChange: (color) => updateMap((draft) => ({ ...draft, customObjects: draft.customObjects.map((entry) => entry.id === object.id ? { ...entry, color } : entry) })),
            onColorEnabledChange: (colorEnabled) => updateMap((draft) => ({ ...draft, customObjects: draft.customObjects.map((entry) => entry.id === object.id ? { ...entry, colorEnabled } : entry) })),
            onColorOpacityChange: (colorOpacity) => updateMap((draft) => ({ ...draft, customObjects: draft.customObjects.map((entry) => entry.id === object.id ? { ...entry, colorOpacity } : entry) })),
            onOutlineColorChange: (outlineColor) => updateMap((draft) => ({ ...draft, customObjects: draft.customObjects.map((entry) => entry.id === object.id ? { ...entry, outlineColor } : entry) })),
            onOutlineEnabledChange: (outlineEnabled) => updateMap((draft) => ({ ...draft, customObjects: draft.customObjects.map((entry) => entry.id === object.id ? { ...entry, outlineEnabled } : entry) })),
            onOutlineOpacityChange: (outlineOpacity) => updateMap((draft) => ({ ...draft, customObjects: draft.customObjects.map((entry) => entry.id === object.id ? { ...entry, outlineOpacity } : entry) })),
            onOutlineWidthChange: (outlineWidth) => updateMap((draft) => ({ ...draft, customObjects: draft.customObjects.map((entry) => entry.id === object.id ? { ...entry, outlineWidth } : entry) })),
          }}
          defaultTexturesCollapsed
          defaultTextureFit="cover"
        />
        <PropertySection title="Связи">
          <TextInput label="Тег задания" value={object.objectiveTag ?? ''} onChange={(objectiveTag) => updateMap((draft) => ({ ...draft, customObjects: draft.customObjects.map((entry) => entry.id === object.id ? { ...entry, objectiveTag } : entry) }))} />
          <MultiIdInput
            label="Объекты"
            value={object.linkedObjectIds ?? []}
            options={objectLinkOptions(map, object.id)}
            onChange={(linkedObjectIds) => updateMap((draft) => ({ ...draft, customObjects: draft.customObjects.map((entry) => entry.id === object.id ? { ...entry, linkedObjectIds } : entry) }))}
          />
        </PropertySection>
        <ActionListEditor map={map} title="При активации" actions={object.onActivateActions ?? []} onChange={(onActivateActions) => updateMap((draft) => ({ ...draft, customObjects: draft.customObjects.map((entry) => entry.id === object.id ? { ...entry, onActivateActions } : entry) }))} />
        <ActionListEditor map={map} title="При подборе" actions={object.onPickupActions ?? []} onChange={(onPickupActions) => updateMap((draft) => ({ ...draft, customObjects: draft.customObjects.map((entry) => entry.id === object.id ? { ...entry, onPickupActions } : entry) }))} />
      </div>
    );
  }

  if (selection.type === 'door') {
    const door = map.doors.find((entry) => entry.id === selection.id);
    if (!door) return <MissingSelection label={label} />;
    return (
      <div className="space-y-2">
        <PropertySection title="Основное">
          <ReadonlyProperty label="ID" value={door.id} />
          <TextInput label="Имя" value={door.label ?? door.id} onChange={(label) => updateMap((draft) => ({ ...draft, doors: draft.doors.map((entry) => entry.id === door.id ? { ...entry, label } : entry) }))} />
          <SelectInput label="Ориентация" value={door.orientation} options={['horizontal', 'vertical']} onChange={(value) => updateMap((draft) => ({ ...draft, doors: draft.doors.map((entry) => entry.id === door.id ? { ...entry, orientation: value as 'horizontal' | 'vertical' } : entry) }))} />
        </PropertySection>
        <PropertySection title="Доступ">
          <ReadonlyProperty label="Описание" value={doorAccessModeLabel(door.accessMode ?? (door.requiredAccess === 'public' ? 'public' : 'card_required'))} />
          <SelectInput
            label="Режим"
            value={door.accessMode ?? (door.requiredAccess === 'public' ? 'public' : 'card_required')}
            options={DOOR_ACCESS_MODES}
            onChange={(value) => updateMap((draft) => ({
              ...draft,
              doors: draft.doors.map((entry) =>
                entry.id === door.id
                  ? {
                      ...entry,
                      accessMode: value as MysteryRoomDoorAccessMode,
                      requiredAccess: value === 'public' || value === 'npc_only' ? 'public' : entry.requiredAccess,
                      accessCardObjectIds: value === 'public' || value === 'npc_only' ? [] : entry.accessCardObjectIds ?? [],
                    }
                  : entry,
              ),
            }))}
          />
          <MultiIdInput
            label="Пропуска"
            value={door.accessCardObjectIds ?? []}
            options={doorAccessCardOptions(map)}
            onChange={(accessCardObjectIds) => updateMap((draft) => ({ ...draft, doors: draft.doors.map((entry) => entry.id === door.id ? { ...entry, accessCardObjectIds, accessMode: accessCardObjectIds.length > 0 ? entry.accessMode ?? 'card_required' : entry.accessMode } : entry) }))}
          />
        </PropertySection>
        {layerControls}
        <TextureLayerSection
          layers={door.textures ?? []}
          onChange={(textures) => updateMap((draft) => ({ ...draft, doors: draft.doors.map((entry) => entry.id === door.id ? { ...entry, textures } : entry) }))}
          baseColor={{
            color: door.color ?? 0x0f172a,
            colorEnabled: door.colorEnabled,
            colorOpacity: door.colorOpacity,
            outlineColor: door.outlineColor,
            outlineEnabled: door.outlineEnabled,
            outlineOpacity: door.outlineOpacity,
            outlineWidth: door.outlineWidth,
            onColorChange: (color) => updateMap((draft) => ({ ...draft, doors: draft.doors.map((entry) => entry.id === door.id ? { ...entry, color } : entry) })),
            onColorEnabledChange: (colorEnabled) => updateMap((draft) => ({ ...draft, doors: draft.doors.map((entry) => entry.id === door.id ? { ...entry, colorEnabled } : entry) })),
            onColorOpacityChange: (colorOpacity) => updateMap((draft) => ({ ...draft, doors: draft.doors.map((entry) => entry.id === door.id ? { ...entry, colorOpacity } : entry) })),
            onOutlineColorChange: (outlineColor) => updateMap((draft) => ({ ...draft, doors: draft.doors.map((entry) => entry.id === door.id ? { ...entry, outlineColor } : entry) })),
            onOutlineEnabledChange: (outlineEnabled) => updateMap((draft) => ({ ...draft, doors: draft.doors.map((entry) => entry.id === door.id ? { ...entry, outlineEnabled } : entry) })),
            onOutlineOpacityChange: (outlineOpacity) => updateMap((draft) => ({ ...draft, doors: draft.doors.map((entry) => entry.id === door.id ? { ...entry, outlineOpacity } : entry) })),
            onOutlineWidthChange: (outlineWidth) => updateMap((draft) => ({ ...draft, doors: draft.doors.map((entry) => entry.id === door.id ? { ...entry, outlineWidth } : entry) })),
          }}
        />
        <PropertySection title="Навигация">
          <ZoneInput map={map} label="Из" value={door.fromZoneId} onChange={(zoneId) => updateMap((draft) => ({ ...draft, doors: draft.doors.map((entry) => entry.id === door.id ? { ...entry, fromZoneId: zoneId } : entry) }))} />
          <ZoneInput map={map} label="В" value={door.toZoneId} onChange={(zoneId) => updateMap((draft) => ({ ...draft, doors: draft.doors.map((entry) => entry.id === door.id ? { ...entry, toZoneId: zoneId } : entry) }))} />
        </PropertySection>
        <PropertySection title="Transform">
          <RectInputs rect={door.panel} onChange={(panel) => updateMap((draft) => ({ ...draft, doors: draft.doors.map((entry) => entry.id === door.id ? { ...entry, panel } : entry) }))} />
        </PropertySection>
      </div>
    );
  }

  if (selection.type === 'light') {
    const light = map.lights.find((entry) => entry.id === selection.id);
    if (!light) return <MissingSelection label={label} />;
    const flickerMode = light.flickerMode ?? (light.flicker ? 'flicker' : 'steady');
    const updateLight = (patch: Partial<MysteryRoomLightDefinition>) =>
      updateMap((draft) => ({
        ...draft,
        lights: draft.lights.map((entry) => (entry.id === light.id ? { ...entry, ...patch } : entry)),
      }));
    return (
      <div className="space-y-2">
        <PropertySection title="Основное">
          <ReadonlyProperty label="ID" value={light.id} />
          <TextInput label="Имя" value={light.label ?? 'Свет'} onChange={(value) => updateMap((draft) => ({ ...draft, lights: draft.lights.map((entry) => entry.id === light.id ? { ...entry, label: value } : entry) }))} />
          <ZoneInput map={map} value={light.zoneId} onChange={(zoneId) => updateMap((draft) => ({ ...draft, lights: draft.lights.map((entry) => entry.id === light.id ? { ...entry, zoneId } : entry) }))} />
        </PropertySection>
        {layerControls}
        <TextureLayerSection
          layers={light.textures ?? []}
          onChange={(textures) => updateMap((draft) => ({ ...draft, lights: draft.lights.map((entry) => entry.id === light.id ? { ...entry, textures } : entry) }))}
          baseColor={{
            color: light.color,
            colorEnabled: light.colorEnabled,
            colorOpacity: light.colorOpacity,
            outlineColor: light.outlineColor,
            outlineEnabled: light.outlineEnabled,
            outlineOpacity: light.outlineOpacity,
            outlineWidth: light.outlineWidth,
            onColorChange: (color) => updateMap((draft) => ({ ...draft, lights: draft.lights.map((entry) => entry.id === light.id ? { ...entry, color } : entry) })),
            onColorEnabledChange: (colorEnabled) => updateMap((draft) => ({ ...draft, lights: draft.lights.map((entry) => entry.id === light.id ? { ...entry, colorEnabled } : entry) })),
            onColorOpacityChange: (colorOpacity) => updateMap((draft) => ({ ...draft, lights: draft.lights.map((entry) => entry.id === light.id ? { ...entry, colorOpacity } : entry) })),
            onOutlineColorChange: (outlineColor) => updateMap((draft) => ({ ...draft, lights: draft.lights.map((entry) => entry.id === light.id ? { ...entry, outlineColor } : entry) })),
            onOutlineEnabledChange: (outlineEnabled) => updateMap((draft) => ({ ...draft, lights: draft.lights.map((entry) => entry.id === light.id ? { ...entry, outlineEnabled } : entry) })),
            onOutlineOpacityChange: (outlineOpacity) => updateMap((draft) => ({ ...draft, lights: draft.lights.map((entry) => entry.id === light.id ? { ...entry, outlineOpacity } : entry) })),
            onOutlineWidthChange: (outlineWidth) => updateMap((draft) => ({ ...draft, lights: draft.lights.map((entry) => entry.id === light.id ? { ...entry, outlineWidth } : entry) })),
          }}
        />
        <PropertySection title="Transform">
          <PointInputs point={light.position} onChange={(position) => updateMap((draft) => ({ ...draft, lights: draft.lights.map((entry) => entry.id === light.id ? { ...entry, position } : entry) }))} />
          <NumberInput label="Radius" value={light.radius} onChange={(radius) => updateMap((draft) => ({ ...draft, lights: draft.lights.map((entry) => entry.id === light.id ? { ...entry, radius } : entry) }))} />
        </PropertySection>
        <PropertySection title="Свет">
          <SliderNumberInput label="Яркость" value={light.intensity} min={0} max={1.4} step={0.05} onChange={(intensity) => updateLight({ intensity })} />
          <SliderNumberInput
            label="Температура K"
            value={light.colorTemperatureK ?? 4200}
            min={1800}
            max={8000}
            step={100}
            onChange={(colorTemperatureK) => updateLight({ colorTemperatureK, color: kelvinToRgbNumber(colorTemperatureK) })}
          />
          <ColorInput label="Цвет" value={light.color} onChange={(color) => updateLight({ color })} />
          <SelectInput
            label="Свечение"
            value={flickerMode}
            options={[...LIGHT_FLICKER_MODES]}
            optionLabels={LIGHT_FLICKER_MODE_LABELS}
            onChange={(mode) => updateLight({ flickerMode: mode as MysteryRoomLightDefinition['flickerMode'], flicker: mode !== 'steady' })}
          />
          {flickerMode !== 'steady' ? (
            <>
              <SliderNumberInput label="Сила мерцания" value={light.flickerIntensity ?? 0.26} min={0.05} max={0.92} step={0.05} onChange={(flickerIntensity) => updateLight({ flickerIntensity })} />
              <SliderNumberInput label="Интервал мин, мс" value={light.flickerMinIntervalMs ?? 90} min={40} max={3000} step={10} onChange={(flickerMinIntervalMs) => updateLight({ flickerMinIntervalMs })} />
              <SliderNumberInput label="Интервал макс, мс" value={light.flickerMaxIntervalMs ?? 220} min={40} max={5000} step={10} onChange={(flickerMaxIntervalMs) => updateLight({ flickerMaxIntervalMs })} />
            </>
          ) : null}
        </PropertySection>
        <PropertySection title="Связи">
          <MultiIdInput
            label="Выключатели"
            value={light.switchIds ?? (light.switchId ? [light.switchId] : [])}
            options={map.interactables.filter((entry) => entry.kind === 'light_switch').map((entry) => ({ id: entry.id, label: entry.label }))}
            onChange={(ids) => updateMap((draft) => ({ ...draft, lights: draft.lights.map((entry) => entry.id === light.id ? { ...entry, switchIds: ids, switchId: ids[0] } : entry) }))}
          />
          <LinkedTriggersReadout map={map} targetKind="light" targetId={light.id} />
        </PropertySection>
      </div>
    );
  }

  if (selection.type === 'interactable') {
    const entry = map.interactables.find((item) => item.id === selection.id);
    if (!entry) return <MissingSelection label={label} />;
    return (
      <div className="space-y-2">
        <PropertySection title="Основное">
          <ReadonlyProperty label="ID" value={entry.id} />
          <TextInput label="Имя" value={entry.label} onChange={(value) => updateMap((draft) => ({ ...draft, interactables: draft.interactables.map((item) => item.id === entry.id ? { ...item, label: value } : item) }))} />
          <WorldLabelVisibilityInput
            value={entry.worldLabelVisibility}
            onChange={(worldLabelVisibility) => updateMap((draft) => ({ ...draft, interactables: draft.interactables.map((item) => item.id === entry.id ? { ...item, worldLabelVisibility } : item) }))}
          />
          <SelectInput label="Тип" value={entry.kind} options={INTERACTABLE_KINDS} onChange={(value) => updateMap((draft) => ({ ...draft, interactables: draft.interactables.map((item) => item.id === entry.id ? { ...item, kind: value as MysteryRoomInteractableKind } : item) }))} />
          <ZoneInput map={map} value={entry.zoneId} onChange={(zoneId) => updateMap((draft) => ({ ...draft, interactables: draft.interactables.map((item) => item.id === entry.id ? { ...item, zoneId } : item) }))} />
        </PropertySection>
        {layerControls}
        <TextureLayerSection
          layers={entry.textures ?? []}
          onChange={(textures) => updateMap((draft) => ({ ...draft, interactables: draft.interactables.map((item) => item.id === entry.id ? { ...item, textures } : item) }))}
          baseColor={{
            color: entry.color ?? defaultInteractableColor(entry.kind),
            colorEnabled: entry.colorEnabled,
            colorOpacity: entry.colorOpacity,
            outlineColor: entry.outlineColor,
            outlineEnabled: entry.outlineEnabled,
            outlineOpacity: entry.outlineOpacity,
            outlineWidth: entry.outlineWidth,
            onColorChange: (color) => updateMap((draft) => ({ ...draft, interactables: draft.interactables.map((item) => item.id === entry.id ? { ...item, color } : item) })),
            onColorEnabledChange: (colorEnabled) => updateMap((draft) => ({ ...draft, interactables: draft.interactables.map((item) => item.id === entry.id ? { ...item, colorEnabled } : item) })),
            onColorOpacityChange: (colorOpacity) => updateMap((draft) => ({ ...draft, interactables: draft.interactables.map((item) => item.id === entry.id ? { ...item, colorOpacity } : item) })),
            onOutlineColorChange: (outlineColor) => updateMap((draft) => ({ ...draft, interactables: draft.interactables.map((item) => item.id === entry.id ? { ...item, outlineColor } : item) })),
            onOutlineEnabledChange: (outlineEnabled) => updateMap((draft) => ({ ...draft, interactables: draft.interactables.map((item) => item.id === entry.id ? { ...item, outlineEnabled } : item) })),
            onOutlineOpacityChange: (outlineOpacity) => updateMap((draft) => ({ ...draft, interactables: draft.interactables.map((item) => item.id === entry.id ? { ...item, outlineOpacity } : item) })),
            onOutlineWidthChange: (outlineWidth) => updateMap((draft) => ({ ...draft, interactables: draft.interactables.map((item) => item.id === entry.id ? { ...item, outlineWidth } : item) })),
          }}
        />
        <PropertySection title="Transform">
          <PointInputs point={entry.anchor} onChange={(anchor) => updateMap((draft) => ({ ...draft, interactables: draft.interactables.map((item) => item.id === entry.id ? { ...item, anchor } : item) }))} />
          <NumberInput label="Radius" value={entry.interactionRadius} onChange={(interactionRadius) => updateMap((draft) => ({ ...draft, interactables: draft.interactables.map((item) => item.id === entry.id ? { ...item, interactionRadius } : item) }))} />
        </PropertySection>
        <PropertySection title="Доступ и предмет">
          <SelectInput label="Требует доступ" value={entry.requiredAccess ?? ''} options={['', ...ACCESS_LEVELS]} onChange={(requiredAccess) => updateMap((draft) => ({ ...draft, interactables: draft.interactables.map((item) => item.id === entry.id ? { ...item, requiredAccess: requiredAccess ? requiredAccess as MysteryRoomAccessLevel : undefined } : item) }))} />
          <SelectInput label="Выдает доступ" value={entry.grantsAccess ?? ''} options={['', ...ACCESS_LEVELS]} onChange={(grantsAccess) => updateMap((draft) => ({ ...draft, interactables: draft.interactables.map((item) => item.id === entry.id ? { ...item, grantsAccess: grantsAccess ? grantsAccess as MysteryRoomAccessLevel : undefined } : item) }))} />
          <TextInput label="Item ID" value={entry.itemId ?? ''} onChange={(itemId) => updateMap((draft) => ({ ...draft, interactables: draft.interactables.map((item) => item.id === entry.id ? { ...item, itemId: itemId as any } : item) }))} />
          {entry.kind === 'pickup' || entry.kind === 'access_grant' || entry.itemId ? (
            <InventoryIconFields
              entry={entry}
              imageAssets={imageAssets}
              onChange={(patch) =>
                updateMap((draft) => ({
                  ...draft,
                  interactables: draft.interactables.map((item) => (item.id === entry.id ? { ...item, ...patch } : item)),
                }))
              }
            />
          ) : null}
          <TextInput label="Тег задания" value={entry.objectiveTag ?? ''} onChange={(objectiveTag) => updateMap((draft) => ({ ...draft, interactables: draft.interactables.map((item) => item.id === entry.id ? { ...item, objectiveTag } : item) }))} />
        </PropertySection>
        <PropertySection title="Связи">
          {entry.kind === 'light_switch' && (
            <>
              <ToggleInput
                label="Включен по умолчанию"
                value={entry.defaultEnabled ?? true}
                onChange={(defaultEnabled) => updateMap((draft) => {
                  const targetLightIds = new Set(getEditorLightSwitchTargetIds(draft, entry));
                  return {
                    ...draft,
                    interactables: draft.interactables.map((item) => item.id === entry.id ? { ...item, defaultEnabled } : item),
                    lights: draft.lights.map((light) => targetLightIds.has(light.id) ? { ...light, enabled: defaultEnabled } : light),
                  };
                })}
              />
              <SelectInput
                label="Реакция света"
                value={entry.lightReaction ?? 'toggle'}
                options={['toggle', 'on', 'off']}
                onChange={(lightReaction) => updateMap((draft) => ({ ...draft, interactables: draft.interactables.map((item) => item.id === entry.id ? { ...item, lightReaction: lightReaction as 'toggle' | 'on' | 'off' } : item) }))}
              />
            </>
          )}
          <MultiIdInput
            label="Лампы"
            value={entry.kind === 'light_switch' ? getEditorLightSwitchTargetIds(map, entry) : entry.linkedLightIds ?? []}
            options={map.lights.map((light) => ({ id: light.id, label: light.label ?? light.id }))}
            onChange={(linkedLightIds) => updateMap((draft) => ({
              ...draft,
              interactables: draft.interactables.map((item) => item.id === entry.id ? { ...item, linkedLightIds } : item),
              lights: draft.lights.map((light) => {
                const currentIds = light.switchIds ?? (light.switchId ? [light.switchId] : []);
                const nextIds = linkedLightIds.includes(light.id)
                  ? Array.from(new Set([...currentIds, entry.id]))
                  : currentIds.filter((id) => id !== entry.id);
                return {
                  ...light,
                  enabled: entry.kind === 'light_switch' && linkedLightIds.includes(light.id) ? entry.defaultEnabled ?? true : light.enabled,
                  switchIds: nextIds,
                  switchId: nextIds[0],
                };
              }),
            }))}
          />
          <MultiIdInput
            label="Объекты"
            value={entry.linkedObjectIds ?? []}
            options={objectLinkOptions(map)}
            onChange={(linkedObjectIds) => updateMap((draft) => ({ ...draft, interactables: draft.interactables.map((item) => item.id === entry.id ? { ...item, linkedObjectIds } : item) }))}
          />
        </PropertySection>
        <ActionListEditor map={map} title="При активации" actions={entry.onActivateActions ?? []} onChange={(onActivateActions) => updateMap((draft) => ({ ...draft, interactables: draft.interactables.map((item) => item.id === entry.id ? { ...item, onActivateActions } : item) }))} />
        {entry.kind === 'pickup' && (
          <ActionListEditor map={map} title="При подборе" actions={entry.onPickupActions ?? []} onChange={(onPickupActions) => updateMap((draft) => ({ ...draft, interactables: draft.interactables.map((item) => item.id === entry.id ? { ...item, onPickupActions } : item) }))} />
        )}
      </div>
    );
  }

  if (selection.type === 'trigger') {
    const trigger = map.triggers.find((entry) => entry.id === selection.id);
    if (!trigger) return <MissingSelection label={label} />;
    return (
      <div className="space-y-2">
        <PropertySection title="Основное">
          <ReadonlyProperty label="ID" value={trigger.id} />
          <TextInput label="Имя" value={trigger.label} onChange={(value) => updateMap((draft) => ({ ...draft, triggers: draft.triggers.map((entry) => entry.id === trigger.id ? { ...entry, label: value } : entry) }))} />
          <ZoneInput map={map} value={trigger.zoneId} onChange={(zoneId) => updateMap((draft) => ({ ...draft, triggers: draft.triggers.map((entry) => entry.id === trigger.id ? { ...entry, zoneId } : entry) }))} />
        </PropertySection>
        {layerControls}
        <TextureLayerSection
          layers={trigger.textures ?? []}
          onChange={(textures) => updateMap((draft) => ({ ...draft, triggers: draft.triggers.map((entry) => entry.id === trigger.id ? { ...entry, textures } : entry) }))}
          baseColor={{
            color: trigger.color ?? 0xa78bfa,
            colorEnabled: trigger.colorEnabled,
            colorOpacity: trigger.colorOpacity,
            outlineColor: trigger.outlineColor,
            outlineEnabled: trigger.outlineEnabled,
            outlineOpacity: trigger.outlineOpacity,
            outlineWidth: trigger.outlineWidth,
            onColorChange: (color) => updateMap((draft) => ({ ...draft, triggers: draft.triggers.map((entry) => entry.id === trigger.id ? { ...entry, color } : entry) })),
            onColorEnabledChange: (colorEnabled) => updateMap((draft) => ({ ...draft, triggers: draft.triggers.map((entry) => entry.id === trigger.id ? { ...entry, colorEnabled } : entry) })),
            onColorOpacityChange: (colorOpacity) => updateMap((draft) => ({ ...draft, triggers: draft.triggers.map((entry) => entry.id === trigger.id ? { ...entry, colorOpacity } : entry) })),
            onOutlineColorChange: (outlineColor) => updateMap((draft) => ({ ...draft, triggers: draft.triggers.map((entry) => entry.id === trigger.id ? { ...entry, outlineColor } : entry) })),
            onOutlineEnabledChange: (outlineEnabled) => updateMap((draft) => ({ ...draft, triggers: draft.triggers.map((entry) => entry.id === trigger.id ? { ...entry, outlineEnabled } : entry) })),
            onOutlineOpacityChange: (outlineOpacity) => updateMap((draft) => ({ ...draft, triggers: draft.triggers.map((entry) => entry.id === trigger.id ? { ...entry, outlineOpacity } : entry) })),
            onOutlineWidthChange: (outlineWidth) => updateMap((draft) => ({ ...draft, triggers: draft.triggers.map((entry) => entry.id === trigger.id ? { ...entry, outlineWidth } : entry) })),
          }}
        />
        <PropertySection title="Область">
          <RectInputs rect={trigger.rect} onChange={(rect) => updateMap((draft) => ({ ...draft, triggers: draft.triggers.map((entry) => entry.id === trigger.id ? { ...entry, rect } : entry) }))} />
        </PropertySection>
        <PropertySection title="Событие">
          <SelectInput label="Event" value={trigger.event} options={['onEnter', 'onStep']} onChange={(value) => updateMap((draft) => ({ ...draft, triggers: draft.triggers.map((entry) => entry.id === trigger.id ? { ...entry, event: value as 'onEnter' | 'onStep' } : entry) }))} />
          <ToggleInput label="Repeat" value={trigger.repeatable} onChange={(repeatable) => updateMap((draft) => ({ ...draft, triggers: draft.triggers.map((entry) => entry.id === trigger.id ? { ...entry, repeatable } : entry) }))} />
          <NumberInput label="Cooldown ms" value={trigger.cooldownMs} onChange={(cooldownMs) => updateMap((draft) => ({ ...draft, triggers: draft.triggers.map((entry) => entry.id === trigger.id ? { ...entry, cooldownMs } : entry) }))} />
        </PropertySection>
        <ActionListEditor map={map} title="Actions" actions={trigger.actions} onChange={(actions) => updateMap((draft) => ({ ...draft, triggers: draft.triggers.map((entry) => entry.id === trigger.id ? { ...entry, actions } : entry) }))} />
      </div>
    );
  }

  if (selection.type === 'hazard') {
    const hazard = map.hazards.find((entry) => entry.id === selection.id);
    if (!hazard) return <MissingSelection label={label} />;
    return (
      <div className="space-y-2">
        <PropertySection title="Основное">
          <ReadonlyProperty label="ID" value={hazard.id} />
          <SelectInput
            label="Тип"
            value={hazard.kind}
            options={HAZARD_KINDS}
            onChange={(kind) => updateMap((draft) => ({ ...draft, hazards: draft.hazards.map((entry) => entry.id === hazard.id ? { ...entry, kind: kind as MysteryRoomHazardDefinition['kind'] } : entry) }))}
          />
          <ZoneInput map={map} value={hazard.zoneId} onChange={(zoneId) => updateMap((draft) => ({ ...draft, hazards: draft.hazards.map((entry) => entry.id === hazard.id ? { ...entry, zoneId } : entry) }))} />
        </PropertySection>
        <TextureLayerSection
          layers={hazard.textures ?? []}
          onChange={(textures) => updateMap((draft) => ({ ...draft, hazards: draft.hazards.map((entry) => entry.id === hazard.id ? { ...entry, textures } : entry) }))}
        />
        <PropertySection title="Область">
          <PointInputs point={hazard.position} onChange={(position) => updateMap((draft) => ({ ...draft, hazards: draft.hazards.map((entry) => entry.id === hazard.id ? { ...entry, position } : entry) }))} />
          <NumberInput label="Radius" value={hazard.radius} onChange={(radius) => updateMap((draft) => ({ ...draft, hazards: draft.hazards.map((entry) => entry.id === hazard.id ? { ...entry, radius } : entry) }))} />
          <NumberInput label="Intensity" value={hazard.intensity} step={0.05} onChange={(intensity) => updateMap((draft) => ({ ...draft, hazards: draft.hazards.map((entry) => entry.id === hazard.id ? { ...entry, intensity } : entry) }))} />
        </PropertySection>
        <PropertySection title="Связи">
          <LinkedTriggersReadout map={map} targetKind="hazard" targetId={hazard.id} />
        </PropertySection>
      </div>
    );
  }

  if (selection.type === 'incidentAnchor') {
    const anchor = map.incidentAnchors.find((entry) => entry.id === selection.id);
    if (!anchor) return <MissingSelection label={label} />;
    return (
      <div className="space-y-2">
        <PropertySection title="Основное">
          <ReadonlyProperty label="ID" value={anchor.id} />
          <SelectInput
            label="Тип"
            value={anchor.kind}
            options={INCIDENT_ANCHOR_KINDS}
            onChange={(kind) => updateMap((draft) => ({ ...draft, incidentAnchors: draft.incidentAnchors.map((entry) => entry.id === anchor.id ? { ...entry, kind: kind as MysteryRoomIncidentAnchorKind } : entry) }))}
          />
          <ZoneInput map={map} value={anchor.zoneId} onChange={(zoneId) => updateMap((draft) => ({ ...draft, incidentAnchors: draft.incidentAnchors.map((entry) => entry.id === anchor.id ? { ...entry, zoneId } : entry) }))} />
        </PropertySection>
        <PropertySection title="Transform">
          <PointInputs point={anchor.position} onChange={(position) => updateMap((draft) => ({ ...draft, incidentAnchors: draft.incidentAnchors.map((entry) => entry.id === anchor.id ? { ...entry, position } : entry) }))} />
        </PropertySection>
      </div>
    );
  }

  if (selection.type === 'workAnchor') {
    const anchor = map.workAnchors.find((entry) => entry.id === selection.id);
    if (!anchor) return <MissingSelection label={label} />;
    return (
      <div className="space-y-2">
        <PropertySection title="Основное">
          <ReadonlyProperty label="ID" value={anchor.id} />
          <SelectInput
            label="Тип"
            value={anchor.kind}
            options={WORK_ANCHOR_KINDS}
            onChange={(kind) => updateMap((draft) => ({ ...draft, workAnchors: draft.workAnchors.map((entry) => entry.id === anchor.id ? { ...entry, kind: kind as MysteryRoomAnchorKind, description: workAnchorKindDescription(kind as MysteryRoomAnchorKind) } : entry) }))}
          />
          <TextInput
            label="Описание"
            value={anchor.description ?? workAnchorKindDescription(anchor.kind)}
            onChange={(description) => updateMap((draft) => ({ ...draft, workAnchors: draft.workAnchors.map((entry) => entry.id === anchor.id ? { ...entry, description } : entry) }))}
          />
          <ZoneInput map={map} value={anchor.zoneId} onChange={(zoneId) => updateMap((draft) => ({ ...draft, workAnchors: draft.workAnchors.map((entry) => entry.id === anchor.id ? { ...entry, zoneId } : entry) }))} />
        </PropertySection>
        <PropertySection title="Transform">
          <PointInputs point={anchor.position} onChange={(position) => updateMap((draft) => ({ ...draft, workAnchors: draft.workAnchors.map((entry) => entry.id === anchor.id ? { ...entry, position } : entry) }))} />
        </PropertySection>
        <PropertySection title="Зона задачи">
          <SelectInput
            label="Форма"
            value={anchor.taskAreaShape ?? 'circle'}
            options={TASK_AREA_SHAPES}
            onChange={(taskAreaShape) => updateMap((draft) => ({ ...draft, workAnchors: draft.workAnchors.map((entry) => entry.id === anchor.id ? { ...entry, taskAreaShape: taskAreaShape as 'circle' | 'square' } : entry) }))}
          />
          <NumberInput
            label="Радиус / половина квадрата"
            value={anchor.taskAreaRadius ?? 84}
            onChange={(taskAreaRadius) => updateMap((draft) => ({ ...draft, workAnchors: draft.workAnchors.map((entry) => entry.id === anchor.id ? { ...entry, taskAreaRadius: Math.max(8, taskAreaRadius) } : entry) }))}
          />
        </PropertySection>
        <PropertySection title="Роли NPC">
          <MultiIdInput
            label="Разрешенные роли"
            value={anchor.allowedRoles}
            options={NPC_ROLES.map((role) => ({ id: role, label: role }))}
            onChange={(allowedRoles) => updateMap((draft) => ({ ...draft, workAnchors: draft.workAnchors.map((entry) => entry.id === anchor.id ? { ...entry, allowedRoles: allowedRoles as MysteryRoomNpcRole[] } : entry) }))}
          />
        </PropertySection>
      </div>
    );
  }

  if (selection.type === 'npc') {
    const npc = map.npcs.find((entry) => entry.id === selection.id);
    if (!npc) return <MissingSelection label={label} />;
    const updateNpc = (patch: Partial<typeof npc>) => updateMap((draft) => ({
      ...draft,
      npcs: draft.npcs.map((entry) => entry.id === npc.id ? { ...entry, ...patch } : entry),
    }));
    return (
      <div className="space-y-2">
        <PropertySection title="Актер">
          <ReadonlyProperty label="ID" value={npc.id} />
          <TextInput label="Имя" value={npc.name} onChange={(name) => updateNpc({ name })} />
          <SelectInput label="Тип" value={npc.actorKind ?? 'bot'} options={['bot', 'boss']} onChange={(actorKind) => updateNpc({ actorKind: actorKind as 'bot' | 'boss', actorPreset: actorKind === 'boss' ? 'boss' : npc.actorPreset ?? 'npc' })} />
          <SelectInput label="Пресет" value={npc.actorPreset ?? (npc.actorKind === 'boss' ? 'boss' : 'npc')} options={NPC_ACTOR_PRESETS} onChange={(actorPreset) => updateNpc({ actorPreset: actorPreset as MysteryRoomNpcActorPreset })} />
          <SelectInput label="Роль" value={npc.role} options={NPC_ROLES} onChange={(role) => updateNpc({ role: role as MysteryRoomNpcRole })} />
          <SelectInput label="Фракция" value={npc.faction ?? 'auto'} options={['auto', ...NPC_FACTIONS]} onChange={(faction) => updateNpc({ faction: faction === 'auto' ? undefined : faction as MysteryRoomNpcFaction })} />
          <SelectInput label="Профиль поведения" value={npc.behaviorProfile ?? 'auto'} options={['auto', ...NPC_BEHAVIOR_PROFILES]} onChange={(behaviorProfile) => updateNpc({ behaviorProfile: behaviorProfile === 'auto' ? undefined : behaviorProfile as MysteryRoomNpcBehaviorProfile })} />
          <ZoneInput map={map} label="Домашняя зона" value={npc.homeZoneId} onChange={(homeZoneId) => updateNpc({ homeZoneId })} />
        </PropertySection>
        <PropertySection title="Позиция">
          <PointInputs
            point={npc.spawn}
            onChange={(spawn) => updateNpc({ spawn, homeZoneId: resolveInspectorZoneIdAtPoint(map, spawn, npc.homeZoneId) })}
          />
          <MultiIdInput
            label="Доступы"
            value={npc.accessLevels}
            options={ACCESS_LEVELS.map((access) => ({ id: access, label: access }))}
            onChange={(accessLevels) => updateNpc({ accessLevels: accessLevels as MysteryRoomAccessLevel[] })}
          />
          <SelectInput label="Маршрут" value={npc.routeId ?? ''} options={['', ...map.patrolRoutes.map((route) => route.id)]} onChange={(routeId) => updateNpc({ routeId: routeId || undefined })} />
          <SelectInput label="Эвакуация" value={npc.evacuationNodeId} options={['', ...map.navNodes.map((node) => node.id)]} onChange={(evacuationNodeId) => updateNpc({ evacuationNodeId })} />
        </PropertySection>
        <PropertySection title="Обязанности">
          <SelectInput
            label="Запуск"
            value={npc.dutyActivation ?? 'auto'}
            options={NPC_DUTY_ACTIVATIONS}
            onChange={(dutyActivation) => updateNpc({ dutyActivation: dutyActivation as NonNullable<typeof npc.dutyActivation> })}
          />
          <MultiIdInput
            label="Триггеры"
            value={npc.dutyTriggerIds ?? []}
            options={map.triggers.map((trigger) => ({ id: trigger.id, label: trigger.label }))}
            onChange={(dutyTriggerIds) => updateNpc({ dutyTriggerIds })}
          />
          <MultiIdInput
            label="Двери"
            value={npc.dutyDoorIds ?? []}
            options={map.doors.map((door) => ({ id: door.id, label: door.label ?? door.id }))}
            onChange={(dutyDoorIds) => updateNpc({ dutyDoorIds })}
          />
        </PropertySection>
        <PropertySection title="Бой">
          <NumberInput label="Health" value={npc.health ?? npc.maxHealth ?? 100} onChange={(health) => updateNpc({ health })} />
          <NumberInput label="Max health" value={npc.maxHealth ?? npc.health ?? 100} onChange={(maxHealth) => updateNpc({ maxHealth })} />
          <NumberInput label="Radius" value={npc.radius ?? 16} onChange={(radius) => updateNpc({ radius })} />
          <NumberInput label="Speed" value={npc.speed ?? 118} onChange={(speed) => updateNpc({ speed })} />
          <NumberInput label="Scale" value={npc.scale ?? 1} step={0.1} onChange={(scale) => updateNpc({ scale })} />
          <ToggleInput label="Невидимый" value={npc.invisible ?? false} onChange={(invisible) => updateNpc({ invisible })} />
        </PropertySection>
        <PropertySection title="Зрение">
          <NumberInput label="Vision range" value={npc.visionRange ?? 560} onChange={(visionRange) => updateNpc({ visionRange })} />
          <NumberInput label="Half angle" value={npc.visionHalfAngleDeg ?? 42} onChange={(visionHalfAngleDeg) => updateNpc({ visionHalfAngleDeg })} />
          <ColorInput label="Цвет" value={npc.color} onChange={(color) => updateNpc({ color })} />
        </PropertySection>
      </div>
    );
  }

  if (selection.type === 'soundSource') {
    const source = map.soundSources.find((entry) => entry.id === selection.id);
    if (!source) return <MissingSelection label={label} />;
    const soundClipLabels = Object.fromEntries(map.soundDefinitions.map((sound) => [sound.id, sound.label || sound.id]));
    return (
      <div className="space-y-2">
        <PropertySection title="Основное">
          <ReadonlyProperty label="ID" value={source.id} />
          <TextInput
            label="Имя"
            hint="Подпись источника в редакторе и списках."
            value={source.label}
            onChange={(value) => updateMap((draft) => ({ ...draft, soundSources: draft.soundSources.map((entry) => entry.id === source.id ? { ...entry, label: value } : entry) }))}
          />
          <ZoneInput
            map={map}
            value={source.zoneId}
            hint="Логическая комната для фильтров редактора; на слышимость в игре не влияет."
            onChange={(zoneId) => updateMap((draft) => ({ ...draft, soundSources: draft.soundSources.map((entry) => entry.id === source.id ? { ...entry, zoneId } : entry) }))}
          />
        </PropertySection>
        {layerControls}
        <PropertySection title="Положение">
          <PointInputs point={source.position} onChange={(position) => updateMap((draft) => ({ ...draft, soundSources: draft.soundSources.map((entry) => entry.id === source.id ? { ...entry, position } : entry) }))} />
          <NumberInput
            label="Радиус зоны"
            hint="Дистанция от точки, на которой звук затихает (если не включена «по всей карте»). Вне радиуса петля не играет."
            value={source.radius}
            onChange={(radius) => updateMap((draft) => ({ ...draft, soundSources: draft.soundSources.map((entry) => entry.id === source.id ? { ...entry, radius } : entry) }))}
          />
        </PropertySection>
        <PropertySection title="Звук">
          <SelectInput
            label="Клип"
            hint="Идентификатор записи из списка определений звуков карты (вкладка ассетов / загрузки)."
            value={source.soundId}
            options={map.soundDefinitions.map((sound) => sound.id)}
            optionLabels={soundClipLabels}
            onChange={(soundId) => updateMap((draft) => ({ ...draft, soundSources: draft.soundSources.map((entry) => entry.id === source.id ? { ...entry, soundId } : entry) }))}
          />
          {map.soundDefinitions.find((sound) => sound.id === source.soundId)?.assetName ? (
            <ReadonlyProperty label="Файл" value={map.soundDefinitions.find((sound) => sound.id === source.soundId)?.assetName ?? source.soundId} />
          ) : null}
          <NumberInput
            label="Громкость"
            hint="Базовая громкость петли до пользовательских настроек музыки в игре."
            value={source.volume}
            step={0.05}
            onChange={(volume) => updateMap((draft) => ({ ...draft, soundSources: draft.soundSources.map((entry) => entry.id === source.id ? { ...entry, volume } : entry) }))}
          />
          <ToggleInput
            label="Зацикливать"
            hint="Если выключено, клип можно запускать сценарием (триггер «Воспроизвести звук»)."
            value={source.loop}
            onChange={(loop) => updateMap((draft) => ({ ...draft, soundSources: draft.soundSources.map((entry) => entry.id === source.id ? { ...entry, loop } : entry) }))}
          />
          <ToggleInput
            label="Слышно на всей карте"
            hint="Одинаковая громкость в любой точке карты, без затухания от источника. Радиус зоны не используется для громкости."
            value={Boolean(source.uniformMapVolume)}
            onChange={(uniformMapVolume) =>
              updateMap((draft) => ({
                ...draft,
                soundSources: draft.soundSources.map((entry) =>
                  entry.id === source.id ? { ...entry, uniformMapVolume, uniformVolumeInRadius: uniformMapVolume ? false : entry.uniformVolumeInRadius } : entry,
                ),
              }))
            }
          />
          <ToggleInput
            label="Ровно в зоне (без нарастания)"
            hint="Пока игрок внутри радиуса, громкость постоянная; у края зоны не тише, чем у центра. За пределами радиуса — тишина."
            value={Boolean(source.uniformVolumeInRadius)}
            disabled={Boolean(source.uniformMapVolume)}
            onChange={(uniformVolumeInRadius) =>
              updateMap((draft) => ({
                ...draft,
                soundSources: draft.soundSources.map((entry) => (entry.id === source.id ? { ...entry, uniformVolumeInRadius } : entry)),
              }))
            }
          />
        </PropertySection>
        <PropertySection title="Связи">
          <MultiIdInput
            label="Триггеры"
            value={source.triggerIds ?? []}
            options={map.triggers.map((trigger) => ({ id: trigger.id, label: trigger.label }))}
            onChange={(triggerIds) => updateMap((draft) => ({ ...draft, soundSources: draft.soundSources.map((entry) => entry.id === source.id ? { ...entry, triggerIds } : entry) }))}
          />
        </PropertySection>
      </div>
    );
  }

  if (selection.type === 'navNode') {
    const node = map.navNodes.find((entry) => entry.id === selection.id);
    if (!node) return <MissingSelection label={label} />;
    return (
      <div className="space-y-2">
        <PropertySection title="Основное">
          <ReadonlyProperty label="ID" value={node.id} />
          <ZoneInput map={map} value={node.zoneId} onChange={(zoneId) => updateMap((draft) => ({ ...draft, navNodes: draft.navNodes.map((entry) => entry.id === node.id ? { ...entry, zoneId } : entry) }))} />
          <SelectInput label="Kind" value={node.kind} options={['hub', 'door', 'work', 'incident', 'evac']} onChange={(kind) => updateMap((draft) => ({ ...draft, navNodes: draft.navNodes.map((entry) => entry.id === node.id ? { ...entry, kind: kind as any } : entry) }))} />
        </PropertySection>
        <PropertySection title="Transform">
          <PointInputs point={node.position} onChange={(position) => updateMap((draft) => ({ ...draft, navNodes: draft.navNodes.map((entry) => entry.id === node.id ? { ...entry, position } : entry) }))} />
        </PropertySection>
      </div>
    );
  }

  if (selection.type === 'spawn') {
    const spawnIndex = Number.isInteger(Number(selection.id)) ? Number(selection.id) : 0;
    const slots = normalizeEditorSpawnSlots(map);
    const slot = slots[spawnIndex] ?? slots[0];
    if (!slot) return <MissingSelection label={label} />;
    const updateSpawnSlot = (patch: Partial<typeof slot>) => updateMap((draft) => {
      const nextSlots = normalizeEditorSpawnSlots(draft).map((entry, index) => index === spawnIndex ? { ...entry, ...patch } : entry);
      return normalizeEditableMap({ ...draft, spawnSlots: nextSlots });
    });
    return (
      <div className="space-y-2">
        <SelectionActionSection label={label} selectedCount={selectedCount} canCopy={canCopySelection} onDuplicate={onDuplicateSelection} onCopy={onCopySelection} />
        <PropertySection title={`Спавн игрока ${spawnIndex + 1}`}>
          <TextInput label="Название" value={slot.label ?? `Игрок ${spawnIndex + 1}`} onChange={(label) => updateSpawnSlot({ label })} />
          <SelectInput
            label="Тип спавна"
            value={slot.actor === 'bot' ? 'bot' : 'player'}
            options={['player', 'bot']}
            onChange={(actor) => updateSpawnSlot({ actor: actor === 'bot' ? 'bot' : 'player' })}
          />
          <SelectInput
            label="Сторона"
            value={slot.side === 'B' ? 'B' : 'A'}
            options={['A', 'B']}
            onChange={(side) => updateSpawnSlot({ side: side === 'B' ? 'B' : 'A' })}
          />
          <PointInputs
            point={{ x: slot.x, y: slot.y }}
            onChange={(point) => {
              const zoneId = resolveInspectorZoneIdAtPoint(map, point, slot.room ?? slot.zoneId);
              updateSpawnSlot({ x: point.x, y: point.y, room: zoneId, zoneId });
            }}
          />
          <ZoneInput map={map} label="Зона" value={slot.room ?? slot.zoneId ?? map.spawnZoneId} onChange={(zoneId) => updateSpawnSlot({ room: zoneId, zoneId })} />
          <ReadonlyProperty label="Место в списке" value={String(spawnIndex + 1)} />
        </PropertySection>
      </div>
    );
  }

  if (selection.type === 'wall') {
    const index = Number(selection.id);
    const wall = map.walls[index];
    if (!wall) return <MissingSelection label={label} />;
    const wallIndices = activeSelections
      .filter((entry) => entry.type === 'wall')
      .map((entry) => Number(entry.id))
      .filter((wallIndex) => Number.isInteger(wallIndex) && Boolean(map.walls[wallIndex]));
    const targetWallIndices = wallIndices.length > 0 ? new Set(wallIndices) : new Set([index]);
    const updateTargetWalls = (updater: (entry: typeof wall, wallIndex: number) => typeof wall) =>
      updateMap((draft) => ({
        ...draft,
        walls: draft.walls.map((entry, wallIndex) => (targetWallIndices.has(wallIndex) ? updater(entry, wallIndex) : entry)),
      }));
    return (
      <div className="space-y-2">
        <SelectionActionSection label={label} selectedCount={selectedCount} canCopy={canCopySelection} onDuplicate={onDuplicateSelection} onCopy={onCopySelection} />
        {layerControls}
        <TextureLayerSection
          layers={textureLayersFromLegacy(wall.textures, wall.texture, 'wall')}
          onChange={(textures) => updateTargetWalls((entry) => ({ ...entry, textures, texture: textureLayerToSurfaceTexture(textures[0]) }))}
          baseColor={{
            color: wall.color ?? 0x0f172a,
            colorEnabled: wall.colorEnabled,
            colorOpacity: wall.colorOpacity,
            outlineColor: wall.outlineColor,
            outlineEnabled: wall.outlineEnabled,
            outlineOpacity: wall.outlineOpacity,
            outlineWidth: wall.outlineWidth,
            onColorChange: (color) => updateTargetWalls((entry) => ({ ...entry, color })),
            onColorEnabledChange: (colorEnabled) => updateTargetWalls((entry) => ({ ...entry, colorEnabled })),
            onColorOpacityChange: (colorOpacity) => updateTargetWalls((entry) => ({ ...entry, colorOpacity })),
            onOutlineColorChange: (outlineColor) => updateTargetWalls((entry) => ({ ...entry, outlineColor })),
            onOutlineEnabledChange: (outlineEnabled) => updateTargetWalls((entry) => ({ ...entry, outlineEnabled })),
            onOutlineOpacityChange: (outlineOpacity) => updateTargetWalls((entry) => ({ ...entry, outlineOpacity })),
            onOutlineWidthChange: (outlineWidth) => updateTargetWalls((entry) => ({ ...entry, outlineWidth })),
          }}
        />
        <PropertySection title="Start">
          <PointInputs point={wall.start} onChange={(start) => updateMap((draft) => ({ ...draft, walls: draft.walls.map((entry, wallIndex) => wallIndex === index ? { ...entry, start } : entry) }))} />
        </PropertySection>
        <PropertySection title="End">
          <PointInputs point={wall.end} onChange={(end) => updateMap((draft) => ({ ...draft, walls: draft.walls.map((entry, wallIndex) => wallIndex === index ? { ...entry, end } : entry) }))} />
        </PropertySection>
      </div>
    );
  }

  return <MissingSelection label={label} />;
}

function Panel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-[16px] border border-site-border bg-site-bg/72 p-3">
      <div className="text-[0.68rem] font-black uppercase tracking-[0.18em] text-site-secondary">{title}</div>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-[12px] border border-site-border bg-site-bg/60 px-3 py-2">
      <div className="text-[0.62rem] uppercase tracking-[0.16em] text-site-muted">{label}</div>
      <div className="mt-1 truncate text-sm font-semibold text-site-text">{value}</div>
    </div>
  );
}

type TextureSlotSelection =
  | { kind: 'layer'; id: string }
  | { kind: 'empty'; id: string };

function createEmptyTextureSlotId(): string {
  return `empty_texture_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

function replaceTextureLayerSource(current: MysteryRoomTextureLayer, nextSource: MysteryRoomTextureLayer): MysteryRoomTextureLayer {
  return normalizeTextureLayer(
    {
      ...current,
      builtinTextureId: nextSource.builtinTextureId,
      assetId: nextSource.assetId,
      assetUrl: nextSource.assetUrl,
      assetName: nextSource.assetName,
      assetMimeType: nextSource.assetMimeType,
      label: nextSource.label ?? nextSource.assetName ?? current.label,
    },
    0,
  );
}

function TextureLayerSection({
  layers,
  onChange,
  baseColor,
  defaultTexturesCollapsed = true,
  defaultTextureFit = 'repeat',
}: {
  layers: MysteryRoomTextureLayer[];
  onChange: (layers: MysteryRoomTextureLayer[]) => void;
  baseColor?: TextureBaseColorControls;
  defaultTexturesCollapsed?: boolean;
  defaultTextureFit?: MysteryRoomAssetFit;
}) {
  const normalized = layers.map(normalizeTextureLayer);
  const [scaleLocks, setScaleLocks] = useState<Record<string, boolean>>({});
  const [emptySlots, setEmptySlots] = useState<string[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<TextureSlotSelection | null>(null);
  const [draggingLayerId, setDraggingLayerId] = useState<string | null>(null);
  const updateLayer = (index: number, updater: (layer: MysteryRoomTextureLayer) => MysteryRoomTextureLayer) => {
    onChange(normalized.map((layer, layerIndex) => (layerIndex === index ? normalizeTextureLayer(updater(layer), layerIndex) : layer)));
  };
  const updateLayerScale = (index: number, axis: 'x' | 'y', value: number, locked: boolean) => {
    const scale = normalizeTextureScale(value);
    updateLayer(index, (entry) => ({
      ...entry,
      scaleX: locked || axis === 'x' ? scale : normalizeTextureScale(entry.scaleX),
      scaleY: locked || axis === 'y' ? scale : normalizeTextureScale(entry.scaleY),
    }));
  };
  const toggleScaleLock = (index: number, layer: MysteryRoomTextureLayer, locked: boolean) => {
    setScaleLocks((current) => ({ ...current, [layer.id]: !locked }));
    if (!locked) {
      updateLayer(index, (entry) => {
        const scale = normalizeTextureScale(entry.scaleX);
        return { ...entry, scaleX: scale, scaleY: scale };
      });
    }
  };
  const moveLayer = (index: number, direction: -1 | 1) => {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= normalized.length) return;
    const next = [...normalized];
    const [layer] = next.splice(index, 1);
    next.splice(nextIndex, 0, layer);
    onChange(next);
  };
  const moveLayerToIndex = (sourceId: string, targetIndex: number) => {
    const sourceIndex = normalized.findIndex((layer) => layer.id === sourceId);
    if (sourceIndex < 0 || targetIndex < 0 || targetIndex >= normalized.length || sourceIndex === targetIndex) return;
    const next = [...normalized];
    const [layer] = next.splice(sourceIndex, 1);
    next.splice(sourceIndex < targetIndex ? targetIndex - 1 : targetIndex, 0, layer);
    onChange(next);
  };
  const addDefaultTexture = (textureId: string) => {
    const texture = getMysteryRoomDefaultTexture(textureId);
    if (!texture) return;
    const nextLayer = createTextureLayerFromDefaultTexture(texture, normalized.length, defaultTextureFit);
    const selectedEmptyId = selectedSlot?.kind === 'empty' ? selectedSlot.id : null;
    const targetEmptyId = selectedEmptyId && emptySlots.includes(selectedEmptyId) ? selectedEmptyId : emptySlots[0];
    if (targetEmptyId) {
      onChange([...normalized, nextLayer]);
      setEmptySlots((current) => current.filter((slotId) => slotId !== targetEmptyId));
      setSelectedSlot({ kind: 'layer', id: nextLayer.id });
      return;
    }
    const selectedLayerIndex = selectedSlot?.kind === 'layer'
      ? normalized.findIndex((layer) => layer.id === selectedSlot.id)
      : -1;
    const targetIndex = selectedLayerIndex >= 0 ? selectedLayerIndex : normalized.length - 1;
    if (targetIndex >= 0) {
      onChange(normalized.map((layer, index) => (index === targetIndex ? replaceTextureLayerSource(layer, nextLayer) : layer)));
      setSelectedSlot({ kind: 'layer', id: normalized[targetIndex].id });
      return;
    }
    onChange([nextLayer]);
    setSelectedSlot({ kind: 'layer', id: nextLayer.id });
  };
  const addEmptySlot = () => {
    const id = createEmptyTextureSlotId();
    setEmptySlots((current) => [...current, id]);
    setSelectedSlot({ kind: 'empty', id });
  };
  const hasOutlineControls = Boolean(
    baseColor?.onOutlineColorChange &&
      baseColor.onOutlineEnabledChange &&
      baseColor.onOutlineOpacityChange &&
      baseColor.onOutlineWidthChange,
  );

  return (
    <PropertySection title="Текстура">
      {baseColor ? (
        <>
          <ToggleInput label="Цвет включен" value={baseColor.colorEnabled ?? true} onChange={baseColor.onColorEnabledChange} />
          <ColorInput label="Цвет внутри" value={baseColor.color} onChange={baseColor.onColorChange} />
          <NumberInput
            label="Прозрачность внутри"
            value={normalizeBaseColorOpacity(baseColor.colorOpacity, 0.9)}
            step={0.05}
            onChange={(opacity) => baseColor.onColorOpacityChange(normalizeBaseColorOpacity(opacity, 0.9))}
          />
          {hasOutlineControls ? (
            <>
              <ToggleInput label="Обводка включена" value={baseColor.outlineEnabled ?? true} onChange={baseColor.onOutlineEnabledChange!} />
              <ColorInput label="Цвет обводки" value={baseColor.outlineColor ?? 0xf8fafc} onChange={baseColor.onOutlineColorChange!} />
              <NumberInput
                label="Прозрачность обводки"
                value={normalizeBaseColorOpacity(baseColor.outlineOpacity, 0.5)}
                step={0.05}
                onChange={(opacity) => baseColor.onOutlineOpacityChange!(normalizeBaseColorOpacity(opacity, 0.5))}
              />
              <NumberInput
                label="Толщина обводки"
                value={normalizeOutlineWidth(baseColor.outlineWidth, 3)}
                step={1}
                onChange={(width) => baseColor.onOutlineWidthChange!(normalizeOutlineWidth(width, 3))}
              />
            </>
          ) : null}
        </>
      ) : null}
      <ReadonlyProperty label="Слои" value={normalized.length ? `${normalized.length} (сверху вниз)` : 'нет'} />
      <DefaultTextureSelect onSelect={addDefaultTexture} initiallyCollapsed={defaultTexturesCollapsed} />
      {normalized.length === 0 ? (
        <div className="px-3 py-2 text-xs leading-5 text-site-muted">
          Добавьте ячейку и выберите стандартную текстуру или назначьте изображение из панели `Ассеты карты`.
        </div>
      ) : null}
      <div className="space-y-2 px-2 py-2">
        {[...normalized].map((layer, index) => ({ layer, index })).reverse().map(({ layer, index }) => (
          <div
            key={layer.id}
            draggable
            onClick={() => setSelectedSlot({ kind: 'layer', id: layer.id })}
            onDragStart={() => setDraggingLayerId(layer.id)}
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => {
              event.preventDefault();
              if (draggingLayerId) moveLayerToIndex(draggingLayerId, index);
              setDraggingLayerId(null);
            }}
            onDragEnd={() => setDraggingLayerId(null)}
            className={`rounded-[10px] border p-2 transition ${
              selectedSlot?.kind === 'layer' && selectedSlot.id === layer.id
                ? 'border-site-accent/45 bg-site-accent/8'
                : 'border-site-border bg-site-surface/[0.025]'
            }`}
          >
            <div className="mb-2 flex items-center gap-2">
              <input
                type="checkbox"
                checked={layer.visible !== false}
                onChange={(event) => updateLayer(index, (entry) => ({ ...entry, visible: event.target.checked }))}
                className="size-4 accent-cyan-300"
                title="Показать слой"
              />
              <input
                value={layer.label ?? layer.assetName ?? `Текстура ${index + 1}`}
                onChange={(event) => updateLayer(index, (entry) => ({ ...entry, label: event.target.value }))}
                className="h-8 min-w-0 flex-1 rounded-[8px] border border-site-border bg-site-bg/70 px-2 text-xs font-semibold text-site-text outline-none focus:border-site-accent/45"
              />
              <button type="button" onClick={() => moveLayer(index, -1)} disabled={index === 0} className="rounded-[7px] border border-site-border px-2 py-1 text-xs text-site-text disabled:opacity-35">Ниже</button>
              <button type="button" onClick={() => moveLayer(index, 1)} disabled={index === normalized.length - 1} className="rounded-[7px] border border-site-accent/25 bg-site-accent/10 px-2 py-1 text-xs text-site-accent disabled:opacity-35">Выше</button>
              <button type="button" onClick={() => onChange(normalized.filter((_, layerIndex) => layerIndex !== index))} className="rounded-[7px] border border-site-danger/20 bg-site-danger/10 px-2 py-1 text-xs text-site-danger">Удалить</button>
            </div>
            <ReadonlyProperty label="Ассет" value={layer.assetName ?? getMysteryRoomDefaultTexture(layer.builtinTextureId)?.label ?? layer.assetId ?? 'image'} />
            <SelectInput label="Режим" value={layer.fit ?? 'repeat'} options={SURFACE_TEXTURE_FITS} onChange={(fit) => updateLayer(index, (entry) => ({ ...entry, fit: fit as MysteryRoomAssetFit }))} />
            <NumberInput label="Прозрачность" value={normalizeSurfaceTextureOpacity(layer.opacity)} step={0.05} onChange={(opacity) => updateLayer(index, (entry) => ({ ...entry, opacity: normalizeSurfaceTextureOpacity(opacity) }))} />
            <TexturePairRow
              label="Сдвиг"
              x={finiteEditorNumber(layer.offsetX, 0)}
              y={finiteEditorNumber(layer.offsetY, 0)}
              step={1}
              onXChange={(offsetX) => updateLayer(index, (entry) => ({ ...entry, offsetX }))}
              onYChange={(offsetY) => updateLayer(index, (entry) => ({ ...entry, offsetY }))}
            />
            <TextureScaleRow
              locked={scaleLocks[layer.id] ?? true}
              scaleX={normalizeTextureScale(layer.scaleX)}
              scaleY={normalizeTextureScale(layer.scaleY)}
              onScaleXChange={(scaleX) => updateLayerScale(index, 'x', scaleX, scaleLocks[layer.id] ?? true)}
              onScaleYChange={(scaleY) => updateLayerScale(index, 'y', scaleY, scaleLocks[layer.id] ?? true)}
              onToggleLock={() => toggleScaleLock(index, layer, scaleLocks[layer.id] ?? true)}
            />
            <TexturePairRow
              label="Повтор"
              x={normalizeTextureScale(layer.repeatX)}
              y={normalizeTextureScale(layer.repeatY)}
              step={0.25}
              onXChange={(repeatX) => updateLayer(index, (entry) => ({ ...entry, repeatX: normalizeTextureScale(repeatX) }))}
              onYChange={(repeatY) => updateLayer(index, (entry) => ({ ...entry, repeatY: normalizeTextureScale(repeatY) }))}
            />
            <NumberInput label="Поворот" value={finiteEditorNumber(layer.rotation, 0)} step={1} onChange={(rotation) => updateLayer(index, (entry) => ({ ...entry, rotation }))} />
            {TEXTURE_EFFECT_FIELDS.map((field) => (
              <NumberInput
                key={field.key}
                label={field.label}
                value={normalizeTextureEffectIntensity(layer.effects?.[field.key])}
                step={5}
                onChange={(value) => updateLayer(index, (entry) => ({
                  ...entry,
                  effects: {
                    ...defaultTextureEffects(),
                    ...(entry.effects ?? {}),
                    [field.key]: normalizeTextureEffectIntensity(value),
                  },
                }))}
              />
            ))}
          </div>
        ))}
        {emptySlots.map((slotId) => (
          <button
            key={slotId}
            type="button"
            onClick={() => setSelectedSlot({ kind: 'empty', id: slotId })}
            className={`w-full rounded-[10px] border border-dashed px-3 py-3 text-left text-xs transition ${
              selectedSlot?.kind === 'empty' && selectedSlot.id === slotId
                ? 'border-site-accent/55 bg-site-accent/8 text-site-accent'
                : 'border-site-border bg-site-bg/40 text-site-muted hover:border-site-border hover:text-site-text'
            }`}
          >
            Свободная ячейка текстуры
          </button>
        ))}
        <button
          type="button"
          onClick={addEmptySlot}
          className="w-full rounded-[9px] border border-site-accent/25 bg-site-accent/10 px-3 py-2 text-xs font-semibold text-site-accent transition hover:border-site-accent/55 hover:bg-site-accent/15"
        >
          Добавить ячейку текстуры
        </button>
      </div>
    </PropertySection>
  );
}

function TexturePairRow({
  label,
  x,
  y,
  step,
  onXChange,
  onYChange,
}: {
  label: string;
  x: number;
  y: number;
  step: number;
  onXChange: (value: number) => void;
  onYChange: (value: number) => void;
}) {
  return (
    <PropertyRow label={label}>
      <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] items-center gap-1.5">
        <label className="flex min-w-0 items-center gap-1">
          <span className="text-[0.62rem] font-black text-site-muted">X</span>
          <NumberEditorInput value={x} step={step} onChange={onXChange} />
        </label>
        <label className="flex min-w-0 items-center gap-1">
          <span className="text-[0.62rem] font-black text-site-muted">Y</span>
          <NumberEditorInput value={y} step={step} onChange={onYChange} />
        </label>
      </div>
    </PropertyRow>
  );
}

function TextureScaleRow({
  locked,
  scaleX,
  scaleY,
  onScaleXChange,
  onScaleYChange,
  onToggleLock,
}: {
  locked: boolean;
  scaleX: number;
  scaleY: number;
  onScaleXChange: (value: number) => void;
  onScaleYChange: (value: number) => void;
  onToggleLock: () => void;
}) {
  return (
    <PropertyRow label="Масштаб">
      <div className="grid grid-cols-[minmax(0,1fr)_32px_minmax(0,1fr)] items-center gap-1.5">
        <label className="flex min-w-0 items-center gap-1">
          <span className="text-[0.62rem] font-black text-site-muted">X</span>
          <NumberEditorInput value={scaleX} step={0.05} onChange={onScaleXChange} />
        </label>
        <button
          type="button"
          onClick={onToggleLock}
          aria-pressed={locked}
          title={locked ? 'Развязать масштаб X/Y' : 'Связать масштаб X/Y'}
          className={`grid h-8 place-items-center rounded-[8px] border text-xs font-black transition ${
            locked
              ? 'border-site-accent/35 bg-site-accent/12 text-site-accent hover:border-site-accent/60'
              : 'border-site-border bg-site-bg/70 text-site-muted hover:border-site-border hover:text-site-text'
          }`}
        >
          <LockGlyph locked={locked} />
        </button>
        <label className="flex min-w-0 items-center gap-1">
          <span className="text-[0.62rem] font-black text-site-muted">Y</span>
          <NumberEditorInput value={scaleY} step={0.05} onChange={onScaleYChange} />
        </label>
      </div>
    </PropertyRow>
  );
}

function LockGlyph({ locked }: { locked: boolean }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="size-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="5" y="10" width="14" height="10" rx="2" />
      {locked ? <path d="M8 10V7a4 4 0 0 1 8 0v3" /> : <path d="M8 10V7a4 4 0 0 1 7.2-2.4" />}
      <path d="M12 14v2" />
    </svg>
  );
}

function DefaultTextureSelect({
  onSelect,
  initiallyCollapsed = false,
}: {
  onSelect: (textureId: string) => void;
  initiallyCollapsed?: boolean;
}) {
  const byStyle = MYSTERY_ROOM_DEFAULT_TEXTURE_STYLES.map((style) => ({
    style,
    textures: MYSTERY_ROOM_DEFAULT_TEXTURES.filter((texture) => texture.style === style),
  })).filter((group) => group.textures.length > 0);
  const [activeStyle, setActiveStyle] = useState<MysteryRoomDefaultTextureStyle>(byStyle[0]?.style ?? 'realism');
  const [collapsed, setCollapsed] = useState(initiallyCollapsed);
  const activeGroup = byStyle.find((group) => group.style === activeStyle) ?? byStyle[0];

  useEffect(() => {
    setCollapsed(initiallyCollapsed);
  }, [initiallyCollapsed]);

  if (!activeGroup) {
    return null;
  }

  return (
    <div className="px-2 py-2">
      <button
        type="button"
        onClick={() => setCollapsed((value) => !value)}
        className="mb-2 flex w-full items-center justify-between rounded-[9px] border border-site-border bg-site-bg/70 px-3 py-2 text-left text-xs font-semibold text-site-text transition hover:border-site-border hover:bg-site-surface/[0.04]"
      >
        <span>Стандартные текстуры</span>
        <span className="text-site-muted">{collapsed ? 'Развернуть' : 'Свернуть'}</span>
      </button>
      {collapsed ? null : (
        <>
          <div className="mb-2 flex gap-1 overflow-x-auto rounded-[9px] border border-site-border bg-site-bg/70 p-1">
            {byStyle.map((group) => (
              <button
                key={group.style}
                type="button"
                onClick={() => setActiveStyle(group.style)}
                className={`shrink-0 rounded-[7px] px-2 py-1 text-[0.68rem] font-semibold transition ${
                  group.style === activeGroup.style
                    ? 'bg-site-accent/18 text-site-accent ring-1 ring-site-accent/35'
                    : 'text-site-muted hover:bg-site-surface/[0.06] hover:text-site-text'
                }`}
              >
                {defaultTextureStyleLabel(group.style)} <span className="text-site-muted">{group.textures.length}</span>
              </button>
            ))}
          </div>
          <div className="grid max-h-72 grid-cols-2 gap-2 overflow-y-auto pr-1">
            {activeGroup.textures.map((texture) => (
              <button
                key={texture.id}
                type="button"
                onClick={() => onSelect(texture.id)}
                className="group overflow-hidden rounded-[9px] border border-site-border bg-site-surface/[0.03] text-left transition hover:border-site-accent/55 hover:bg-site-accent/8 focus:outline-none focus:ring-2 focus:ring-site-accent/45"
                title={`Добавить: ${texture.label}`}
              >
                <img
                  src={texture.assetUrl}
                  alt=""
                  className="h-16 w-full object-cover"
                  loading="lazy"
                  draggable={false}
                />
                <span className="block truncate px-2 py-1.5 text-[0.68rem] font-semibold text-site-text group-hover:text-site-accent">
                  {texture.label.replace(`${defaultTextureStyleLabel(texture.style)} / `, '')}
                </span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function defaultTextureStyleLabel(style: MysteryRoomDefaultTextureStyle): string {
  if (style === 'realism') return 'Реализм';
  if (style === 'horror') return 'Хоррор';
  if (style === 'decor') return 'Декор';
  if (style === 'sci_fi') return 'Sci-Fi';
  return 'pixel';
}

type InventoryIconEditable = {
  inventoryIcon?: string;
  inventoryIconAssetId?: string;
  inventoryIconAssetUrl?: string;
  inventoryIconAssetName?: string;
  inventoryIconAssetMimeType?: string;
};

type ObjectAppearancePatch = Partial<Pick<
  MysteryRoomCustomObjectDefinition,
  | 'assetId'
  | 'assetUrl'
  | 'assetName'
  | 'assetMimeType'
  | 'assetFit'
  | 'assetTransform'
  | 'assetAlphaCollisionEnabled'
  | 'textures'
  | 'visualSkinId'
  | 'visualSkinName'
  | 'colorEnabled'
  | 'colorOpacity'
  | 'outlineEnabled'
  | 'outlineOpacity'
  | 'outlineWidth'
>>;

function ObjectAppearanceAssetSection({
  object,
  imageAssets,
  onChange,
}: {
  object: MysteryRoomCustomObjectDefinition;
  imageAssets: MrMapAsset[];
  onChange: (patch: ObjectAppearancePatch) => void;
}) {
  const selectedAsset = imageAssets.find((asset) => asset.id === object.assetId) ?? null;
  const previewUrl = selectedAsset ? mapAssetPreviewUrl(selectedAsset) : object.assetUrl ?? '';
  return (
    <PropertySection title="Картинка объекта">
      <PropertyRow label="Изображение">
        <div className="space-y-2">
          <select
            value={object.assetId ?? ''}
            onChange={(event) => {
              const asset = imageAssets.find((item) => item.id === event.target.value) ?? null;
              onChange(objectAppearanceAssetPatch(asset));
            }}
            className="h-8 w-full rounded-[8px] border border-site-border bg-site-bg/70 px-2 text-xs text-site-text outline-none focus:border-site-accent/45"
          >
            <option value="">Без изображения</option>
            {imageAssets.map((asset) => (
              <option key={asset.id} value={asset.id}>
                {asset.name || asset.filename}
              </option>
            ))}
          </select>
          <div className="flex items-center gap-2">
            <div
              className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-[8px] border border-site-border text-[0.62rem] text-site-muted"
              style={mapAssetCheckerBackground()}
            >
              {previewUrl ? <img src={previewUrl} alt="" className="h-full w-full object-contain" draggable={false} /> : 'нет'}
            </div>
            <div className="min-w-0 flex-1 text-[0.68rem] text-site-muted">
              {previewUrl ? 'Картинка заменяет линии, цвет и сгенерированный вид объекта.' : 'Выберите изображение из ассетов карты.'}
            </div>
          </div>
          {(object.assetId || object.assetUrl) ? (
            <button
              type="button"
              onClick={() => onChange(objectAppearanceAssetPatch(null))}
              className="w-full rounded-[8px] border border-site-border bg-site-surface/[0.04] px-2 py-1.5 text-[0.68rem] font-semibold text-site-text transition hover:border-site-border"
            >
              Сбросить картинку
            </button>
          ) : null}
        </div>
      </PropertyRow>
    </PropertySection>
  );
}

function InventoryIconFields({
  entry,
  imageAssets,
  onChange,
}: {
  entry: InventoryIconEditable;
  imageAssets: MrMapAsset[];
  onChange: (patch: Partial<InventoryIconEditable>) => void;
}) {
  const selectedAsset = imageAssets.find((asset) => asset.id === entry.inventoryIconAssetId) ?? null;
  const previewUrl = selectedAsset ? mapAssetPreviewUrl(selectedAsset) : entry.inventoryIconAssetUrl ?? '';

  return (
    <>
      <TextInput
        label="Иконка"
        value={entry.inventoryIcon ?? ''}
        onChange={(inventoryIcon) => onChange({ inventoryIcon: normalizeInventoryIconEditorValue(inventoryIcon) })}
      />
      <PropertyRow label="Картинка">
        <div className="space-y-2">
          <select
            value={entry.inventoryIconAssetId ?? ''}
            onChange={(event) => {
              const asset = imageAssets.find((item) => item.id === event.target.value) ?? null;
              onChange(inventoryIconAssetPatch(asset));
            }}
            className="h-8 w-full rounded-[8px] border border-site-border bg-site-bg/70 px-2 text-xs text-site-text outline-none focus:border-site-accent/45"
          >
            <option value="">Без изображения</option>
            {imageAssets.map((asset) => (
              <option key={asset.id} value={asset.id}>
                {asset.name || asset.filename}
              </option>
            ))}
          </select>
          <div className="flex items-center gap-2">
            <div
              className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-[10px] border border-site-border bg-site-bg/70 text-[0.62rem] font-black text-site-muted"
              style={mapAssetCheckerBackground()}
            >
              {previewUrl ? <img src={previewUrl} alt="" className="h-full w-full object-contain" draggable={false} /> : 'ICON'}
            </div>
            <div className="min-w-0 flex-1 text-[0.68rem] leading-4 text-site-muted">
              <div className="truncate text-site-secondary">{selectedAsset?.name ?? entry.inventoryIconAssetName ?? 'Буквы используются как fallback'}</div>
              <div className="truncate">{selectedAsset?.type ?? entry.inventoryIconAssetMimeType ?? 'PNG/GIF/WebP/JPEG из ассетов карты'}</div>
            </div>
            {(entry.inventoryIconAssetId || entry.inventoryIconAssetUrl) && (
              <button
                type="button"
                onClick={() => onChange(inventoryIconAssetPatch(null))}
                className="rounded-[8px] border border-site-danger/20 bg-site-danger/10 px-2 py-1 text-[0.68rem] font-semibold text-site-danger transition hover:border-site-danger/55"
              >
                Сброс
              </button>
            )}
          </div>
        </div>
      </PropertyRow>
    </>
  );
}

function normalizeInventoryIconEditorValue(value: string): string | undefined {
  const normalized = value.trim().slice(0, 4).toUpperCase();
  return normalized || undefined;
}

function inventoryIconAssetPatch(asset: MrMapAsset | null): Partial<InventoryIconEditable> {
  if (!asset) {
    return {
      inventoryIconAssetId: undefined,
      inventoryIconAssetUrl: undefined,
      inventoryIconAssetName: undefined,
      inventoryIconAssetMimeType: undefined,
    };
  }
  return {
    inventoryIconAssetId: asset.id,
    inventoryIconAssetUrl: asset.url,
    inventoryIconAssetName: asset.name || asset.filename,
    inventoryIconAssetMimeType: asset.type,
  };
}

function objectAppearanceAssetPatch(asset: MrMapAsset | null): ObjectAppearancePatch {
  if (!asset) {
    return {
      assetId: undefined,
      assetUrl: undefined,
      assetName: undefined,
      assetMimeType: undefined,
      assetFit: undefined,
      assetTransform: undefined,
      assetAlphaCollisionEnabled: false,
      visualSkinId: undefined,
      visualSkinName: undefined,
    };
  }
  return {
    assetId: asset.id,
    assetUrl: asset.url,
    assetName: asset.name || asset.filename,
    assetMimeType: asset.type,
    assetFit: 'contain',
    assetTransform: { offsetX: 0, offsetY: 0, scaleX: 1, scaleY: 1, rotation: 0, clipToBounds: true },
    assetAlphaCollisionEnabled: asset.type === 'image/png' || asset.type === 'image/webp' || asset.type === 'image/gif',
    textures: undefined,
    visualSkinId: undefined,
    visualSkinName: undefined,
    colorEnabled: false,
    colorOpacity: 0,
    outlineEnabled: false,
    outlineOpacity: 0,
    outlineWidth: 0,
  };
}

function PropertySection({ title, children }: { title: string; children: ReactNode }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="overflow-hidden rounded-[12px] border border-site-border bg-site-bg/42">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className="flex w-full items-center justify-between border-b border-site-border bg-site-surface/[0.035] px-3 py-1.5 text-left text-[0.62rem] font-black uppercase tracking-[0.16em] text-site-accent/80"
      >
        <span>{title}</span>
        <span className="text-site-accent/55">{open ? '−' : '+'}</span>
      </button>
      {open ? <div className="divide-y divide-site-border">{children}</div> : null}
    </div>
  );
}

function PropertyRow({ label, children, hint }: { label: string; children: ReactNode; hint?: string }) {
  return (
    <div
      className="grid min-h-[38px] grid-cols-[112px_minmax(0,1fr)] items-center gap-2 px-3 py-1.5 text-xs text-site-muted"
      title={hint}
    >
      <span className="truncate">{label}</span>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

function roundEditorInputValue(value: number): number {
  return Math.round(value * 1_000_000) / 1_000_000;
}

function kelvinToRgbNumber(kelvin: number): number {
  const temp = Math.max(10, Math.min(400, kelvin / 100));
  const red = temp <= 66 ? 255 : clampEditorColor(329.698727446 * Math.pow(temp - 60, -0.1332047592));
  const green = temp <= 66
    ? clampEditorColor(99.4708025861 * Math.log(temp) - 161.1195681661)
    : clampEditorColor(288.1221695283 * Math.pow(temp - 60, -0.0755148492));
  const blue = temp >= 66 ? 255 : temp <= 19 ? 0 : clampEditorColor(138.5177312231 * Math.log(temp - 10) - 305.0447927307);
  return (red << 16) + (green << 8) + blue;
}

function clampEditorColor(value: number): number {
  return Math.max(0, Math.min(255, Math.round(value)));
}

function NumberEditorInput({
  value,
  onChange,
  step = 1,
  className = 'h-8 w-full rounded-[8px] border border-site-border bg-site-bg/70 px-2 text-xs text-site-text outline-none focus:border-site-accent/45',
}: {
  value: number;
  onChange: (value: number) => void;
  step?: number;
  className?: string;
}) {
  const handleWheel = (event: ReactWheelEvent<HTMLInputElement>) => {
    if (event.currentTarget !== document.activeElement || event.deltaY === 0) return;
    event.preventDefault();
    const direction = event.deltaY < 0 ? 1 : -1;
    const multiplier = event.shiftKey ? 10 : event.altKey ? 0.1 : 1;
    const current = Number.isFinite(value) ? value : 0;
    onChange(roundEditorInputValue(current + direction * step * multiplier));
  };

  return (
    <input
      type="number"
      step={step}
      value={Number.isFinite(value) ? value : 0}
      onChange={(event) => onChange(Number(event.target.value))}
      onWheel={handleWheel}
      className={className}
    />
  );
}

function PropertyTextInput({ label, value, onChange, hint }: { label: string; value: string; onChange: (value: string) => void; hint?: string }) {
  return (
    <PropertyRow label={label} hint={hint}>
      <input value={value} onChange={(event) => onChange(event.target.value)} className="h-8 w-full rounded-[8px] border border-site-border bg-site-bg/70 px-2 text-xs text-site-text outline-none focus:border-site-accent/45" />
    </PropertyRow>
  );
}

function PropertyTextareaInput({ label, value, onChange, rows = 3 }: { label: string; value: string; onChange: (value: string) => void; rows?: number }) {
  return (
    <PropertyRow label={label}>
      <textarea value={value} rows={rows} onChange={(event) => onChange(event.target.value)} className="w-full resize-y rounded-[8px] border border-site-border bg-site-bg/70 px-2 py-1.5 text-xs text-site-text outline-none placeholder:text-site-muted focus:border-site-accent/45" />
    </PropertyRow>
  );
}

function PropertyNumberInput({ label, value, onChange, step = 1, hint }: { label: string; value: number; onChange: (value: number) => void; step?: number; hint?: string }) {
  return (
    <PropertyRow label={label} hint={hint}>
      <NumberEditorInput value={value} onChange={onChange} step={step} />
    </PropertyRow>
  );
}

function SliderNumberInput({
  label,
  value,
  min,
  max,
  step = 1,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  onChange: (value: number) => void;
}) {
  const safeValue = Number.isFinite(value) ? Math.max(min, Math.min(max, value)) : min;
  return (
    <PropertyRow label={label}>
      <div className="grid grid-cols-[minmax(0,1fr)_72px] items-center gap-2">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={safeValue}
          onChange={(event) => onChange(roundEditorInputValue(Number(event.target.value)))}
          className="h-2 w-full accent-cyan-300"
        />
        <NumberEditorInput
          value={safeValue}
          onChange={(next) => onChange(Math.max(min, Math.min(max, roundEditorInputValue(next))))}
          step={step}
          className="h-8 w-full rounded-[8px] border border-site-border bg-site-bg/70 px-2 text-right text-xs text-site-text outline-none focus:border-site-accent/45"
        />
      </div>
    </PropertyRow>
  );
}

function PropertyColorInput({ label, value, onChange }: { label: string; value: number; onChange: (value: number) => void }) {
  return (
    <PropertyRow label={label}>
      <input type="color" value={numberToHexColor(value)} onChange={(event) => onChange(hexColorToNumber(event.target.value, value))} className="h-8 w-full rounded-[8px] border border-site-border bg-site-bg/70 px-1 py-1" />
    </PropertyRow>
  );
}

function PropertySelectInput({ label, value, options, optionLabels, onChange, hint }: { label: string; value: string; options: string[]; optionLabels?: Record<string, string>; onChange: (value: string) => void; hint?: string }) {
  return (
    <PropertyRow label={label} hint={hint}>
      <select value={value} onChange={(event) => onChange(event.target.value)} className="h-8 w-full rounded-[8px] border border-site-border bg-site-bg/70 px-2 text-xs text-site-text outline-none focus:border-site-accent/45">
        {options.map((option) => (
          <option key={option} value={option}>{optionLabels?.[option] ?? (option || 'none')}</option>
        ))}
      </select>
    </PropertyRow>
  );
}

function PropertyToggleInput({
  label,
  value,
  onChange,
  hint,
  disabled,
}: {
  label: string;
  value: boolean;
  onChange: (value: boolean) => void;
  hint?: string;
  disabled?: boolean;
}) {
  return (
    <PropertyRow label={label} hint={hint}>
      <input
        type="checkbox"
        checked={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        className="size-4 accent-cyan-300 disabled:cursor-not-allowed disabled:opacity-40"
      />
    </PropertyRow>
  );
}

function ReadonlyProperty({ label, value }: { label: string; value: string | number }) {
  return (
    <PropertyRow label={label}>
      <span className="block truncate text-xs font-semibold text-site-text">{value}</span>
    </PropertyRow>
  );
}

function TextInput({ label, value, onChange, hint }: { label: string; value: string; onChange: (value: string) => void; hint?: string }) {
  return <PropertyTextInput label={label} value={value} onChange={onChange} hint={hint} />;
}

function NumberInput({ label, value, onChange, step = 1, hint }: { label: string; value: number; onChange: (value: number) => void; step?: number; hint?: string }) {
  return <PropertyNumberInput label={label} value={value} onChange={onChange} step={step} hint={hint} />;
}

function ColorInput({ label, value, onChange }: { label: string; value: number; onChange: (value: number) => void }) {
  return <PropertyColorInput label={label} value={value} onChange={onChange} />;
}

function SelectInput({ label, value, options, optionLabels, onChange, hint }: { label: string; value: string; options: string[]; optionLabels?: Record<string, string>; onChange: (value: string) => void; hint?: string }) {
  return <PropertySelectInput label={label} value={value} options={options} optionLabels={optionLabels} onChange={onChange} hint={hint} />;
}

function WorldLabelVisibilityInput({
  value,
  onChange,
}: {
  value: MysteryRoomWorldLabelVisibility | undefined;
  onChange: (value: MysteryRoomWorldLabelVisibility) => void;
}) {
  return (
    <PropertyRow label="Надпись в игре">
      <select
        value={value ?? 'hidden'}
        onChange={(event) => onChange(event.target.value as MysteryRoomWorldLabelVisibility)}
        className="h-8 w-full rounded-[8px] border border-site-border bg-site-bg/70 px-2 text-xs text-site-text outline-none focus:border-site-accent/45"
      >
        {WORLD_LABEL_VISIBILITY_OPTIONS.map((option) => (
          <option key={option} value={option}>{worldLabelVisibilityLabel(option)}</option>
        ))}
      </select>
    </PropertyRow>
  );
}

function ToggleInput({
  label,
  value,
  onChange,
  hint,
  disabled,
}: {
  label: string;
  value: boolean;
  onChange: (value: boolean) => void;
  hint?: string;
  disabled?: boolean;
}) {
  return <PropertyToggleInput label={label} value={value} onChange={onChange} hint={hint} disabled={disabled} />;
}

function ZoneInput({ map, value, onChange, label = 'Зона', hint }: { map: MysteryRoomMapDefinition; value: string; onChange: (value: string) => void; label?: string; hint?: string }) {
  return <SelectInput label={label} value={value} options={map.rooms.map((room) => room.id)} onChange={onChange} hint={hint} />;
}

function LayerControls({
  map,
  selection,
  updateMap,
}: {
  map: MysteryRoomMapDefinition;
  selection: MysteryRoomEditorSelection;
  updateMap: (updater: (map: MysteryRoomMapDefinition) => MysteryRoomMapDefinition) => void;
}) {
  const layer = getEditorSelectionLayer(map, selection);
  if (layer === null) return null;
  return (
    <PropertySection title="Слой">
      <NumberInput
        label="Уровень"
        value={layer}
        onChange={(value) => updateMap((draft) => setEditorSelectionLayer(draft, selection, value))}
      />
      <PropertyRow label="Быстро">
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => updateMap((draft) => adjustEditorSelectionLayer(draft, selection, -1))}
            disabled={layer <= MR_EDITOR_LAYER_MIN}
            className="rounded-[8px] border border-site-border bg-site-surface/[0.04] px-2 py-1 text-xs font-semibold text-site-text transition enabled:hover:border-site-border disabled:opacity-45"
          >
            Ниже
          </button>
          <button
            type="button"
            onClick={() => updateMap((draft) => adjustEditorSelectionLayer(draft, selection, 1))}
            disabled={layer >= MR_EDITOR_LAYER_MAX}
            className="rounded-[8px] border border-site-accent/25 bg-site-accent/10 px-2 py-1 text-xs font-semibold text-site-accent transition enabled:hover:border-site-accent/60 disabled:opacity-45"
          >
            Выше
          </button>
        </div>
      </PropertyRow>
    </PropertySection>
  );
}

function RectInputs({ rect, onChange }: { rect: MysteryRoomRect; onChange: (rect: MysteryRoomRect) => void }) {
  return (
    <>
      <NumberInput label="X" value={rect.x} onChange={(x) => onChange({ ...rect, x })} />
      <NumberInput label="Y" value={rect.y} onChange={(y) => onChange({ ...rect, y })} />
      <NumberInput label="W" value={rect.width} onChange={(width) => onChange({ ...rect, width: Math.max(1, width) })} />
      <NumberInput label="H" value={rect.height} onChange={(height) => onChange({ ...rect, height: Math.max(1, height) })} />
    </>
  );
}

function PointInputs({ point, onChange, label }: { point: MysteryRoomVec2; onChange: (point: MysteryRoomVec2) => void; label?: string }) {
  return (
    <>
      {label && <ReadonlyProperty label="Группа" value={label} />}
      <NumberInput label="X" value={point.x} onChange={(x) => onChange({ ...point, x })} />
      <NumberInput label="Y" value={point.y} onChange={(y) => onChange({ ...point, y })} />
    </>
  );
}

type IdOption = {
  id: string;
  label: string;
};

const ACTION_KINDS: MysteryRoomTriggerAction['kind'][] = [
  'toggle_light',
  'set_light',
  'set_light_flicker',
  'play_sound',
  'spawn_smoke',
  'explosion',
  'open_door',
  'close_door',
  'enable_equipment',
  'set_alarm',
];

function MultiIdInput({ label, value, options, onChange }: { label: string; value: string[]; options: IdOption[]; onChange: (value: string[]) => void }) {
  const selectedIds = new Set(value);
  const selectedOptions = value.map((id) => options.find((option) => option.id === id) ?? { id, label: id });
  const availableOptions = options.filter((option) => !selectedIds.has(option.id));
  return (
    <PropertyRow label={label}>
      <div className="space-y-2">
        <div className="flex min-h-8 flex-wrap gap-1.5 rounded-[8px] border border-site-border bg-site-bg/70 p-1.5">
          {selectedOptions.map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={() => onChange(value.filter((id) => id !== option.id))}
              className="max-w-full rounded-[7px] border border-site-accent/25 bg-site-accent/10 px-2 py-1 text-left text-[0.68rem] font-semibold text-site-accent transition hover:border-site-accent/55"
              title={`Убрать ${option.label}`}
            >
              <span className="mr-1 text-site-accent">×</span>
              {option.label}
            </button>
          ))}
          {selectedOptions.length === 0 && <span className="px-1 py-1 text-[0.68rem] text-site-muted">Связей нет</span>}
        </div>
        <div className="max-h-28 overflow-y-auto rounded-[8px] border border-site-border bg-site-surface/[0.025] p-1">
          {availableOptions.map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={() => onChange([...value, option.id])}
              className="mb-1 grid w-full grid-cols-[18px_minmax(0,1fr)] items-center gap-1 rounded-[7px] px-2 py-1 text-left text-[0.68rem] text-site-text transition hover:bg-site-surface/[0.06]"
              title={`${option.label} (${option.id})`}
            >
              <span className="font-black text-site-accent">+</span>
              <span className="truncate">{option.label} <span className="text-site-muted">({option.id})</span></span>
            </button>
          ))}
          {availableOptions.length === 0 && <span className="block px-2 py-1 text-[0.68rem] text-site-muted">{options.length === 0 ? 'Нет доступных объектов' : 'Все доступные объекты добавлены'}</span>}
        </div>
      </div>
    </PropertyRow>
  );
}

function ActionListEditor({
  map,
  title,
  actions,
  onChange,
}: {
  map: MysteryRoomMapDefinition;
  title: string;
  actions: MysteryRoomTriggerAction[];
  onChange: (actions: MysteryRoomTriggerAction[]) => void;
}) {
  return (
    <PropertySection title={title}>
      <div className="space-y-2 px-2 py-2">
        {actions.map((action, index) => (
          <div key={`${action.kind}:${index}`} className="rounded-[10px] border border-site-border bg-site-surface/[0.025] p-2">
            <div className="flex items-center gap-2">
              <select
                value={action.kind}
                onChange={(event) => onChange(actions.map((entry, entryIndex) => entryIndex === index ? createDefaultAction(event.target.value as MysteryRoomTriggerAction['kind'], map) : entry))}
                className="h-8 min-w-0 flex-1 rounded-[8px] border border-site-border bg-site-bg/70 px-2 text-xs text-site-text outline-none focus:border-site-accent/45"
              >
                {ACTION_KINDS.map((kind) => (
                  <option key={kind} value={kind}>{actionKindLabel(kind)}</option>
                ))}
              </select>
              <button type="button" onClick={() => onChange(actions.filter((_, entryIndex) => entryIndex !== index))} className="rounded-[8px] border border-site-danger/20 bg-site-danger/10 px-2 py-1 text-[0.68rem] text-site-danger">
                remove
              </button>
            </div>
            <ActionFields map={map} action={action} onChange={(nextAction) => onChange(actions.map((entry, entryIndex) => entryIndex === index ? nextAction : entry))} />
          </div>
        ))}
        {actions.length === 0 && <div className="rounded-[10px] border border-dashed border-site-border px-3 py-2 text-xs text-site-muted">Действий пока нет.</div>}
        <button type="button" onClick={() => onChange([...actions, createDefaultAction('toggle_light', map)])} className="w-full rounded-[10px] border border-site-accent/25 bg-site-accent/10 px-3 py-2 text-xs font-semibold text-site-accent transition hover:border-site-accent/60">
          Добавить действие
        </button>
      </div>
    </PropertySection>
  );
}

function ActionFields({ map, action, onChange }: { map: MysteryRoomMapDefinition; action: MysteryRoomTriggerAction; onChange: (action: MysteryRoomTriggerAction) => void }) {
  if (action.kind === 'toggle_light' || action.kind === 'set_light') {
    return (
      <div className="mt-2 space-y-1">
        <MultiIdInput label="Лампы" value={action.lightIds} options={map.lights.map((light) => ({ id: light.id, label: light.label ?? light.id }))} onChange={(lightIds) => onChange({ ...action, lightIds } as MysteryRoomTriggerAction)} />
        <SelectInput
          label="Реакция"
          value={lightActionReactionValue(action)}
          options={['toggle', 'on', 'off']}
          onChange={(reaction) => onChange(updateLightActionReaction(action, reaction as 'toggle' | 'on' | 'off'))}
        />
      </div>
    );
  }
  if (action.kind === 'set_light_flicker') {
    return (
      <div className="mt-2 space-y-1">
        <MultiIdInput label="Лампы" value={action.lightIds} options={map.lights.map((light) => ({ id: light.id, label: light.label ?? light.id }))} onChange={(lightIds) => onChange({ ...action, lightIds })} />
        <ToggleInput label="Мерцание" value={action.enabled} onChange={(enabled) => onChange({ ...action, enabled })} />
        <NumberInput label="Длительность, мс" value={action.durationMs ?? 3000} onChange={(durationMs) => onChange({ ...action, durationMs })} />
      </div>
    );
  }
  if (action.kind === 'play_sound') {
    return (
      <div className="mt-2">
        <MultiIdInput label="Звуки" value={action.soundSourceIds} options={map.soundSources.map((source) => ({ id: source.id, label: source.label }))} onChange={(soundSourceIds) => onChange({ ...action, soundSourceIds })} />
      </div>
    );
  }
  if (action.kind === 'open_door' || action.kind === 'close_door') {
    return (
      <div className="mt-2">
        <MultiIdInput label="Двери" value={action.doorIds} options={map.doors.map((door) => ({ id: door.id, label: door.id }))} onChange={(doorIds) => onChange({ ...action, doorIds } as MysteryRoomTriggerAction)} />
      </div>
    );
  }
  if (action.kind === 'enable_equipment') {
    return (
      <div className="mt-2 space-y-1">
        <MultiIdInput label="Оборудование" value={action.objectIds} options={map.customObjects.map((object) => ({ id: object.id, label: object.label }))} onChange={(objectIds) => onChange({ ...action, objectIds })} />
        <ToggleInput label="Включить" value={action.enabled} onChange={(enabled) => onChange({ ...action, enabled })} />
      </div>
    );
  }
  if (action.kind === 'set_alarm') {
    return <div className="mt-2"><ToggleInput label="Тревога" value={action.enabled} onChange={(enabled) => onChange({ ...action, enabled })} /></div>;
  }
  if (action.kind === 'spawn_smoke') {
    return (
      <div className="mt-2 space-y-1">
        <MultiIdInput
          label="Эффект"
          value={action.hazardId ? [action.hazardId] : []}
          options={map.hazards.map((hazard) => ({ id: hazard.id, label: `${hazard.id} (${hazardKindLabel(hazard.kind)})` }))}
          onChange={(hazardIds) => onChange({ ...action, hazardId: hazardIds[0] })}
        />
        <NumberInput label="Radius" value={action.radius ?? 180} onChange={(radius) => onChange({ ...action, radius })} />
      </div>
    );
  }
  if (action.kind === 'explosion') {
    return <div className="mt-2"><NumberInput label="Radius" value={action.radius ?? 180} onChange={(radius) => onChange({ ...action, radius })} /></div>;
  }
  return null;
}

function lightActionReactionValue(action: Extract<MysteryRoomTriggerAction, { kind: 'toggle_light' | 'set_light' }>): 'toggle' | 'on' | 'off' {
  if (action.kind === 'toggle_light') return 'toggle';
  return action.enabled ? 'on' : 'off';
}

function updateLightActionReaction(
  action: Extract<MysteryRoomTriggerAction, { kind: 'toggle_light' | 'set_light' }>,
  reaction: 'toggle' | 'on' | 'off',
): MysteryRoomTriggerAction {
  if (reaction === 'toggle') {
    return { kind: 'toggle_light', lightIds: action.lightIds };
  }
  return { kind: 'set_light', lightIds: action.lightIds, enabled: reaction === 'on' };
}

function createDefaultAction(kind: MysteryRoomTriggerAction['kind'], map: MysteryRoomMapDefinition): MysteryRoomTriggerAction {
  const firstLight = map.lights[0]?.id;
  const firstDoor = map.doors[0]?.id;
  const firstSound = map.soundSources[0]?.id;
  const firstObject = map.customObjects[0]?.id;
  const firstHazard = map.hazards[0]?.id;
  if (kind === 'toggle_light') return { kind, lightIds: firstLight ? [firstLight] : [] };
  if (kind === 'set_light') return { kind, lightIds: firstLight ? [firstLight] : [], enabled: true };
  if (kind === 'set_light_flicker') return { kind, lightIds: firstLight ? [firstLight] : [], enabled: true, durationMs: 3000 };
  if (kind === 'play_sound') return { kind, soundSourceIds: firstSound ? [firstSound] : [] };
  if (kind === 'open_door') return { kind, doorIds: firstDoor ? [firstDoor] : [] };
  if (kind === 'close_door') return { kind, doorIds: firstDoor ? [firstDoor] : [] };
  if (kind === 'enable_equipment') return { kind, objectIds: firstObject ? [firstObject] : [], enabled: true };
  if (kind === 'set_alarm') return { kind, enabled: true };
  if (kind === 'explosion') return { kind, radius: 220 };
  return { kind: 'spawn_smoke', radius: 180, hazardId: firstHazard };
}

function actionKindLabel(kind: MysteryRoomTriggerAction['kind']): string {
  switch (kind) {
    case 'toggle_light':
      return 'Toggle light';
    case 'set_light':
      return 'Set light';
    case 'set_light_flicker':
      return 'Мерцание света';
    case 'play_sound':
      return 'Play sound';
    case 'spawn_smoke':
      return 'Smoke';
    case 'explosion':
      return 'Explosion';
    case 'open_door':
      return 'Open door';
    case 'close_door':
      return 'Close door';
    case 'enable_equipment':
      return 'Equipment';
    case 'set_alarm':
      return 'Alarm';
    default:
      return kind;
  }
}

function objectLinkOptions(map: MysteryRoomMapDefinition, excludeId?: string): IdOption[] {
  return map.customObjects
    .filter((object) => object.id !== excludeId)
    .map((object) => ({ id: object.id, label: object.label }));
}

function doorAccessCardOptions(map: MysteryRoomMapDefinition): IdOption[] {
  const options = new Map<string, IdOption>();
  map.customObjects
    .filter((object) => object.kind === 'badge')
    .forEach((object) => options.set(object.id, { id: object.id, label: `${object.label} (${object.id})` }));
  map.interactables
    .filter((entry) => entry.kind === 'access_grant' || entry.kind === 'pickup')
    .forEach((entry) => {
      const id = entry.itemId ?? entry.id;
      options.set(id, { id, label: `${entry.label} (${id})` });
    });

  return Array.from(options.values()).sort((a, b) => a.label.localeCompare(b.label, 'ru'));
}

function doorAccessCardSelection(map: MysteryRoomMapDefinition, cardId: string): MysteryRoomEditorSelection | null {
  const object = map.customObjects.find((entry) => entry.id === cardId && entry.kind === 'badge');
  if (object) {
    return { type: 'customObject', id: object.id };
  }
  const interactable = map.interactables.find((entry) => entry.id === cardId || entry.itemId === cardId);
  return interactable ? { type: 'interactable', id: interactable.id } : null;
}

function doorAccessCardSelections(map: MysteryRoomMapDefinition, door: MysteryRoomDoorDefinition): MysteryRoomEditorSelection[] {
  return (door.accessCardObjectIds ?? [])
    .map((cardId) => doorAccessCardSelection(map, cardId))
    .filter((selection): selection is MysteryRoomEditorSelection => Boolean(selection));
}

function doorAccessModeLabel(accessMode: MysteryRoomDoorAccessMode | undefined): string {
  switch (accessMode) {
    case 'public':
      return 'Общественный';
    case 'npc_only':
      return 'Только NPC';
    case 'all_cards_required':
      return 'Нужны все привязанные пропуска';
    case 'card_required':
    default:
      return 'Нужен один из привязанных пропусков';
  }
}

function isCustomObjectCollisionEnabled(object: MysteryRoomCustomObjectDefinition): boolean {
  if (typeof object.collisionEnabled === 'boolean') {
    return object.collisionEnabled;
  }
  return object.collision.kind === 'rect' || object.collision.kind === 'circle' || object.collision.kind === 'polygon'
    ? object.collision.blocksMovement
    : false;
}

function syncCustomObjectCollisionForInspector(object: MysteryRoomCustomObjectDefinition): MysteryRoomCustomObjectDefinition {
  if (object.collision.kind === 'none') {
    return object;
  }
  if (object.collision.kind === 'circle' && object.shape?.kind !== 'polygon') {
    return {
      ...object,
      collision: {
        ...object.collision,
        center: rectCenter(object.rect),
        radius: Math.max(8, Math.round(Math.min(object.rect.width, object.rect.height) / 2)),
      },
    };
  }
  return {
    ...object,
    collision: createCustomObjectShapeCollision(object),
  };
}

function setCustomObjectCollisionEnabled(
  object: MysteryRoomCustomObjectDefinition,
  collisionEnabled: boolean,
): MysteryRoomCustomObjectDefinition {
  if (!collisionEnabled) {
    return {
      ...object,
      collisionEnabled: false,
      collision: { kind: 'none' },
    };
  }

  const collision: MysteryRoomCollisionShape =
    object.collision.kind === 'circle'
      ? { ...object.collision, blocksMovement: true, blocksSight: object.blocksSight }
      : object.shape?.kind === 'polygon'
        ? createCustomObjectShapeCollision(object)
      : { kind: 'rect', rect: { ...object.rect }, blocksMovement: true, blocksSight: object.blocksSight };

  return {
    ...object,
    collisionEnabled: true,
    collision,
  };
}

function normalizeInspectorWeight(value: number | null | undefined): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return 25;
  }
  return Math.max(0, Math.round(value * 10) / 10);
}

function LinkedTriggersReadout({ map, targetKind, targetId }: { map: MysteryRoomMapDefinition; targetKind: 'light' | 'sound' | 'door' | 'object' | 'hazard'; targetId: string }) {
  const linked = map.triggers.filter((trigger) =>
    trigger.actions.some((action) => {
      if (
        targetKind === 'light'
        && (action.kind === 'toggle_light' || action.kind === 'set_light' || action.kind === 'set_light_flicker')
      ) return action.lightIds.includes(targetId);
      if (targetKind === 'sound' && action.kind === 'play_sound') return action.soundSourceIds.includes(targetId);
      if (targetKind === 'door' && (action.kind === 'open_door' || action.kind === 'close_door')) return action.doorIds.includes(targetId);
      if (targetKind === 'object' && action.kind === 'enable_equipment') return action.objectIds.includes(targetId);
      if (targetKind === 'hazard' && action.kind === 'spawn_smoke') return action.hazardId === targetId;
      return false;
    }),
  );
  return (
    <ReadonlyProperty
      label="Триггеры"
      value={linked.length > 0 ? linked.map((trigger) => trigger.label || trigger.id).join(', ') : 'нет'}
    />
  );
}

function SelectionActionSection({
  label,
  selectedCount,
  canCopy,
  onDuplicate,
  onCopy,
}: {
  label: string;
  selectedCount: number;
  canCopy: boolean;
  onDuplicate: () => void;
  onCopy: () => void;
}) {
  return (
    <PropertySection title="Действия">
      <ReadonlyProperty label="Выбрано" value={label} />
      {selectedCount > 1 && <ReadonlyProperty label="Группа" value={`${selectedCount} объектов`} />}
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={onDuplicate}
          disabled={!canCopy}
          title="Создать рядом полноценный дубликат выбранного объекта"
          className="rounded-[10px] border border-site-accent/20 bg-site-accent/10 px-3 py-2 text-xs font-semibold text-site-accent transition enabled:hover:border-site-accent/50 disabled:opacity-45"
        >
          Дублировать
        </button>
        <button
          type="button"
          onClick={onCopy}
          disabled={!canCopy}
          title="Скопировать выбранный объект справа от оригинала"
          className="rounded-[10px] border border-site-border bg-site-surface/[0.04] px-3 py-2 text-xs font-semibold text-site-text transition enabled:hover:border-site-border disabled:opacity-45"
        >
          Копировать
        </button>
      </div>
      <div className="mt-2 text-[0.68rem] leading-4 text-site-muted">Копия появляется справа, получает новое имя и сразу остается выбранной для перемещения.</div>
    </PropertySection>
  );
}

function MissingSelection({ label }: { label: string }) {
  return <div className="rounded-[12px] border border-dashed border-site-border px-3 py-2 text-sm text-site-muted">{label}</div>;
}

function clampViewBoxToMap(viewBox: ViewBox, mapWidth: number, mapHeight: number): ViewBox {
  const width = clamp(viewBox.width, MIN_VIEWBOX_WIDTH, Math.max(MAX_VIEWBOX_WIDTH, mapWidth * 1.5));
  const height = clamp(viewBox.height, MIN_VIEWBOX_HEIGHT, Math.max(MAX_VIEWBOX_HEIGHT, mapHeight * 1.5));
  const panLimitX = Math.max(MR_EDITOR_DEFAULT_CANVAS_WIDTH * 6, mapWidth * 4, MAX_VIEWBOX_WIDTH);
  const panLimitY = Math.max(MR_EDITOR_DEFAULT_CANVAS_HEIGHT * 6, mapHeight * 4, MAX_VIEWBOX_HEIGHT);
  return {
    x: clamp(viewBox.x, -panLimitX, panLimitX),
    y: clamp(viewBox.y, -panLimitY, panLimitY),
    width,
    height,
  };
}

function editorGridBoundsForViewBox(viewBox: ViewBox): ViewBox {
  const pad = Math.max(viewBox.width, viewBox.height) * 0.65;
  const x = Math.floor((viewBox.x - pad) / GRID_CELL_SIZE) * GRID_CELL_SIZE;
  const y = Math.floor((viewBox.y - pad) / GRID_CELL_SIZE) * GRID_CELL_SIZE;
  const right = Math.ceil((viewBox.x + viewBox.width + pad) / GRID_CELL_SIZE) * GRID_CELL_SIZE;
  const bottom = Math.ceil((viewBox.y + viewBox.height + pad) / GRID_CELL_SIZE) * GRID_CELL_SIZE;
  return {
    x,
    y,
    width: Math.max(GRID_CELL_SIZE, right - x),
    height: Math.max(GRID_CELL_SIZE, bottom - y),
  };
}

function fitViewBoxToEditorContent(map: MysteryRoomMapDefinition): ViewBox {
  const bounds = getEditableMapContentBounds(map);
  const pad = 620;
  const minWidth = 1800;
  const minHeight = 1100;
  const contentWidth = Math.max(minWidth, bounds.width + pad * 2);
  const contentHeight = Math.max(minHeight, bounds.height + pad * 2);
  const width = Math.max(contentWidth, contentHeight * 1.55);
  const height = Math.max(contentHeight, width / 1.78);
  const centerX = bounds.minX + bounds.width / 2;
  const centerY = bounds.minY + bounds.height / 2;
  return clampViewBoxToMap(
    {
      x: centerX - width / 2,
      y: centerY - height / 2,
      width,
      height,
    },
    map.width,
    map.height,
  );
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, Number.isFinite(value) ? value : min));
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.closest('input, textarea, select, [contenteditable="true"]') !== null;
}

function mapAssetPreviewUrl(asset: MrMapAsset): string {
  if (!asset.url) return '';
  const cacheKey = asset.createdAt || asset.id || asset.filename;
  if (!cacheKey) return asset.url;
  const separator = asset.url.includes('?') ? '&' : '?';
  return `${asset.url}${separator}mrAsset=${encodeURIComponent(cacheKey)}`;
}

function extractUploadedMapAsset(response: unknown): MrMapAsset | null {
  if (!isRecord(response)) return null;
  if (isMapAssetPayload(response.asset)) return response.asset;
  const nestedData = response.data;
  if (isRecord(nestedData) && isMapAssetPayload(nestedData.asset)) return nestedData.asset;
  return null;
}

function isMapAssetPayload(value: unknown): value is MrMapAsset {
  if (!isRecord(value)) return false;
  return (
    typeof value.id === 'string' &&
    typeof value.assetKey === 'string' &&
    typeof value.name === 'string' &&
    typeof value.filename === 'string' &&
    typeof value.url === 'string' &&
    typeof value.type === 'string' &&
    typeof value.size === 'number' &&
    typeof value.createdAt === 'string'
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function mapAssetCheckerBackground() {
  return {
    backgroundColor: '#0f172a',
    backgroundImage:
      'linear-gradient(45deg, rgba(148, 163, 184, 0.16) 25%, transparent 25%), linear-gradient(-45deg, rgba(148, 163, 184, 0.16) 25%, transparent 25%), linear-gradient(45deg, transparent 75%, rgba(148, 163, 184, 0.16) 75%), linear-gradient(-45deg, transparent 75%, rgba(148, 163, 184, 0.16) 75%)',
    backgroundPosition: '0 0, 0 9px, 9px -9px, -9px 0',
    backgroundSize: '18px 18px',
  };
}

function formatDate(value: string | null | undefined) {
  if (!value) return 'нет даты';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('ru-RU');
}

function publishIssuesFromError(error: any): string[] {
  const rawIssues = Array.isArray(error?.details?.issues) ? error.details.issues : [];
  const issues = rawIssues.map((issue: unknown) => formatMysteryRoomPublishIssue(String(issue))).filter(Boolean);
  return issues.length > 0 ? issues : [mrApiErrorMessage(error, 'Проверьте карту и повторите публикацию.')];
}

function formatSaveTime(value: Date): string {
  return value.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}

function toMapSummary(full: MrMapFull): MrMapSummary {
  return {
    id: full.id,
    mapKey: full.mapKey,
    title: full.title,
    description: full.description,
    playableGameModes: full.playableGameModes ?? full.map?.playableGameModes ?? null,
    status: full.status,
    schemaVersion: full.schemaVersion,
    ownerUserId: full.ownerUserId,
    ownerName: full.ownerName,
    assetBytesTotal: full.assetBytesTotal,
    publishedAt: full.publishedAt,
    updatedAt: full.updatedAt,
    createdAt: full.createdAt,
  };
}

function resolveInspectorZoneIdAtPoint(map: MysteryRoomMapDefinition, point: MysteryRoomVec2, fallback?: string): string {
  return map.rooms.find((room) => room.floor.some((floor) => rectContainsPoint(floor, point)))?.id ?? fallback ?? map.spawnZoneId ?? map.rooms[0]?.id ?? 'room';
}
